import { edgeStatus, migrationWave, workspaceIdentity } from "./workspace-status.js";
import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BadgeCheck,
  BookOpenCheck,
  Boxes,
  Building2,
  Check,
  ChevronDown,
  CircleGauge,
  Clock3,
  Cloud,
  Coffee,
  Copy,
  Download,
  FileKey2,
  Fingerprint,
  GitBranch,
  KeyRound,
  Laptop,
  LockKeyhole,
  Menu,
  Network,
  PauseCircle,
  RefreshCw,
  RadioTower,
  RotateCw,
  ScrollText,
  Server,
  Settings2,
  ShieldCheck,
  ShieldEllipsis,
  ShieldX,
  Sparkles,
  Users,
  Vault,
  X,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type {
  AuditEvent,
  CapabilityRecord,
  CertificateAuthorityConnector,
  CertificateDeploymentMode,
  CertificateSet,
  CryptoPolicy,
  DependencyBlastRadius,
  DependencyObservation,
  IssuanceGuardrails,
  KeyEstablishment,
  PolicyImpactAssessment,
  PlatformPolicy,
  RotationPolicy,
  SignatureAlgorithm,
  TransactionRecord,
  WorkloadProfilePolicy,
} from "@edgepki/protocol";
import { analyzeDependencyBlastRadius, assessPolicyImpact, assertCryptoPolicy, assertPolicySatisfiableUnderPlatform, resolveEffectiveIssuanceGuardrails, resolveEffectivePolicyConstraints, sha256 } from "@edgepki/protocol";
import {
  analyzeDependencyTargets,
  assignRole,
  approvePolicy,
  approveIssuanceResume,
  approveServiceRegistration,
  createCaImportSession,
  updateCaScmIds,
  createNodeEnrollmentPackage,
  exportTransactionEvidence,
  loadOCSPPage,
  listAccessAssignments,
  listPolicyProposals,
  loadConsoleData,
  promoteCertificateSet,
  previewPolicyImpact,
  proposePolicy,
  refreshCrl,
  refreshOcsp,
  revokeCertificate,
  revokeScimConnector,
  requestIssuanceResume,
  rotateScimConnector,
  saveIntegration,
  setKillSwitch,
  testIntegration,
  type ConsoleData,
  type AccessAssignment,
  type CRLPublicationStatus,
  type OCSPPublicationStatus,
  type BreakGlassResumeRequest,
  ConsoleApiError,
  type IntegrationConfigurationInput,
  type IntegrationKind,
  type IntegrationRecord,
  type NodeRecord,
  type NodeEnrollmentPackage,
  type Overview,
  type ProductRole,
  type PolicyProposal,
  type RegistrationApprovalInput,
  type ServiceRegistration,
  type ScimConnectorCredential,
  type ScimConnectorStatus,
} from "./api.js";
import {
  auditIntegrityLabel, caAccountLabel, certificateAlgorithm, parseScmIdDraft, scmIdsLabel, cliIssueExample, expiringCertificates, hasActiveIncident,
  identityConfiguration, integrationTargets, integrationTestMessage, latestIssuancePath, newProfileScopes, nodeHealthMetric,
  platformEnforcementStatus, policyActivationStatus, profileSummary, resumeRequestState, topbarStatus, trustParties,
  verifyAuditWindow, type AuditVerification, type ScmIdDraft,
} from "./tenant-truth.js";
import { GuidedDemo } from "./GuidedDemo.js";
import { MigrationView } from "./MigrationView.js";
import { createPasskeyCredential, passkeysSupported, type PasskeyEnrollmentOptions } from "./passkeys.js";

interface AccountPasskey {
  credential_id: string;
  credential_id_hint: string;
  created_at: string;
  last_used_at: string | null;
  backup_eligible: boolean;
  backup_state: boolean;
  transports: string[];
}

type View =
  | "guided"
  | "health"
  | "certificates"
  | "nodes"
  | "downloads"
  | "registrations"
  | "crypto"
  | "dependencies"
  | "migrations"
  | "rotation"
  | "cas"
  | "revocation"
  | "policies"
  | "access"
  | "audit"
  | "events"
  | "integrations"
  | "incidents";

const guidedNavItem: {
  id: View;
  label: string;
  icon: LucideIcon;
  section?: string;
} = {
  id: "guided",
  label: "End-to-end demo",
  icon: Sparkles,
  section: "Guided tour",
};

const nav: Array<{
  id: View;
  label: string;
  icon: LucideIcon;
  section?: string;
}> = [
  { id: "health", label: "Trust health", icon: CircleGauge },
  { id: "certificates", label: "Certificates", icon: FileKey2 },
  { id: "nodes", label: "Mesh nodes", icon: Network },
  { id: "downloads", label: "Downloads & install", icon: Download },
  { id: "registrations", label: "Service claims", icon: BadgeCheck },
  { id: "crypto", label: "Crypto posture", icon: Fingerprint },
  { id: "dependencies", label: "Trust graph", icon: GitBranch },
  { id: "migrations", label: "Migrations", icon: ShieldEllipsis },
  { id: "rotation", label: "Rotation", icon: RotateCw },
  { id: "cas", label: "CAs & signing", icon: KeyRound, section: "Control" },
  { id: "revocation", label: "Revocation", icon: ShieldX },
  { id: "policies", label: "Policies", icon: BookOpenCheck },
  { id: "access", label: "Access", icon: Users },
  { id: "audit", label: "Audit evidence", icon: ScrollText },
  { id: "events", label: "Trust events", icon: RadioTower },
  {
    id: "integrations",
    label: "Integrations",
    icon: Boxes,
    section: "Operate",
  },
  { id: "incidents", label: "Incidents", icon: AlertTriangle },
];

const viewIds = new Set<View>([
  "guided",
  "health",
  "certificates",
  "nodes",
  "downloads",
  "registrations",
  "crypto",
  "dependencies",
  "migrations",
  "rotation",
  "cas",
  "revocation",
  "policies",
  "access",
  "audit",
  "events",
  "integrations",
  "incidents",
]);

function initialView(): View {
  const requested = new URLSearchParams(window.location.search).get(
    "view",
  ) as View | null;
  return requested && viewIds.has(requested) ? requested : "health";
}

const title: Record<
  View,
  { eyebrow: string; heading: string; description: string }
> = {
  guided: {
    eyebrow: "Interactive product tour",
    heading: "See every trust decision",
    description:
      "Run complete PKI, workload, agentic, and zero-downtime rotation flows step by step.",
  },
  health: {
    eyebrow: "Mesh trust",
    heading: "Trust health",
    description: "Live posture across both independent trust domains.",
  },
  certificates: {
    eyebrow: "Inventory",
    heading: "Certificates",
    description:
      "Every managed identity, lifecycle state, and deployment slot.",
  },
  nodes: {
    eyebrow: "Customer trust domain",
    heading: "Mesh nodes",
    description: "Outbound-only customer authorization participants.",
  },
  downloads: {
    eyebrow: "Private infrastructure",
    heading: "Install Sectigo Edge locally",
    description:
      "Deploy verified Mesh Nodes, the operator CLI, or the signing service in Windows, Linux, and Kubernetes environments.",
  },
  registrations: {
    eyebrow: "Workload identity",
    heading: "Service claims",
    description:
      "Claim measured services and agentic runtimes without reusable bootstrap secrets.",
  },
  crypto: {
    eyebrow: "Crypto intelligence",
    heading: "Crypto posture",
    description: "Know exactly what can migrate before changing policy.",
  },
  dependencies: {
    eyebrow: "Verified communication paths",
    heading: "Trust dependency graph",
    description: "Prove direct and transitive cryptographic blast radius before changing policy.",
  },
  migrations: {
    eyebrow: "Controlled cryptographic change",
    heading: "Migration control",
    description: "Plan, approve, canary, observe, and automatically restore exact certificates without downtime.",
  },
  rotation: {
    eyebrow: "Continuity",
    heading: "Zero-downtime rotation",
    description: "Stage, verify, promote, and roll back without an outage.",
  },
  cas: {
    eyebrow: "Signing plane",
    heading: "CAs & signing",
    description: "Connect Sectigo SCM or bring an existing issuing CA.",
  },
  revocation: {
    eyebrow: "Certificate status",
    heading: "Revocation distribution",
    description: "Continuously verified CRLs and low-latency OCSP responses without exposing CA keys.",
  },
  policies: {
    eyebrow: "Deterministic control",
    heading: "Cryptographic policy",
    description: "Signed, versioned rules evaluated by both trust domains.",
  },
  access: {
    eyebrow: "Enterprise identity",
    heading: "Users & access",
    description: "Simple roles backed by resource-level OpenFGA decisions.",
  },
  audit: {
    eyebrow: "Provable operations",
    heading: "Audit evidence",
    description: "A hash-chained record held by Sectigo and the customer.",
  },
  events: {
    eyebrow: "Verified event mesh",
    heading: "Trust events",
    description: "Signed, tenant-bound facts for local-first service reconciliation and secure cloud failback.",
  },
  integrations: {
    eyebrow: "Ecosystem",
    heading: "Integrations",
    description: "Standards-first automation across your estate.",
  },
  incidents: {
    eyebrow: "Response",
    heading: "Incidents",
    description:
      "Contain issuance risk without interrupting existing identities.",
  },
};

function cx(...values: Array<string | false | null | undefined>): string {
  return values.filter(Boolean).join(" ");
}

function timeAgo(value: string): string {
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - Date.parse(value)) / 1000),
  );
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86_400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86_400)}d ago`;
}

function timeUntil(value: string): string {
  const milliseconds = Date.parse(value) - Date.now();
  if (!Number.isFinite(milliseconds)) return "Not published";
  if (milliseconds <= 0) return "Refresh due";
  const minutes = Math.floor(milliseconds / 60_000);
  if (minutes < 60) return `in ${Math.max(1, minutes)}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `in ${hours}h`;
  return `in ${Math.floor(hours / 24)}d`;
}

function compact(value: number): string {
  return new Intl.NumberFormat("en", {
    notation: value > 9999 ? "compact" : "standard",
    maximumFractionDigits: 1,
  }).format(value);
}

function downloadJson(filename: string, value: unknown): void {
  const url = URL.createObjectURL(new Blob([`${JSON.stringify(value, null, 2)}\n`], { type: "application/json" }));
  try {
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    link.click();
  } finally {
    URL.revokeObjectURL(url);
  }
}

function bootstrapFilename(value: NodeEnrollmentPackage): string {
  const safeName = value.node_name.toLowerCase().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 64) || "node";
  return `sectigo-edge-${safeName}.bootstrap.json`;
}

function Status({
  value,
  label,
}: {
  value: "healthy" | "warning" | "offline" | "paused";
  label?: string;
}) {
  return (
    <span className={`status status--${value}`}>
      <span className="status__dot" />
      {label ?? value}
    </span>
  );
}

function Metric({
  label,
  value,
  detail,
  icon: Icon,
  tone = "default",
}: {
  label: string;
  value: string;
  detail: string;
  icon: LucideIcon;
  tone?: "default" | "good" | "warn";
}) {
  return (
    <article className={`metric metric--${tone}`}>
      <div className="metric__top">
        <span>{label}</span>
        <Icon size={17} />
      </div>
      <strong>{value}</strong>
      <p>{detail}</p>
    </article>
  );
}

function Card({
  children,
  className,
  id,
  title: cardTitle,
  action,
}: {
  children: ReactNode;
  className?: string;
  id?: string;
  title?: string;
  action?: ReactNode;
}) {
  return (
    <section id={id} className={cx("card", className)}>
      {cardTitle && (
        <header className="card__header">
          <h3>{cardTitle}</h3>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

function PartyIcon({ state }: { state: "ok" | "warn" | "neutral" }) {
  if (state === "ok") return <BadgeCheck size={18} />;
  if (state === "warn") return <AlertTriangle size={18} />;
  return null;
}

function TrustDiagram({ overview, demo }: { overview: Overview; demo: boolean }) {
  // The demo keeps its sample copy; a real tenant sees its own quorum and pause state.
  const parties = demo
    ? { sectigo: { title: "Edge approved", state: "ok" as const }, customer: { title: "Node approved", state: "ok" as const } }
    : trustParties(overview);
  return (
    <div className="trust-diagram" aria-label="Dual-control trust path">
      <div className="trust-party trust-party--sectigo">
        <span className="party-icon">
          <Cloud size={20} />
        </span>
        <div>
          <small>SECTIGO TRUST DOMAIN</small>
          <strong>{parties.sectigo.title}</strong>
        </div>
        <PartyIcon state={parties.sectigo.state} />
      </div>
      <div className="trust-line">
        <span />
        <div className="trust-lock">
          <LockKeyhole size={18} />
        </div>
        <span />
      </div>
      <div className="trust-party trust-party--customer">
        <span className="party-icon">
          <Building2 size={20} />
        </span>
        <div>
          <small>CUSTOMER TRUST DOMAIN</small>
          <strong>{parties.customer.title}</strong>
        </div>
        <PartyIcon state={parties.customer.state} />
      </div>
      <div className="trust-result">
        <ShieldCheck size={20} />
        <div>
          <small>ISSUANCE CONDITION</small>
          <strong>Both approvals required</strong>
        </div>
      </div>
    </div>
  );
}

function HealthView({
  data,
  audit,
  onNavigate,
}: {
  data: ConsoleData;
  audit: AuditVerification;
  onNavigate: (view: View) => void;
}) {
  const { overview } = data;
  const demo = data.demo;
  const nodesMetric = demo
    ? { value: `${data.nodes.filter((node) => node.status === "healthy").length}/${data.nodes.length}`, detail: "Logical customer participant", tone: "good" as const }
    : nodeHealthMetric(data.nodes);
  const expiring = demo ? (overview.counts.expiring_30d ?? 0) : expiringCertificates(data.certificateSets);
  const auditLabel = demo ? undefined : auditIntegrityLabel(audit);
  const issuancePath = demo ? undefined : latestIssuancePath(data.transactions, data.certificateSets);
  return (
    <>
      <section className="trust-hero">
        <div className="trust-hero__copy">
          <div className="trust-state">
            <span className="pulse" />
            <span>TRUST STATUS</span>
            <strong>{overview.trust_status.replace("_", " ")}</strong>
          </div>
          <h2>Neither side holds unilateral authority.</h2>
          <p>
            Every certificate requires the same immutable transaction to be
            independently approved by Sectigo and your enterprise.
          </p>
        </div>
        <TrustDiagram overview={overview} demo={demo} />
      </section>
      <div className="metrics">
        <Metric
          label="Managed identities"
          value={compact(overview.counts.certificates ?? 0)}
          detail={demo ? "Across all environments" : "Certificates issued through Sectigo Edge"}
          icon={FileKey2}
        />
        <Metric
          label="Healthy nodes"
          value={nodesMetric.value}
          detail={nodesMetric.detail}
          icon={Network}
          tone={nodesMetric.tone}
        />
        <Metric
          label="Expiry exposure"
          value={String(expiring)}
          detail={demo ? "Certificates under 30 days" : "Active certificates expiring within 30 days"}
          icon={Activity}
          tone={expiring ? "warn" : demo || data.certificateSets.length ? "good" : "default"}
        />
        <Metric
          label="Policy version"
          value={`v${overview.policy_version}`}
          detail={demo ? "Synchronized across mesh" : "Active tenant policy"}
          icon={BookOpenCheck}
        />
      </div>
      <section className="local-install-callout" aria-labelledby="local-install-callout-title">
        <span className="local-install-callout__icon"><Download size={23} /></span>
        <div>
          <small>RUN SECTIGO EDGE IN YOUR ENVIRONMENT</small>
          <h2 id="local-install-callout-title">Install the local Mesh Node</h2>
          <p>Download verified Windows, Linux, or Kubernetes components, create a one-time enrollment, and connect your CA without opening an inbound firewall port.</p>
        </div>
        <button className="button button--primary" onClick={() => onNavigate("downloads")}>
          Downloads & instructions <ArrowRight size={15} />
        </button>
      </section>
      <div className="grid grid--2-1">
        <Card
          title="Post-quantum readiness"
          action={
            <button
              className="text-button"
              onClick={() => onNavigate("crypto")}
            >
              View migration plan <ArrowRight size={14} />
            </button>
          }
        >
          <div className="pqc-score">
            <strong>{overview.pqc.ready_percent}%</strong>
            <span>of the estate can accept a PQ identity today</span>
          </div>
          <div
            className="readiness-bar"
            aria-label="PQC readiness distribution"
          >
            <span style={{ width: `${overview.pqc.ready_percent}%` }} />
            <i
              style={{
                width: `${Math.max(0, overview.pqc.hybrid_percent - overview.pqc.ready_percent)}%`,
              }}
            />
            <b style={{ width: `${overview.pqc.classical_only_percent}%` }} />
          </div>
          <div className="legend">
            <span>
              <i className="dot dot--aqua" />
              PQC ready <b>{overview.pqc.ready_percent}%</b>
            </span>
            <span>
              <i className="dot dot--blue" />
              Hybrid only{" "}
              <b>{overview.pqc.hybrid_percent - overview.pqc.ready_percent}%</b>
            </span>
            <span>
              <i className="dot dot--slate" />
              Classical only <b>{overview.pqc.classical_only_percent}%</b>
            </span>
          </div>
          <div className="migration-callout">
            <Sparkles size={18} />
            <div>
              <strong>{migrationWave(overview).title}</strong>
              <span>{migrationWave(overview).detail}</span>
            </div>
            <button
              className="button button--quiet"
              onClick={() => onNavigate("crypto")}
            >
              Review wave
            </button>
          </div>
        </Card>
        <Card title="Audit integrity">
          <div className="audit-seal">
            <div>
              <Fingerprint size={27} />
            </div>
            <strong>{auditLabel ? auditLabel.title : "Chain verified"}</strong>
            <span>{compact(overview.audit_head.sequence)} events</span>
            {auditLabel && <small>{auditLabel.detail}</small>}
          </div>
          <dl className="mini-dl">
            <div>
              <dt>Customer witnesses</dt>
              <dd>
                {overview.audit_witness.received}/{overview.audit_witness.required} independent node signatures
              </dd>
            </div>
            <div>
              <dt>Witnessed checkpoint</dt>
              <dd>
                {overview.audit_witness.checkpoint_sequence
                  ? `#${compact(overview.audit_witness.checkpoint_sequence)}`
                  : "Awaiting first checkpoint"}
              </dd>
            </div>
            <div>
              <dt>Chain head</dt>
              <dd className="mono">{overview.audit_head.hash.slice(0, 18)}…</dd>
            </div>
          </dl>
        </Card>
      </div>
      <div className="grid grid--2-1">
        <Card
          title="Recent trust activity"
          action={
            <button className="text-button" onClick={() => onNavigate("audit")}>
              Open audit <ArrowRight size={14} />
            </button>
          }
        >
          <AuditList events={data.audit.slice(0, 4)} compactMode />
        </Card>
        <Card title={demo ? "Issuance path" : "Latest issuance path"}>
          {!demo && !issuancePath && (
            <p className="form-message">No certificate request has been made yet. Each step appears here from the latest real transaction.</p>
          )}
          {!demo && issuancePath && (
            <ol className="trace-list" aria-label={`Transaction ${issuancePath.transactionId}`}>
              {issuancePath.steps.map((step) => (
                <li key={step.label} className={`trace-step--${step.state}`}>
                  <span>
                    {step.state === "done" ? <Check size={12} /> : step.state === "failed" ? <ShieldX size={12} /> : <Clock3 size={12} />}
                  </span>
                  <b>{step.label}</b>
                  <time>{step.at ? timeAgo(step.at) : step.state}</time>
                </li>
              ))}
            </ol>
          )}
          {demo && <ol className="trace-list">
            {[
              "Identity verified",
              "Enterprise approved",
              "Sectigo approved",
              "Certificate signed",
              "Deployment verified",
            ].map((item, index) => (
              <li key={item}>
                <span>
                  <Check size={12} />
                </span>
                <b>{item}</b>
                <time>{[2, 7, 11, 23, 41][index]} ms</time>
              </li>
            ))}
          </ol>}
        </Card>
      </div>
    </>
  );
}

function NodesView({
  nodes,
  quorum,
  onEnroll,
}: {
  nodes: NodeRecord[];
  quorum: ConsoleData["overview"]["approval_quorum"];
  onEnroll: () => void;
}) {
  return (
    <Card
      className="table-card"
      title="Logical trust participant"
      action={
        <button className="button button--primary" onClick={onEnroll}>
          <Zap size={15} /> Enroll node
        </button>
      }
    >
      <div className="replica-summary">
        <div className="replica-stack">
          <span>A</span>
          <span>B</span>
          <span>C</span>
        </div>
        <div>
          <strong>
            {quorum.required} of {quorum.eligible_nodes} authorization quorum
          </strong>
          <p>
            {quorum.healthy_nodes} eligible replicas are healthy and
            independently validate each exact transaction.
          </p>
        </div>
        <Status
          value={quorum.available ? "healthy" : "warning"}
          label={quorum.available ? "Quorum available" : "Quorum unavailable"}
        />
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Node</th>
              <th>Environment</th>
              <th>Version</th>
              <th>Capabilities</th>
              <th>Last seen</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {nodes.map((node) => (
              <tr key={node.id}>
                <td>
                  <div className="primary-cell">
                    <span className="table-icon">
                      <Server size={16} />
                    </span>
                    <div>
                      <strong>{node.name}</strong>
                      <small>{node.id}</small>
                    </div>
                  </div>
                </td>
                <td className="capitalize">{node.platform}</td>
                <td className="mono">{node.version}</td>
                <td>
                  <div className="tag-row">
                    {node.capabilities.slice(0, 3).map((item) => (
                      <span className="tag" key={item}>
                        {item}
                      </span>
                    ))}
                  </div>
                </td>
                <td>{timeAgo(node.last_seen_at)}</td>
                <td>
                  <Status
                    value={
                      node.status === "healthy"
                        ? "healthy"
                        : node.status === "offline"
                          ? "offline"
                          : "warning"
                    }
                    label={node.status}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

const releaseVersion = "v0.1.26";
const releaseBase = `https://downloads.sharppki.com/${releaseVersion}`;

function InstallCommand({ label, command }: { label: string; command: string }) {
  return (
    <div className="install-command">
      <span>{label}</span>
      <code>{command}</code>
      <button
        type="button"
        aria-label={`Copy ${label.toLowerCase()}`}
        onClick={() => void navigator.clipboard.writeText(command)}
      >
        <Copy size={15} /> Copy
      </button>
    </div>
  );
}

function DownloadsView({ onEnroll }: { onEnroll: () => void }) {
  const linuxInstall = `base=${releaseBase}; for f in edgepki-node-linux-amd64 edgepki-node.service install-node-linux.sh RELEASE.json bootstrap-trust.json SHA256SUMS manifest.sigstore.json; do curl -fSLO "$base/$f"; done; sudo sh ./install-node-linux.sh --verify-only --bootstrap-package ./sectigo-edge-node.bootstrap.json . hassard0/sharppki`;
  const windowsInstall = `$base='${releaseBase}'; $files='edgepki-node-windows-amd64.exe','install-node-windows.ps1','RELEASE.json','bootstrap-trust.json','SHA256SUMS','manifest.sigstore.json'; foreach($f in $files){Invoke-WebRequest "$base/$f" -OutFile $f}; .\\install-node-windows.ps1 -VerifyOnly -Binary .\\edgepki-node-windows-amd64.exe -BootstrapPackage .\\sectigo-edge-node.bootstrap.json -ReleaseDirectory .`;
  const linuxUpgrade = `sudo sh ./install-node-linux.sh --upgrade . hassard0/sharppki`;
  const windowsUpgrade = `.\\install-node-windows.ps1 -Upgrade -Binary .\\edgepki-node-windows-amd64.exe -ReleaseDirectory .`;
  const helmInstall = `helm upgrade --install sectigo-edge ${releaseBase}/sectigo-edge-helm-charts.tgz --namespace sectigo-edge --create-namespace`;
  const localUIConfig = `"local_ui_enabled": true, "local_ui_allowed_spiffe_prefixes": ["spiffe://customer.example/operators/pki/"]`;
  const otlpAuditConfig = `"otlp_audit_enabled": true, "otlp_audit_exporter_id": "primary-siem", "otlp_audit_endpoint": "https://otel.customer.example:4318/v1/logs"`;
  const eventWatch = `sectigo-edge -url https://127.0.0.1:9443 -ca /etc/sectigo-edge/ca.pem -cert /run/spire/svid.pem -key /run/spire/svid-key.pem -tenant "$SECTIGO_EDGE_TENANT" -node-spki /etc/sectigo-edge/node-public.pem -node-spki-sha256 "$SECTIGO_EDGE_NODE_SPKI_SHA256" -event-topics certificate.promoted,policy.version.available -watch events`;
  return (
    <div className="downloads-view">
      <section className="local-stack-overview" aria-labelledby="local-stack-title">
        <div className="local-stack-copy">
          <span>WHAT RUNS IN YOUR ENVIRONMENT</span>
          <h2 id="local-stack-title">Your private PKI control point</h2>
          <p>
            Start with one Mesh Node on Windows or Linux. Add redundant nodes,
            the operator CLI, a private signer, and the read-only MCP bridge only
            where your architecture needs them. Every component connects outbound;
            CA keys and local credentials remain under your control.
          </p>
        </div>
        <div className="local-stack-components">
          <article><Server /><div><strong>Mesh Node</strong><span>Required · Windows, Linux, or Kubernetes</span></div></article>
          <article><ShieldCheck /><div><strong>Operator CLI</strong><span>Health, discovery, events, enrollment, and MCP</span></div></article>
          <article><LockKeyhole /><div><strong>Private signer</strong><span>Optional · isolate signing behind your policy boundary</span></div></article>
          <article><Boxes /><div><strong>SDKs and adapters</strong><span>ACME, SPIFFE, EST, ADCS, NGINX, Apache, and Kubernetes</span></div></article>
        </div>
        <nav className="local-stack-jumps" aria-label="Local deployment shortcuts">
          <a href="#linux-node">Linux</a>
          <a href="#windows-node">Windows</a>
          <a href="#kubernetes-node">Kubernetes</a>
          <a href="/docs#operations">Deployment guide <ArrowRight size={14} /></a>
        </nav>
      </section>
      <section className="release-banner">
        <div>
          <span>VERIFIED RELEASE</span>
          <h2>Sectigo Edge {releaseVersion}</h2>
          <p>
            Every package is built from the tagged source, accompanied by SHA-256
            checksums, an SPDX SBOM, and keyless provenance.
          </p>
        </div>
        <div className="release-actions">
          <a className="button button--quiet" href={`${releaseBase}/SHA256SUMS`}>
            <Fingerprint size={16} /> Checksums
          </a>
          <a className="button button--quiet" href={`${releaseBase}/sbom.spdx.json`}>
            <ScrollText size={16} /> SBOM
          </a>
          <a className="button button--quiet" href={`${releaseBase}/manifest.sigstore.json`}>
            <ShieldCheck size={16} /> Sigstore proof
          </a>
        </div>
      </section>

      <div className="download-grid">
        <Card id="linux-node">
          <div className="download-card-head">
            <span><Server size={22} /></span>
            <small>LINUX · AMD64</small>
          </div>
          <h3>Mesh Node</h3>
          <p>Run the outbound-only local trust participant as a hardened systemd service.</p>
          <a className="button button--primary" href={`${releaseBase}/edgepki-node-linux-amd64`} download>
            <Download size={16} /> Download Linux node
          </a>
          <div className="artifact-links">
            <a href={`${releaseBase}/install-node-linux.sh`} download>Installer script</a>
            <a href={`${releaseBase}/edgepki-node.service`} download>systemd unit</a>
            <a href="/docs#nginx">NGINX adapter guide</a>
            <a href="/docs#apache">Apache adapter guide</a>
          </div>
          <InstallCommand label="Download and authenticate" command={linuxInstall} />
          <InstallCommand label="Upgrade an enrolled node in place" command={linuxUpgrade} />
        </Card>
        <Card id="windows-node">
          <div className="download-card-head">
            <span><Laptop size={22} /></span>
            <small>WINDOWS · AMD64</small>
          </div>
          <h3>Mesh Node for Windows</h3>
          <p>Install as a Windows service with optional Microsoft ADCS and CNG integration.</p>
          <a className="button button--primary" href={`${releaseBase}/edgepki-node-windows-amd64.exe`} download>
            <Download size={16} /> Download Windows node
          </a>
          <div className="artifact-links">
            <a href={`${releaseBase}/install-node-windows.ps1`} download>PowerShell installer</a>
            <a href={`${releaseBase}/uninstall-node-windows.ps1`} download>Uninstaller</a>
          </div>
          <InstallCommand label="Download and authenticate" command={windowsInstall} />
          <InstallCommand label="Upgrade an enrolled node in place" command={windowsUpgrade} />
        </Card>
        <Card id="kubernetes-node">
          <div className="download-card-head">
            <span><Boxes size={22} /></span>
            <small>KUBERNETES</small>
          </div>
          <h3>Helm deployment</h3>
          <p>Deploy redundant nodes with pod hardening, health checks, and native CSR support.</p>
          <a className="button button--primary" href={`${releaseBase}/sectigo-edge-helm-charts.tgz`} download>
            <Download size={16} /> Download Helm charts
          </a>
          <InstallCommand label="Install or upgrade" command={helmInstall} />
        </Card>
        <Card>
          <div className="download-card-head">
            <span><Cloud size={22} /></span>
            <small>OPERATOR TOOLS</small>
          </div>
          <h3>CLI and private signer</h3>
          <p>Use the CLI for node health, signed discovery, integration status, verified event watching, and the read-only local MCP bridge. The node also embeds an optional read-only operations UI protected by a dedicated SPIFFE operator mTLS scope.</p>
          <div className="download-links">
            <a href={`${releaseBase}/sectigo-edge-linux-amd64`} download><Download size={15} /> Linux CLI</a>
            <a href={`${releaseBase}/sectigo-edge-windows-amd64.exe`} download><Download size={15} /> Windows CLI</a>
            <a href={`${releaseBase}/sectigo-edge-signer-linux-amd64`} download><LockKeyhole size={15} /> Linux signer</a>
          </div>
          <InstallCommand label="Watch verified local events" command={eventWatch} />
          <InstallCommand label="Enable the local UI for one operator path" command={localUIConfig} />
          <InstallCommand label="Enable signed OTLP audit export" command={otlpAuditConfig} />
          <div className="artifact-links"><a href="/docs#local-ui">Local UI security and browser setup</a><a href="/docs#otlp-audit">OpenTelemetry collector setup</a></div>
        </Card>
      </div>

      <Card className="install-steps" title="Connect a local environment">
        <ol>
          <li><span>1</span><div><strong>Authenticate the release</strong><p>Install <code>cosign</code>, then run the platform installer in verify-only mode. It pins the exact repository, workflow, tag, Sigstore issuer, and selected SHA-256 digests before privileged installation.</p></div></li>
          <li><span>2</span><div><strong>Create a signed one-time enrollment</strong><p>Download one tamper-evident bootstrap package. Its token is short-lived, bound to the exact node and platform, and never appears in command arguments or <code>config.json</code>.</p><button className="text-button" onClick={onEnroll}>Create enrollment <ArrowRight size={14} /></button></div></li>
          <li><span>3</span><div><strong>Install the verified bytes</strong><p>Place your five customer-held TLS files in <code>node-material</code>, then run without verify-only and pass that directory. The installer validates everything in an isolated staging directory before changing the system.</p></div></li>
          <li><span>4</span><div><strong>Verify quorum and adapters</strong><p>Confirm the node is healthy, then assign ACME, SPIFFE, NGINX, Apache, Kubernetes, EST, or ADCS desired state from Integrations.</p></div></li>
        </ol>
      </Card>
      <div className="download-footnote">
        <ShieldCheck size={18} />
        <p><strong>No inbound firewall opening is required.</strong> Nodes discover the service, establish an authenticated outbound channel, and fail closed when exact policy or quorum evidence is unavailable.</p>
      </div>
    </div>
  );
}

function RegistrationsView({
  registrations,
  onApprove,
}: {
  registrations: ServiceRegistration[];
  onApprove: (registration: ServiceRegistration) => void;
}) {
  return (
    <div className="registration-list">
      <Card className="safety-card">
        <ShieldEllipsis size={25} />
        <div>
          <strong>Introduction is not authorization</strong>
          <p>
            The local node verified fresh key possession. Compare the code shown
            by the service, then approve only its node-derived workload
            selectors.
          </p>
        </div>
      </Card>
      {registrations.length === 0 && (
        <Card>
          <Empty
            title="No service claims"
            text="New SDK, CLI, and MCP runtimes appear here after local proof of possession."
            icon={BadgeCheck}
          />
        </Card>
      )}
      {registrations.map((registration) => (
        <Card
          key={`${registration.node_id}:${registration.claim_id}`}
          className="registration-card"
        >
          <header className="registration-head">
            <div>
              <span className="eyebrow-mini">
                {registration.announcement.site_hint ?? registration.node_id}
              </span>
              <h3>{registration.announcement.display_name}</h3>
              <p className="mono">
                {registration.announcement.requested_spiffe_id}
              </p>
            </div>
            <Status
              value={
                registration.status === "active"
                  ? "healthy"
                  : registration.status === "approved"
                    ? "warning"
                    : "offline"
              }
              label={registration.status.replace("_", " ")}
            />
          </header>
          <div className="registration-body">
            <div>
              <small>PROTOCOLS</small>
              <div className="tag-row">
                {registration.announcement.protocols.map((value) => (
                  <span className="tag tag--aqua" key={value}>
                    {value}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <small>CAPABILITIES</small>
              <div className="tag-row">
                {registration.announcement.capabilities.map((value) => (
                  <span className="tag" key={value}>
                    {value}
                  </span>
                ))}
              </div>
            </div>
          </div>
          <div className="measurement-list">
            {registration.announcement.measurements.map((measurement) => (
              <span key={`${measurement.kind}:${measurement.value}`}>
                <Fingerprint size={14} />
                <b>{measurement.kind}</b>
                <code>
                  {measurement.value.length > 72
                    ? `${measurement.value.slice(0, 69)}…`
                    : measurement.value}
                </code>
              </span>
            ))}
          </div>
          {registration.approval && (
            <div className="registration-approval">
              <ShieldCheck size={18} />
              <div>
                <strong>{registration.approval.owner}</strong>
                <span>
                  {registration.approval.profile_id} ·{" "}
                  {registration.approval.selectors
                    .map((selector) => `${selector.type}:${selector.value}`)
                    .join(" · ")}
                </span>
              </div>
            </div>
          )}
          <footer className="registration-footer">
            <span>
              Claim <code>{registration.claim_id}</code>
            </span>
            {registration.status === "pending_admin" && (
              <button
                className="button button--primary"
                onClick={() => onApprove(registration)}
              >
                <BadgeCheck size={15} /> Verify & claim
              </button>
            )}
          </footer>
        </Card>
      ))}
    </div>
  );
}

function Slot({
  name,
  slot,
}: {
  name: string;
  slot: CertificateSet["active"];
}) {
  if (!slot)
    return (
      <div className="cert-slot cert-slot--empty">
        <span>{name}</span>
        <strong>Empty slot</strong>
      </div>
    );
  return (
    <div className={`cert-slot cert-slot--${slot.state}`}>
      <header>
        <span>{name}</span>
        <Status
          value={
            slot.health === "healthy"
              ? "healthy"
              : slot.health === "unhealthy"
                ? "warning"
                : "offline"
          }
          label={slot.health}
        />
      </header>
      <strong className="mono">{slot.serial_number}</strong>
      <p>
        Expires{" "}
        {new Date(slot.not_after).toLocaleString(undefined, {
          dateStyle: "medium",
          timeStyle: "short",
        })}
      </p>
      <footer>
        <span>{slot.state}</span>
        <span>
          {slot.deployed_at
            ? `Deployed ${timeAgo(slot.deployed_at)}`
            : "Not deployed"}
        </span>
      </footer>
    </div>
  );
}

function RotationView({
  sets,
  transactions,
  onPromote,
}: {
  sets: CertificateSet[];
  transactions: TransactionRecord[];
  onPromote: (id: string, healthURL: string) => void;
}) {
  const [healthURLs, setHealthURLs] = useState<Record<string, string>>(() => Object.fromEntries(sets.map((set) => {
    const transaction = transactions.find((item) => item.transaction.transaction_id === set.standby?.certificate_id);
    const firstSAN = transaction?.transaction.request.sans.find((san) => !san.startsWith("*."));
    return [set.id, firstSAN ? `https://${firstSAN}/health` : ""];
  })));
  return (
    <div className="rotation-list">
      {sets.length === 0 && (
        <Empty
          title="No certificate sets yet"
          text="Dual-slot state appears after the first managed identity is issued."
          icon={RotateCw}
        />
      )}
      {sets.map((set) => (
        <Card key={set.id} className="rotation-card">
          <div className="rotation-head">
            <div>
              <span className="eyebrow-mini">GENERATION {set.generation}</span>
              <h3>{set.endpoint_id.replace("spiffe://", "")}</h3>
              <p>
                {set.profile_id} · {set.rotation_policy.mode.replace("_", " ")}
              </p>
            </div>
            <div className="rotation-window">
              <span>{set.promotion?.status === "pending" ? "Node command" : "Overlap window"}</span>
              <strong>
                {set.promotion?.status === "pending" ? "pending verification" : `${Math.round(set.rotation_policy.overlap_seconds / 60)} min`}
              </strong>
            </div>
          </div>
          <div className="slots">
            <Slot name="ACTIVE" slot={set.active} />
            <div className="slot-arrow">
              <ArrowRight size={20} />
              <span>health-gated</span>
            </div>
            <Slot name="STANDBY" slot={set.standby} />
          </div>
          <div className="rotation-footer">
            <div>
              <Check size={15} />
              <span>
                Old certificate remains serving until standby health passes.
              </span>
            </div>
            {set.standby && <label className="rotation-health-url">
              <span>Exact health URL</span>
              <input
                type="url"
                inputMode="url"
                placeholder="https://service.example/health"
                value={healthURLs[set.id] ?? ""}
                disabled={set.promotion?.status === "pending"}
                onChange={(event) => setHealthURLs((current) => ({ ...current, [set.id]: event.target.value }))}
              />
              <small>The Mesh Node verifies a 2xx response serving this exact staged certificate before switching traffic.</small>
            </label>}
            <button
              disabled={!set.standby || set.promotion?.status === "pending" || !(healthURLs[set.id] ?? "").startsWith("https://")}
              className="button button--primary"
              onClick={() => onPromote(set.id, healthURLs[set.id] ?? "")}
            >
              <RotateCw size={15} /> {set.promotion?.status === "pending" ? "Awaiting Mesh Node" : "Verify & promote"}
            </button>
          </div>
        </Card>
      ))}
      <Card className="safety-card">
        <ShieldEllipsis size={25} />
        <div>
          <strong>Rotation safety invariant</strong>
          <p>
            A failed stage or health check never removes the active certificate.
            Promotion and rollback are idempotent across node restarts.
          </p>
        </div>
      </Card>
    </div>
  );
}


const EMPTY_SCM_DRAFT: ScmIdDraft = { organization: "", certificateType: "" };

function ScmIdFields({
  draft,
  errors,
  onChange,
}: {
  draft: ScmIdDraft;
  errors: { organization?: string; certificateType?: string };
  onChange: (draft: ScmIdDraft) => void;
}) {
  return (
    <fieldset className="scm-id-fields">
      <legend>Sectigo SCM enrollment IDs (SCM only)</legend>
      <p>
        Enter the numeric IDs from your SCM account. Every certificate this
        connector enrolls is requested under this organization and certificate type.
      </p>
      <label className="modal-field">
        <span>SCM organization ID</span>
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft.organization}
          onChange={(event) => onChange({ ...draft, organization: event.target.value })}
          placeholder="e.g. 12345"
          aria-invalid={Boolean(errors.organization)}
        />
        <small className={errors.organization ? "form-message--error" : undefined}>
          {errors.organization ?? "Shown for your organization under Organizations in SCM."}
        </small>
      </label>
      <label className="modal-field">
        <span>SCM certificate type ID</span>
        <input
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft.certificateType}
          onChange={(event) => onChange({ ...draft, certificateType: event.target.value })}
          placeholder="e.g. 678"
          aria-invalid={Boolean(errors.certificateType)}
        />
        <small className={errors.certificateType ? "form-message--error" : undefined}>
          {errors.certificateType ?? "The ID of the SSL certificate type (profile) to enroll, from your SCM certificate settings."}
        </small>
      </label>
    </fieldset>
  );
}

function CasView({
  cas,
  crls,
  onConnect,
  onManage,
  onRefreshCrl,
}: {
  cas: CertificateAuthorityConnector[];
  crls: CRLPublicationStatus[];
  onConnect: () => void;
  onManage: (ca: CertificateAuthorityConnector) => void;
  onRefreshCrl: (ca: CertificateAuthorityConnector) => void;
}) {
  return (
    <>
      <div className="provider-grid">
        <button className="provider-add" onClick={onConnect}>
          <span>
            <KeyRound size={23} />
          </span>
          <strong>Connect a signing authority</strong>
          <p>Use Sectigo SCM or import an existing issuing CA.</p>
          <b>
            Start setup <ArrowRight size={14} />
          </b>
        </button>
        {cas.map((ca) => {
          const crl = crls.find((item) => item.connector_id === ca.id);
          const scmIds = scmIdsLabel(ca);
          return (
          <article className="provider-card" key={ca.id}>
            <header>
              <span className={`provider-logo provider-logo--${ca.provider}`}>
                <KeyRound size={20} />
              </span>
              <Status
                value={ca.status === "active" && scmIds?.complete !== false ? "healthy" : "warning"}
                label={scmIds?.complete === false ? "incomplete" : ca.status}
              />
            </header>
            <small>
              {ca.provider === "sectigo_scm" ? "SECTIGO SCM" : "CUSTOMER CA"}
            </small>
            <h3>{ca.name}</h3>
            <p>{caAccountLabel(ca)}</p>
            <dl>
              <div>
                <dt>Key reference</dt>
                <dd className="mono">{ca.signer_key_reference ?? "Pending"}</dd>
              </div>
              {scmIds && (
                <div>
                  <dt>SCM enrollment</dt>
                  <dd className={scmIds.complete ? "mono" : "scm-ids-missing"}>{scmIds.label}</dd>
                </div>
              )}
              <div>
                <dt>Updated</dt>
                <dd>{timeAgo(ca.updated_at)}</dd>
              </div>
              <div>
                <dt>Revocation list</dt>
                <dd className={`crl-state crl-state--${crl?.status ?? "pending"}`}>
                  {crl ? `${crl.status} · gen ${crl.generation}` : "awaiting first publication"}
                </dd>
              </div>
              <div>
                <dt>Next update</dt>
                <dd>{crl?.next_update ? timeAgo(crl.next_update) : "not published"}</dd>
              </div>
            </dl>
            <div className="provider-actions">
              <button className="button button--quiet" onClick={() => onManage(ca)}>Manage</button>
              <button className="button button--quiet" disabled={crl?.status === "external"} onClick={() => onRefreshCrl(ca)}>
                <RefreshCw size={14} /> {crl?.status === "external" ? "Local CA managed" : "Refresh CRL"}
              </button>
            </div>
          </article>
          );
        })}
      </div>
      <Card className="architecture-note">
        <LockKeyhole size={24} />
        <div>
          <strong>
            Private keys stay outside the Sectigo Edge application plane
          </strong>
          <p>
            Manual CA onboarding uses a one-time signer import session. Sectigo
            Edge retains only the public chain, key reference, and cryptographic
            ceremony receipt.
          </p>
        </div>
      </Card>
    </>
  );
}

function RevocationView({
  cas,
  crls,
  ocsp,
  hasMore,
  loadingMore,
  onRefreshCrl,
  onRefreshOcsp,
  onLoadMore,
}: {
  cas: CertificateAuthorityConnector[];
  crls: CRLPublicationStatus[];
  ocsp: OCSPPublicationStatus[];
  hasMore: boolean;
  loadingMore: boolean;
  onRefreshCrl: (ca: CertificateAuthorityConnector) => void;
  onRefreshOcsp: (publication: OCSPPublicationStatus) => void;
  onLoadMore: () => void;
}) {
  const caNames = new Map(cas.map((ca) => [ca.id, ca.name]));
  const healthy = ocsp.filter((item) => item.health === "healthy").length;
  const attention = ocsp.filter((item) => item.health === "pending" || item.health === "refresh_due").length;
  return (
    <div className="revocation-view">
      <div className="revocation-summary">
        <article>
          <span><Activity size={19} /></span>
          <div><strong>{healthy}</strong><small>healthy OCSP responses</small></div>
        </article>
        <article>
          <span><RefreshCw size={19} /></span>
          <div><strong>{attention}</strong><small>responses needing attention</small></div>
        </article>
        <article>
          <span><ScrollText size={19} /></span>
          <div><strong>{crls.filter((item) => item.status === "healthy").length}/{crls.length}</strong><small>current revocation lists</small></div>
        </article>
      </div>

      <Card title="OCSP response service" className="revocation-card" action={<span className="protocol-badge">RFC 5019 PROFILE</span>}>
        <div className="revocation-explainer">
          <ShieldCheck size={20} />
          <p>Responses come only from the configured CA provider: the protected signer for SCM and BYO authorities, or the assigned Windows node's pinned Online Responder for ADCS. Every artifact is independently verified, stored immutably, and removed before expiry; SHA-1 and SHA-256 CertIDs preserve client compatibility.</p>
        </div>
        <div className="revocation-table-wrap">
          <table className="revocation-table">
            <thead><tr><th>Signing authority</th><th>Certificate</th><th>CertID</th><th>Status</th><th>Next update</th><th><span className="sr-only">Actions</span></th></tr></thead>
            <tbody>
              {ocsp.map((item) => (
                <tr key={`${item.transaction_id}:${item.hash_algorithm}`}>
                  <td data-label="Signing authority"><strong>{caNames.get(item.connector_id) ?? item.connector_id}</strong><small className="mono">{item.connector_id}</small></td>
                  <td data-label="Certificate"><code>{item.serial_number}</code><small className="mono">gen {item.generation}</small></td>
                  <td data-label="CertID"><span className="hash-badge">{item.hash_algorithm.toUpperCase()}</span></td>
                  <td data-label="Status"><Status value={item.health === "healthy" ? "healthy" : item.health === "external" ? "warning" : "offline"} label={item.health === "refresh_due" ? "refresh due" : item.health} /></td>
                  <td data-label="Next update">{item.next_update ? timeUntil(item.next_update) : item.health === "external" ? "Local responder" : "Pending"}</td>
                  <td data-label="Actions"><button className="button button--quiet" onClick={() => onRefreshOcsp(item)}><RefreshCw size={14} /> {item.health === "external" ? "Queue" : "Refresh"}</button></td>
                </tr>
              ))}
              {ocsp.length === 0 && <tr><td colSpan={6} className="revocation-empty">No issued certificate status records yet.</td></tr>}
            </tbody>
          </table>
        </div>
        {hasMore && <div className="revocation-load"><button className="button button--quiet" disabled={loadingMore} onClick={onLoadMore}>{loadingMore ? "Loading…" : "Load more responses"}</button></div>}
      </Card>

      <Card title="Certificate revocation lists" className="revocation-card">
        <div className="crl-grid">
          {cas.map((ca) => {
            const publication = crls.find((item) => item.connector_id === ca.id);
            return <article key={ca.id}>
              <div><strong>{ca.name}</strong><small>{publication ? `CRL ${publication.crl_number} · ${publication.required_revocations} revoked` : "Awaiting first publication"}</small></div>
              <Status value={publication?.status === "healthy" ? "healthy" : publication?.status === "external" ? "warning" : "offline"} label={publication?.status ?? "pending"} />
              <dl><div><dt>Generation</dt><dd>{publication?.generation ?? 0}</dd></div><div><dt>Next update</dt><dd>{publication?.next_update ? timeUntil(publication.next_update) : "Pending"}</dd></div></dl>
              <button className="button button--quiet" disabled={publication?.status === "external"} onClick={() => onRefreshCrl(ca)}><RefreshCw size={14} /> {publication?.status === "external" ? "Local CA managed" : "Publish new CRL"}</button>
            </article>;
          })}
        </div>
      </Card>
    </div>
  );
}

function CryptoView({ data }: { data: ConsoleData }) {
  const total = data.overview.counts.capability_endpoints ?? 0;
  const rows = [
    {
      label: "PQC signature ready",
      percent: data.overview.pqc.ready_percent,
      tone: "aqua",
    },
    {
      label: "Hybrid key exchange ready",
      percent: data.overview.pqc.hybrid_percent,
      tone: "blue",
    },
    {
      label: "Classical only",
      percent: data.overview.pqc.classical_only_percent,
      tone: "slate",
    },
  ].map((row) => ({ ...row, count: Math.round((total * row.percent) / 100) }));
  return (
    <div className="grid grid--2-1">
      <Card title="Estate compatibility">
        <div className="estate-total">
          <strong>{compact(total)}</strong>
          <span>fresh cryptographic endpoint observations</span>
        </div>
        <div className="capability-list">
          {rows.map((row) => (
            <div key={row.label}>
              <header>
                <span>{row.label}</span>
                <b>
                  {compact(row.count)} · {row.percent}%
                </b>
              </header>
              <div>
                <span
                  className={`bar--${row.tone}`}
                  style={{ width: `${row.percent}%` }}
                />
              </div>
            </div>
          ))}
        </div>
        <p className="evidence-note">
          <Fingerprint size={15} /> Expired or unverified observations never
          count toward readiness.
        </p>
      </Card>
      <Card title="Transition posture">
        <div
          className="donut"
          style={
            {
              "--p": `${data.overview.pqc.ready_percent * 3.6}deg`,
            } as React.CSSProperties
          }
        >
          <div>
            <strong>{data.overview.pqc.ready_percent}%</strong>
            <span>PQ signature ready</span>
          </div>
        </div>
        <div className="algorithm-list">
          <span>
            <i className="dot dot--aqua" />
            PQ signature <b>{data.overview.pqc.ready_percent}%</b>
          </span>
          <span>
            <i className="dot dot--blue" />
            Hybrid KEM <b>{data.overview.pqc.hybrid_percent}%</b>
          </span>
          <span>
            <i className="dot dot--slate" />
            Classical only <b>{data.overview.pqc.classical_only_percent}%</b>
          </span>
        </div>
      </Card>
      <Card className="span-2 architecture-note">
        <ShieldCheck size={22} />
        <div>
          <strong>Migration control is operational</strong>
          <p>
            The Migrations workspace turns this live posture into immutable,
            dependency-ordered waves with independent approval, sustained
            health proof, and exact automatic certificate rollback.
          </p>
        </div>
      </Card>
    </div>
  );
}

function dependencyEndpointLabel(value: string): string {
  const trimmed = value.replace(/\/$/, "");
  return trimmed.slice(trimmed.lastIndexOf("/") + 1) || value;
}

function DependencyGraphView({ data }: { data: ConsoleData }) {
  const targets = useMemo(() => [...new Set(data.dependencies.flatMap((edge) => [edge.target_endpoint_id, edge.source_endpoint_id]))].sort(), [data.dependencies]);
  const preferred = targets.find((target) => target.endsWith("/payments")) ?? targets[0] ?? "";
  const [target, setTarget] = useState(preferred);
  const [analysis, setAnalysis] = useState<DependencyBlastRadius>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!target) return;
    let active = true;
    setBusy(true);
    setError("");
    const request = data.demo
      ? analyzeDependencyBlastRadius({ tenantId: data.overview.tenant_id, targetEndpointIds: [target], observations: data.dependencies, now: new Date() })
      : analyzeDependencyTargets([target]);
    void request.then((result) => { if (active) setAnalysis(result); }).catch((reason) => {
      if (active) setError(reason instanceof Error ? reason.message : "Dependency analysis failed");
    }).finally(() => { if (active) setBusy(false); });
    return () => { active = false; };
  }, [data.demo, data.dependencies, data.overview.tenant_id, target]);

  const layers = useMemo(() => {
    const grouped = new Map<number, DependencyBlastRadius["impacted_endpoints"]>();
    for (const endpoint of analysis?.impacted_endpoints ?? []) grouped.set(endpoint.depth, [...(grouped.get(endpoint.depth) ?? []), endpoint]);
    return [...grouped.entries()].sort(([left], [right]) => right - left);
  }, [analysis]);
  const live = data.dependencies.filter((edge) => Date.parse(edge.expires_at) > Date.now()).length;

  return <div className="dependency-view">
    <section className="dependency-hero">
      <div><span>AUTHORITY-AWARE APPLICATION MAP</span><h2>Know what breaks before cryptography changes</h2><p>Mesh Nodes and workload SDKs contribute short-lived, digest-bound observations. The control plane keeps only the latest edge per exact source, target, channel, and port, then walks callers in reverse without crossing tenant boundaries.</p></div>
      <div className="dependency-summary"><article><strong>{data.dependencies.length}</strong><span>committed edges</span></article><article><strong>{live}</strong><span>currently live</span></article><article><strong>{analysis?.counts.direct_dependents ?? 0}</strong><span>direct callers</span></article><article><strong>{analysis?.counts.transitive_dependents ?? 0}</strong><span>transitive callers</span></article></div>
    </section>

    <Card title="Cryptographic blast-radius analysis" action={<Status value={busy ? "warning" : error ? "offline" : "healthy"} label={busy ? "analyzing" : error ? "failed" : "committed"} />}>
      <div className="dependency-target-control">
        <label><span>Identity or service being changed</span><select value={target} onChange={(event) => setTarget(event.target.value)}>{targets.map((value) => <option value={value} key={value}>{dependencyEndpointLabel(value)} · {value}</option>)}</select></label>
        <p><ShieldCheck size={16} /> The result commits the complete bounded graph, including expired latest edges, so hidden or stale evidence cannot be removed without changing the digest.</p>
      </div>
      {error && <div className="integration-error"><AlertTriangle size={15} />{error}</div>}
      {!error && analysis && <>
        <div className="dependency-flow" aria-label="Reverse transitive dependency diagram">
          {layers.map(([depth, endpoints]) => <div className="dependency-flow__segment" key={depth}>
            <div className="dependency-lane"><span>{depth === 1 ? "DIRECT CALLERS" : `${depth} HOPS AWAY`}</span>{endpoints.map((endpoint) => <article key={endpoint.endpoint_id}><GitBranch size={16} /><div><strong>{dependencyEndpointLabel(endpoint.endpoint_id)}</strong><code>{endpoint.endpoint_id}</code><small>{endpoint.channels.join(" + ")} · {endpoint.evidence_ids.length} proof{endpoint.evidence_ids.length === 1 ? "" : "s"}</small></div></article>)}</div><ArrowRight className="dependency-arrow" size={22} />
          </div>)}
          <div className="dependency-lane dependency-lane--target"><span>CHANGE TARGET</span><article><Fingerprint size={18} /><div><strong>{dependencyEndpointLabel(target)}</strong><code>{target}</code><small>policy, certificate, key, or trust-bundle change</small></div></article></div>
        </div>
        {analysis.impacted_endpoints.length === 0 && <div className="empty-state"><GitBranch /><strong>No live callers observed</strong><p>This does not prove absence: require fresh evidence coverage in policy before treating the target as isolated.</p></div>}
        <div className="dependency-commitments"><div><span>Graph commitment</span><code>{analysis.graph_commitment_sha256}</code></div><div><span>Analysis commitment</span><code>{analysis.analysis_sha256}</code></div><div><span>Evidence valid until</span><strong>{analysis.valid_until ? new Date(analysis.valid_until).toLocaleString() : "No live path"}</strong></div></div>
      </>}
    </Card>

    <Card title="Latest signed observations" action={<span className="table-count">{data.dependencies.length} exact paths</span>}>
      <div className="table-wrap dependency-table"><table><thead><tr><th>Caller</th><th>Dependency</th><th>Channel</th><th>Evidence</th><th>Freshness</th></tr></thead><tbody>{data.dependencies.map((edge) => <tr key={edge.id}><td data-label="Caller"><strong>{dependencyEndpointLabel(edge.source_endpoint_id)}</strong><code>{edge.source_endpoint_id}</code></td><td data-label="Dependency"><strong>{dependencyEndpointLabel(edge.target_endpoint_id)}</strong><code>{edge.target_dns_name ?? edge.target_endpoint_id}:{edge.target_port}</code></td><td data-label="Channel"><span className="channel-chip">{edge.channel}</span></td><td data-label="Evidence"><span>{edge.evidence_source === "mesh_node_active" ? "Verified TLS probe" : edge.evidence_source === "mesh_node_passive" ? "Node observation" : "mTLS-bound SDK"}</span><code>{edge.evidence_sha256}</code></td><td data-label="Freshness"><Status value={Date.parse(edge.expires_at) > Date.now() ? "healthy" : "offline"} label={Date.parse(edge.expires_at) > Date.now() ? timeUntil(edge.expires_at) : "expired"} /></td></tr>)}</tbody></table></div>
      <p className="dependency-evidence-note"><LockKeyhole size={15} /> SDK evidence proves which authenticated workload made the claim. Active Mesh Node probes additionally prove the live server certificate, SPKI, TLS version, cipher, key exchange, and ALPN.</p>
    </Card>
  </div>;
}

function PolicyView({ policy, demo }: { policy: CryptoPolicy; demo: boolean }) {
  const activation = demo ? { value: "healthy" as const, label: "validated & active" } : policyActivationStatus(policy);
  const [policySHA256, setPolicySHA256] = useState("");
  const workloadProfiles = Object.values(policy.workload_profiles ?? {});
  useEffect(() => {
    let active = true;
    void sha256(policy).then((digest) => { if (active) setPolicySHA256(digest); });
    return () => { active = false; };
  }, [policy]);
  return (
    <div className="grid grid--2-1">
      <Card
        title={`${policy.id} · version ${policy.version}`}
        action={<Status value={activation.value} label={activation.label} />}
      >
        <div className="policy-block">
          <span>PREFERRED SIGNATURES</span>
          <div>
            {policy.signatures.preferred.map((item) => (
              <b key={item}>{item}</b>
            ))}
          </div>
        </div>
        <div className="policy-block">
          <span>ALLOWED FALLBACKS</span>
          <div>
            {policy.signatures.allowed
              .filter((item) => !policy.signatures.preferred.includes(item))
              .map((item) => (
                <b className="muted-tag" key={item}>
                  {item}
                </b>
              ))}
          </div>
        </div>
        <div className="policy-block">
          <span>KEY ESTABLISHMENT</span>
          <div>
            {policy.key_establishment.preferred.map((item) => (
              <b key={item}>{item}</b>
            ))}
          </div>
        </div>
        <div className="policy-code">
          <span>TRANSITION</span>
          <code>{JSON.stringify(policy.transition, null, 2)}</code>
        </div>
        <div className="policy-block">
          <span>ISSUANCE BEHAVIOR</span>
          <div>
            <b>{policy.issuance_guardrails?.mode ?? "Inherited"}</b>
            {policy.issuance_guardrails && <>
              <b className="muted-tag">{policy.issuance_guardrails.max_requests_per_requester_5m} requests / 5 min</b>
              <b className="muted-tag">{policy.issuance_guardrails.max_unique_sans_per_requester_24h} SANs / 24 h</b>
              <b className="muted-tag">{policy.issuance_guardrails.block_algorithm_downgrade ? "downgrade blocked" : "downgrade monitored"}</b>
            </>}
          </div>
        </div>
        <div className="policy-block">
          <span>WORKLOAD / APPLICATION PROFILES</span>
          <div>
            {workloadProfiles.length === 0 && (
              <b className="muted-tag">No scoped profiles — issuance fails closed</b>
            )}
            {workloadProfiles.map((profile) => (
              <b className="muted-tag" key={profile.id} title={`${profile.allowed_spiffe_prefixes.join(", ")} · ${profile.allowed_dns_suffixes.join(", ")}`}>
                {profile.id} · {profile.max_validity_seconds / 3600}h{profile.require_capability_evidence ? " · evidence" : ""}
              </b>
            ))}
          </div>
        </div>
      </Card>
      <div className="stack">
        <Card title="Effective constraints">
          <dl className="detail-list">
            <div>
              <dt>Maximum validity</dt>
              <dd>{policy.validity.max_seconds / 86_400} days</dd>
            </div>
            <div>
              <dt>Replica approval</dt>
              <dd>
                {policy.customer_approval_quorum?.required ?? 1} distinct node
                {(policy.customer_approval_quorum?.required ?? 1) === 1
                  ? ""
                  : "s"}
              </dd>
            </div>
            <div>
              <dt>Offline renewal</dt>
              <dd>
                {policy.validity.offline_renewal_max_seconds
                  ? "bounded"
                  : "disabled"}
              </dd>
            </div>
            <div>
              <dt>Policy expires</dt>
              <dd>{new Date(policy.expires_at).toLocaleDateString()}</dd>
            </div>
            <div>
              <dt>Content binding</dt>
              <dd><code title={policySHA256}>{policySHA256 ? `${policySHA256.slice(0, 12)}…` : "calculating…"}</code></dd>
            </div>
          </dl>
        </Card>
        <Card className="policy-rule">
          <ShieldCheck size={22} />
          <div>
            <strong>Most restrictive policy wins</strong>
            <p>
              Platform, enterprise, and workload rules are intersected. A lower
              layer can narrow authority but never expand it. Mesh Nodes, the
              edge decision, protected signer registry, signed receipt, and
              portable evidence must agree on this exact content digest.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

const policySignatureChoices: SignatureAlgorithm[] = [
  "ECDSA-P256-SHA256", "ECDSA-P384-SHA384", "RSA-PSS-3072-SHA384",
  "ML-DSA-65", "SLH-DSA-SHA2-128s", "HYBRID-ECDSA-P384-ML-DSA-65",
];
const policyKeyChoices: KeyEstablishment[] = ["X25519", "X25519MLKEM768"];
const policyDeploymentChoices: CertificateDeploymentMode[] = ["single", "dual_slot", "microsoft_adcs", "nginx", "apache", "java_keystore", "hashicorp_vault"];

function effectiveProfileRotation(profile: WorkloadProfilePolicy): RotationPolicy {
  const dualSlot = profile.allowed_deployment_modes.some((mode) => mode === "dual_slot" || mode === "nginx" || mode === "apache" || mode === "java_keystore" || mode === "hashicorp_vault");
  return profile.rotation ?? {
    mode: dualSlot ? "dual_slot" : "in_place",
    renew_before_seconds: Math.max(300, Math.min(7 * 86_400, profile.max_validity_seconds)),
    overlap_seconds: 3600,
    health_check_grace_seconds: 300,
    promotion: "manual",
    rollback_window_seconds: 3600,
  };
}

function editablePolicy(policy: CryptoPolicy, platform?: PlatformPolicy): CryptoPolicy {
  const now = new Date();
  const cloned = structuredClone(policy);
  return {
    ...cloned, workload_profiles: cloned.workload_profiles ?? {},
    ...(cloned.issuance_guardrails
      ? { issuance_guardrails: cloned.issuance_guardrails }
      : platform ? { issuance_guardrails: structuredClone(platform.issuance_guardrails) } : {}),
    version: policy.version + 1, status: "active",
    not_before: now.toISOString(),
    expires_at: new Date(Math.max(Date.parse(policy.expires_at), now.getTime() + 365 * 86_400_000)).toISOString(),
  };
}

function PlatformPolicyStack({ platform, platformSHA256, enterprise, demo }: { platform: PlatformPolicy; platformSHA256: string; enterprise: CryptoPolicy; demo: boolean }) {
  const enforcement = demo ? { value: "healthy" as const, label: "independently enforced" } : platformEnforcementStatus(platform, platformSHA256);
  const effective = resolveEffectivePolicyConstraints(enterprise, platform);
  const firstProfileID = Object.keys(enterprise.workload_profiles)[0];
  const guardrails = resolveEffectiveIssuanceGuardrails(enterprise, platform, firstProfileID ?? "");
  return <Card className="platform-policy-card" title="Effective policy stack" action={<Status value={enforcement.value} label={enforcement.label} />}>
    <div className="policy-stack-flow">
      <article><span>01 · SECTIGO PLATFORM</span><strong>{platform.id} · v{platform.version}</strong><p>Immutable safety floor controlled outside the tenant administration plane.</p><code title={platformSHA256}>{platformSHA256.slice(0, 16)}…</code></article>
      <ArrowRight aria-hidden="true" />
      <article><span>02 · ENTERPRISE</span><strong>{enterprise.id} · v{enterprise.version}</strong><p>Tenant-approved algorithms, lifetimes, quorum, and transition policy.</p><code>{enterprise.tenant_id}</code></article>
      <ArrowRight aria-hidden="true" />
      <article><span>03 · WORKLOAD</span><strong>{Object.keys(enterprise.workload_profiles).length} exact profiles</strong><p>SPIFFE identity, DNS namespace, adapter, evidence, and rotation constraints.</p><code>deny unless every layer allows</code></article>
    </div>
    <div className="effective-policy-summary">
      <div><span>Effective algorithms</span><strong>{effective.allowed_algorithms.join(", ") || "None — issuance blocked"}</strong></div>
      <div><span>Maximum lifetime</span><strong>{Math.round(effective.max_validity_seconds / 3600)} hours</strong></div>
      <div><span>Minimum customer quorum</span><strong>{effective.minimum_customer_approvals} distinct node{effective.minimum_customer_approvals === 1 ? "" : "s"}</strong></div>
      <div><span>Deployment paths</span><strong>{effective.allowed_deployment_modes.map((mode) => mode.replaceAll("_", " ")).join(", ")}</strong></div>
      <div><span>Issuance guardrails</span><strong>{guardrails.mode} · {guardrails.max_requests_per_requester_5m}/5 min · {guardrails.max_unique_sans_per_requester_24h} SANs/day</strong></div>
    </div>
  </Card>;
}

function PolicyManagementView({ policy, platformPolicy, platformPolicySHA256, nodes, capabilities, dependencies, certificateSets, transactions, user, demo, onActivated }: {
  policy: CryptoPolicy; nodes: NodeRecord[]; capabilities: CapabilityRecord[];
  platformPolicy: PlatformPolicy; platformPolicySHA256: string;
  dependencies: DependencyObservation[];
  certificateSets: ConsoleData["certificateSets"]; transactions: ConsoleData["transactions"];
  user: { id: string }; demo: boolean;
  onActivated: (value: CryptoPolicy) => void;
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [draft, setDraft] = useState<CryptoPolicy>(() => editablePolicy(policy, platformPolicy));
  const [proposals, setProposals] = useState<PolicyProposal[]>([]);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);
  const [impact, setImpact] = useState<PolicyImpactAssessment>();
  const [impactAcknowledged, setImpactAcknowledged] = useState(false);

  useEffect(() => {
    if (demo) return;
    void listPolicyProposals().then(setProposals).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "Policy proposals could not be loaded"));
  }, [demo, policy.version]);

  const openEditor = (source = policy, exact = false) => {
    setDraft(exact ? structuredClone(source) : editablePolicy(source, platformPolicy));
    setImpact(undefined);
    setImpactAcknowledged(false);
    setError("");
    setEditorOpen(true);
  };
  const requiredNodes = Math.max(draft.customer_approval_quorum?.required ?? 1, platformPolicy.minimum_customer_approvals);
  const healthyNodes = nodes.filter((node) => node.status === "healthy").length;
  const removedAlgorithms = policy.signatures.allowed.filter((item) => !draft.signatures.allowed.includes(item));

  function signatureMode(algorithm: SignatureAlgorithm, mode: "preferred" | "allowed" | "forbidden") {
    const preferred = draft.signatures.preferred.filter((item) => item !== algorithm);
    const allowed = draft.signatures.allowed.filter((item) => item !== algorithm);
    const forbidden = draft.signatures.forbidden.filter((item) => item !== algorithm);
    if (mode === "preferred") { preferred.push(algorithm); allowed.push(algorithm); }
    if (mode === "allowed") allowed.push(algorithm);
    if (mode === "forbidden") forbidden.push(algorithm);
    setDraft({ ...draft, signatures: { preferred, allowed, forbidden } });
  }

  function keyMode(algorithm: KeyEstablishment, mode: "preferred" | "allowed" | "off") {
    const preferred = draft.key_establishment.preferred.filter((item) => item !== algorithm);
    const allowed = draft.key_establishment.allowed.filter((item) => item !== algorithm);
    if (mode === "preferred") { preferred.push(algorithm); allowed.push(algorithm); }
    if (mode === "allowed") allowed.push(algorithm);
    setDraft({ ...draft, key_establishment: { preferred, allowed } });
  }

  function setWorkloadProfile(profileID: string, next: WorkloadProfilePolicy) {
    setDraft({ ...draft, workload_profiles: { ...draft.workload_profiles, [profileID]: next } });
  }

  function setIssuanceGuardrails(next: Partial<IssuanceGuardrails>) {
    setDraft({
      ...draft,
      issuance_guardrails: {
        ...(draft.issuance_guardrails ?? platformPolicy.issuance_guardrails),
        ...next,
      },
    });
  }

  function renameWorkloadProfile(profileID: string, nextID: string) {
    const profiles = { ...draft.workload_profiles };
    const profile = profiles[profileID];
    if (!profile || nextID === profileID) return;
    if (profiles[nextID]) { setError(`Workload profile ${nextID} already exists`); return; }
    delete profiles[profileID];
    profiles[nextID] = { ...profile, id: nextID };
    setDraft({ ...draft, workload_profiles: profiles });
  }

  function addWorkloadProfile() {
    let index = 1;
    while (draft.workload_profiles[`new-profile-${index}`]) index++;
    const id = `new-profile-${index}`;
    // Real tenants start from their own existing scopes, never placeholder domains.
    const scopes = demo ? { spiffe: ["spiffe://example.org/workloads/"], dns: ["internal.example.com"] } : newProfileScopes(draft);
    setDraft({ ...draft, workload_profiles: { ...draft.workload_profiles, [id]: {
      id, allowed_spiffe_prefixes: scopes.spiffe, allowed_dns_suffixes: scopes.dns,
      allowed_algorithms: [draft.signatures.preferred[0] ?? draft.signatures.allowed[0] ?? "ECDSA-P256-SHA256"], max_validity_seconds: Math.min(3600, draft.validity.max_seconds),
      allowed_deployment_modes: ["single"], require_capability_evidence: false,
    } } });
  }

  function removeWorkloadProfile(profileID: string) {
    const profiles = { ...draft.workload_profiles };
    delete profiles[profileID];
    setDraft({ ...draft, workload_profiles: profiles });
  }

  async function assessImpact() {
    try {
      assertCryptoPolicy(draft);
      assertPolicySatisfiableUnderPlatform(draft, platformPolicy);
      if (draft.tenant_id !== policy.tenant_id || draft.version !== policy.version + 1) throw new Error("Policy must preserve the tenant and advance exactly one version");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Policy is invalid"); return; }
    setBusy(true);
    setError("");
    setImpactAcknowledged(false);
    try {
      const assessment = demo
        ? await assessPolicyImpact({
            policy: draft,
            previousPolicy: policy,
            platformPolicy,
            capabilities,
            dependencies,
            requireDependencyEvidence: false,
            managedEndpoints: [
              ...certificateSets.map((set) => ({ endpoint_id: set.endpoint_id, profile_ids: [set.profile_id] })),
              ...transactions.map((record) => ({ endpoint_id: record.transaction.requester, profile_ids: [record.transaction.request.profile_id] })),
            ],
            now: new Date(),
          })
        : await previewPolicyImpact(draft);
      setImpact(assessment);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Policy impact could not be assessed");
    } finally {
      setBusy(false);
    }
  }

  async function submit() {
    try {
      assertCryptoPolicy(draft);
      assertPolicySatisfiableUnderPlatform(draft, platformPolicy);
      if (draft.tenant_id !== policy.tenant_id || draft.version !== policy.version + 1) throw new Error("Policy must preserve the tenant and advance exactly one version");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Policy is invalid"); return; }
    if (!impact || !impactAcknowledged) { setError("Assess and acknowledge the exact estate impact before submission"); return; }
    if (impact.policy_sha256 !== await sha256(draft)) {
      setImpact(undefined);
      setImpactAcknowledged(false);
      setError("The draft changed after assessment. Re-run estate impact before submission.");
      return;
    }
    setBusy(true); setError("");
    try {
      const now = new Date().toISOString();
      const proposal = demo ? {
        version: draft.version, digest: `demo-${draft.version}-signed-digest`, policy: draft, impact,
        proposed_by: user.id, approved_by: null, status: "pending" as const, created_at: now, updated_at: now,
      } : await proposePolicy(draft, impact.impact_sha256);
      setProposals((current) => [proposal, ...current.filter((item) => item.version !== proposal.version)]);
      setEditorOpen(false); setNotice("Proposal submitted. A different authorized SSO user must approve activation.");
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Policy proposal failed"); }
    finally { setBusy(false); }
  }

  async function activate(proposal: PolicyProposal) {
    setBusy(true); setError("");
    try {
      const active = demo ? proposal.policy : await approvePolicy(proposal.version);
      setProposals((current) => current.map((item) => item.version === proposal.version ? { ...item, status: "approved", approved_by: demo ? "user:independent-demo-reviewer" : user.id, updated_at: new Date().toISOString() } : item));
      onActivated(active); setNotice(`Policy version ${active.version} activated and queued for signed node distribution.`);
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Policy approval failed"); }
    finally { setBusy(false); }
  }

  return <div className="policy-management">
    <section className="policy-management-head">
      <div><span>CRYPTOGRAPHIC CONTROL PLANE</span><h2>Policy versions, review, and safe activation</h2><p>Configure usable guardrails, inspect impact, and activate only after independent fresh-MFA approval.</p></div>
      <button className="button button--primary" onClick={() => openEditor()}><Settings2 size={16} /> Configure next version</button>
    </section>
    {notice && <div className="success-callout"><Check size={17} />{notice}</div>}
    {error && !editorOpen && <div className="integration-error"><AlertTriangle size={15} />{error}</div>}
    <PlatformPolicyStack platform={platformPolicy} platformSHA256={platformPolicySHA256} enterprise={policy} demo={demo} />
    <PolicyView policy={policy} demo={demo} />
    <Card className="policy-proposals" title="Policy review queue" action={<span>{proposals.filter((item) => item.status === "pending").length} pending</span>}>
      {proposals.length === 0 ? <div className="empty-state"><ShieldCheck /><strong>No change is awaiting review</strong><p>Create the next version to start a separately authorized activation.</p></div> : <div className="policy-proposal-list">
        {proposals.map((proposal) => <article key={`${proposal.version}-${proposal.digest}`}>
          <div><span>VERSION {proposal.version}</span><strong>{proposal.policy.id}</strong><p>Proposed by {proposal.proposed_by} · {new Date(proposal.created_at).toLocaleString()}</p><code>{proposal.digest}</code></div>
          <Status value={proposal.status === "approved" ? "healthy" : "warning"} label={proposal.status} />
          <div className="policy-proposal-actions">
            {proposal.impact && <div className={`policy-impact-badge ${proposal.impact.blocking ? "policy-impact-badge--blocked" : "policy-impact-badge--ready"}`}><span>{proposal.impact.blocking ? "Compatibility blocked" : "Impact committed"}</span><code>{proposal.impact.impact_sha256.slice(0, 16)}...</code><small>{proposal.impact.counts.compatible}/{proposal.impact.counts.total} endpoints compatible</small></div>}
            {proposal.status === "pending" && <button className="button button--primary" disabled={busy || !proposal.impact || proposal.impact.blocking || (!demo && proposal.proposed_by === user.id)} onClick={() => void activate(proposal)}>{!proposal.impact ? "Refresh impact required" : proposal.impact.blocking ? "Resolve compatibility blockers" : demo ? "Simulate independent approval" : proposal.proposed_by === user.id ? "Different reviewer required" : "Approve & activate"}</button>}
            {proposal.status === "pending" && proposal.proposed_by === user.id && <button className="button button--quiet" onClick={() => openEditor(proposal.policy, true)}><RefreshCw size={14} /> Refresh estate impact</button>}
            {proposal.status === "approved" && proposal.version !== policy.version && <button className="button button--quiet" onClick={() => openEditor(proposal.policy)}>Use as rollback base</button>}
          </div>
        </article>)}
      </div>}
    </Card>
    {editorOpen && <Modal title={`Configure policy version ${draft.version}`} size="wide" onClose={() => setEditorOpen(false)}><div className="modal-body policy-editor">
      <div className="secure-note"><ShieldCheck size={17} /><span>Submission requires fresh MFA and <code>can_manage_policy</code>. Activation requires a different immutable SSO subject. Sectigo platform baseline <code>{platformPolicy.id}@{platformPolicy.version}</code> is independently enforced and cannot be weakened here.</span></div>
      <section className="modal-section"><header><strong>Identity, lifetimes, and quorum</strong><span>Tenant and next version are fixed by the active policy.</span></header><div className="policy-field-grid">
        <label className="modal-field"><span>Policy ID</span><input value={draft.id} onChange={(event) => setDraft({ ...draft, id: event.target.value })} /></label>
        <label className="modal-field"><span>Expires</span><input type="date" value={draft.expires_at.slice(0, 10)} onChange={(event) => setDraft({ ...draft, expires_at: `${event.target.value}T23:59:59.000Z` })} /></label>
        <label className="modal-field"><span>Default lifetime (hours)</span><input type="number" min="1" max="8760" value={draft.validity.default_seconds / 3600} onChange={(event) => setDraft({ ...draft, validity: { ...draft.validity, default_seconds: Math.round(Number(event.target.value) * 3600) } })} /></label>
        <label className="modal-field"><span>Maximum lifetime (hours)</span><input type="number" min="1" max="8760" value={draft.validity.max_seconds / 3600} onChange={(event) => setDraft({ ...draft, validity: { ...draft.validity, max_seconds: Math.round(Number(event.target.value) * 3600) } })} /></label>
        <label className="modal-field"><span>Offline renewal ceiling (hours)</span><input type="number" min="0" max="8760" value={draft.validity.offline_renewal_max_seconds / 3600} onChange={(event) => setDraft({ ...draft, validity: { ...draft.validity, offline_renewal_max_seconds: Math.round(Number(event.target.value) * 3600) } })} /></label>
        <label className="modal-field"><span>Distinct node approvals</span><input type="number" min="1" max="5" value={requiredNodes} onChange={(event) => setDraft({ ...draft, customer_approval_quorum: { ...draft.customer_approval_quorum, required: Number(event.target.value) } })} /></label>
      </div></section>
      <section className="modal-section"><header><strong>Certificate signatures</strong><span>Preferred is first choice; allowed is fallback; forbidden fails closed.</span></header><div className="algorithm-policy-grid">{policySignatureChoices.map((algorithm) => {
        const mode = draft.signatures.preferred.includes(algorithm) ? "preferred" : draft.signatures.allowed.includes(algorithm) ? "allowed" : "forbidden";
        return <label key={algorithm}><span>{algorithm}</span><select value={mode} onChange={(event) => signatureMode(algorithm, event.target.value as "preferred" | "allowed" | "forbidden")}><option value="preferred">Preferred</option><option value="allowed">Allowed fallback</option><option value="forbidden">Forbidden</option></select></label>;
      })}</div></section>
      <section className="modal-section"><header><strong>Key establishment and PQC transition</strong><span>Introduce hybrid transport before making it mandatory.</span></header><div className="algorithm-policy-grid">{policyKeyChoices.map((algorithm) => {
        const mode = draft.key_establishment.preferred.includes(algorithm) ? "preferred" : draft.key_establishment.allowed.includes(algorithm) ? "allowed" : "off";
        return <label key={algorithm}><span>{algorithm}</span><select value={mode} onChange={(event) => keyMode(algorithm, event.target.value as "preferred" | "allowed" | "off")}><option value="preferred">Preferred</option><option value="allowed">Allowed fallback</option><option value="off">Disabled</option></select></label>;
      })}</div><div className="policy-field-grid policy-field-grid--transition">
        <label className="modal-field"><span>Transition mode</span><select value={draft.transition?.mode ?? "classical"} onChange={(event) => setDraft({ ...draft, transition: { ...draft.transition, mode: event.target.value as "classical" | "hybrid" | "pqc" } })}><option value="classical">Classical</option><option value="hybrid">Hybrid migration</option><option value="pqc">PQC required</option></select></label>
        <label className="modal-field"><span>PQC required after</span><input type="date" value={draft.transition?.pqc_required_after?.slice(0, 10) ?? ""} onChange={(event) => setDraft({ ...draft, transition: { mode: draft.transition?.mode ?? "hybrid", ...(event.target.value ? { pqc_required_after: `${event.target.value}T00:00:00.000Z` } : {}) } })} /></label>
      </div></section>
      <section className="modal-section"><header><strong>Issuance behavior guardrails</strong><span>Detect or block abnormal requester velocity, namespace expansion, algorithm downgrade, and lifetime escalation. The most restrictive platform, enterprise, and workload value wins.</span></header><div className="policy-field-grid">
        <label className="modal-field"><span>Response mode</span><select value={draft.issuance_guardrails?.mode ?? platformPolicy.issuance_guardrails.mode} onChange={(event) => setIssuanceGuardrails({ mode: event.target.value as IssuanceGuardrails["mode"] })}><option value="enforce">Enforce · block matching requests</option><option value="monitor">Monitor · issue with signed finding</option></select></label>
        <label className="modal-field"><span>Requests per requester / 5 min</span><input type="number" min="1" max="1000" value={draft.issuance_guardrails?.max_requests_per_requester_5m ?? platformPolicy.issuance_guardrails.max_requests_per_requester_5m} onChange={(event) => setIssuanceGuardrails({ max_requests_per_requester_5m: Number(event.target.value) })} /></label>
        <label className="modal-field"><span>Unique SANs per requester / 24 h</span><input type="number" min="1" max="10000" value={draft.issuance_guardrails?.max_unique_sans_per_requester_24h ?? platformPolicy.issuance_guardrails.max_unique_sans_per_requester_24h} onChange={(event) => setIssuanceGuardrails({ max_unique_sans_per_requester_24h: Number(event.target.value) })} /></label>
        <label className="modal-field"><span>Maximum lifetime increase (%)</span><input type="number" min="0" max="1000" value={draft.issuance_guardrails?.max_validity_increase_percent ?? platformPolicy.issuance_guardrails.max_validity_increase_percent} onChange={(event) => setIssuanceGuardrails({ max_validity_increase_percent: Number(event.target.value) })} /></label>
        <label className="modal-field"><span>History lookback (days)</span><input type="number" min="1" max="90" value={draft.issuance_guardrails?.history_lookback_days ?? platformPolicy.issuance_guardrails.history_lookback_days} onChange={(event) => setIssuanceGuardrails({ history_lookback_days: Number(event.target.value) })} /></label>
        <label className="modal-field modal-field--check"><span>Algorithm downgrade</span><input type="checkbox" checked={draft.issuance_guardrails?.block_algorithm_downgrade ?? platformPolicy.issuance_guardrails.block_algorithm_downgrade} onChange={(event) => setIssuanceGuardrails({ block_algorithm_downgrade: event.target.checked })} /><small>Signal a request weaker than the latest issued generation</small></label>
      </div><div className="secure-note"><Fingerprint size={16} /><span>Every assessment commits the exact policy stack and bounded issuance-history digest. Protected signers reject missing, substituted, or blocking evidence.</span></div></section>
      <section className="modal-section"><header><strong>Workload and application profiles</strong><span>Exact profile selection narrows requester identity, DNS namespace, algorithm, lifetime, deployment path, and capability evidence.</span><button className="button button--quiet" type="button" onClick={addWorkloadProfile}>Add profile</button></header><div className="workload-policy-list">
        {Object.entries(draft.workload_profiles).map(([profileID, profile]) => <article className="workload-policy-card" key={profileID}>
          <header><strong>{profileID}</strong><button className="button button--quiet" type="button" disabled={Object.keys(draft.workload_profiles).length === 1} onClick={() => removeWorkloadProfile(profileID)}>Remove</button></header>
          <div className="policy-field-grid">
            <label className="modal-field"><span>Profile ID</span><input value={profileID} onChange={(event) => renameWorkloadProfile(profileID, event.target.value)} /></label>
            <label className="modal-field"><span>Maximum lifetime (hours)</span><input type="number" min="1" max={Math.floor(draft.validity.max_seconds / 3600)} value={profile.max_validity_seconds / 3600} onChange={(event) => setWorkloadProfile(profileID, { ...profile, max_validity_seconds: Math.round(Number(event.target.value) * 3600) })} /></label>
            <label className="modal-field modal-field--check"><span>Capability evidence</span><input type="checkbox" checked={profile.require_capability_evidence} onChange={(event) => setWorkloadProfile(profileID, { ...profile, require_capability_evidence: event.target.checked })} /><small>Require fresh endpoint evidence</small></label>
            <label className="modal-field"><span>SPIFFE path prefixes</span><textarea rows={3} placeholder="spiffe://your-trust-domain/workloads/" value={profile.allowed_spiffe_prefixes.join("\n")} onChange={(event) => setWorkloadProfile(profileID, { ...profile, allowed_spiffe_prefixes: event.target.value.split(/[\n,]/).map((value) => value.trim()).filter(Boolean) })} /></label>
            <label className="modal-field"><span>DNS suffixes</span><textarea rows={3} placeholder="services.your-domain.com" value={profile.allowed_dns_suffixes.join("\n")} onChange={(event) => setWorkloadProfile(profileID, { ...profile, allowed_dns_suffixes: event.target.value.split(/[\n,]/).map((value) => value.trim().toLowerCase()).filter(Boolean) })} /></label>
          </div>
          <div className="policy-inline-options"><span>Algorithms</span>{draft.signatures.allowed.map((algorithm) => <label key={algorithm}><input type="checkbox" checked={profile.allowed_algorithms.includes(algorithm)} onChange={() => { const values = profile.allowed_algorithms.includes(algorithm) ? profile.allowed_algorithms.filter((value) => value !== algorithm) : [...profile.allowed_algorithms, algorithm]; setWorkloadProfile(profileID, { ...profile, allowed_algorithms: values }); }} />{algorithm}</label>)}</div>
          <div className="policy-inline-options"><span>Deployment paths</span>{policyDeploymentChoices.map((mode) => <label key={mode}><input type="checkbox" checked={profile.allowed_deployment_modes.includes(mode)} onChange={() => { const values = profile.allowed_deployment_modes.includes(mode) ? profile.allowed_deployment_modes.filter((value) => value !== mode) : [...profile.allowed_deployment_modes, mode]; const dualSlot = values.some((value) => value === "dual_slot" || value === "nginx" || value === "apache" || value === "java_keystore" || value === "hashicorp_vault"); const currentRotation = effectiveProfileRotation(profile); setWorkloadProfile(profileID, { ...profile, allowed_deployment_modes: values, rotation: { ...currentRotation, mode: dualSlot ? "dual_slot" : "in_place", ...(!values.some((value) => value === "nginx" || value === "apache" || value === "java_keystore") && currentRotation.promotion === "automatic_after_health" ? { promotion: "manual" as const } : {}) } }); }} />{mode.replaceAll("_", " ")}</label>)}</div>
          <div className="policy-field-grid policy-rotation-grid">
            <label className="modal-field"><span>Promotion</span><select value={effectiveProfileRotation(profile).promotion} onChange={(event) => setWorkloadProfile(profileID, { ...profile, rotation: { ...effectiveProfileRotation(profile), promotion: event.target.value as RotationPolicy["promotion"] } })}><option value="manual">Operator verifies & promotes</option><option value="automatic_after_health" disabled={!profile.allowed_deployment_modes.some((mode) => mode === "nginx" || mode === "apache" || mode === "java_keystore" || mode === "hashicorp_vault")}>Automatic after exact health</option></select></label>
            <label className="modal-field"><span>Renew before (hours)</span><input type="number" min="1" max={Math.max(1, Math.floor(profile.max_validity_seconds / 3600))} value={effectiveProfileRotation(profile).renew_before_seconds / 3600} onChange={(event) => setWorkloadProfile(profileID, { ...profile, rotation: { ...effectiveProfileRotation(profile), renew_before_seconds: Math.round(Number(event.target.value) * 3600) } })} /></label>
            <label className="modal-field"><span>Overlap (minutes)</span><input type="number" min="0" max="10080" value={effectiveProfileRotation(profile).overlap_seconds / 60} onChange={(event) => setWorkloadProfile(profileID, { ...profile, rotation: { ...effectiveProfileRotation(profile), overlap_seconds: Math.round(Number(event.target.value) * 60) } })} /></label>
            <label className="modal-field"><span>Health grace (seconds)</span><input type="number" min="10" max="3600" value={effectiveProfileRotation(profile).health_check_grace_seconds} onChange={(event) => setWorkloadProfile(profileID, { ...profile, rotation: { ...effectiveProfileRotation(profile), health_check_grace_seconds: Number(event.target.value) } })} /></label>
            <label className="modal-field"><span>Rollback window (minutes)</span><input type="number" min="1" max="1440" value={effectiveProfileRotation(profile).rollback_window_seconds / 60} onChange={(event) => setWorkloadProfile(profileID, { ...profile, rotation: { ...effectiveProfileRotation(profile), rollback_window_seconds: Math.round(Number(event.target.value) * 60) } })} /></label>
          </div>
        </article>)}
      </div></section>
      <section className="modal-section"><header><strong>Eligible approval nodes</strong><span>Leave all unchecked for any enrolled node, or pin an explicit quorum set.</span></header><div className="policy-node-grid">{nodes.map((node) => {
        const selected = draft.customer_approval_quorum?.eligible_node_ids?.includes(node.id) ?? false;
        return <label key={node.id}><input type="checkbox" checked={selected} onChange={() => { const current = draft.customer_approval_quorum?.eligible_node_ids ?? []; const next = selected ? current.filter((id) => id !== node.id) : [...current, node.id]; setDraft({ ...draft, customer_approval_quorum: { required: requiredNodes, ...(next.length ? { eligible_node_ids: next } : {}) } }); }} /><span><strong>{node.name}</strong><small>{node.id} · {node.status}</small></span></label>;
      })}</div></section>
      <section className="policy-impact-preview"><header><Fingerprint size={18} /><div><strong>Activation impact</strong><span>Computed before submission</span></div></header><ul>
        <li className={removedAlgorithms.length ? "impact-warning" : ""}><span>Algorithms removed</span><strong>{removedAlgorithms.length ? removedAlgorithms.join(", ") : "None"}</strong></li>
        <li className={healthyNodes < requiredNodes ? "impact-danger" : ""}><span>Healthy-node quorum now</span><strong>{healthyNodes} available / {requiredNodes} required</strong></li>
        <li><span>Maximum certificate lifetime</span><strong>{draft.validity.max_seconds / 3600} hours</strong></li>
        <li><span>Offline renewal</span><strong>{draft.validity.offline_renewal_max_seconds ? `${draft.validity.offline_renewal_max_seconds / 3600} hours` : "Disabled"}</strong></li>
        <li><span>Workload profiles</span><strong>{Object.keys(draft.workload_profiles).length} exact profile{Object.keys(draft.workload_profiles).length === 1 ? "" : "s"}</strong></li>
        <li><span>Dependency paths</span><strong>{impact ? `${impact.dependency_graph.live_edges} live / ${impact.dependency_graph.graph_edges} committed` : `${dependencies.length} awaiting assessment`}</strong></li>
      </ul>
      <div className="policy-impact-assessment">
        <button className="button button--quiet" disabled={busy} onClick={() => void assessImpact()}><CircleGauge size={15} />{busy ? "Assessing estate..." : impact ? "Re-assess current draft" : "Assess managed estate"}</button>
        {!impact ? <p>Compute a server-authoritative snapshot from managed certificate sets, in-flight issuance, and the latest capability evidence.</p> : <>
          <div className={`policy-impact-verdict ${impact.blocking ? "policy-impact-verdict--blocked" : "policy-impact-verdict--ready"}`}>
            {impact.blocking ? <AlertTriangle size={18} /> : <ShieldCheck size={18} />}
            <div><strong>{impact.blocking ? "Cryptographic activation is blocked" : "Assessment is ready for independent review"}</strong><span>{impact.cryptographic_change ? "Cryptographic behavior changes in this version." : "No cryptographic compatibility surface changes in this version."}</span></div>
          </div>
          <div className="policy-impact-counts">
            {(["compatible", "incompatible", "missing", "stale", "unassigned", "ambiguous", "unverified"] as const).map((status) => <span key={status}><b>{impact.counts[status]}</b>{status}</span>)}
          </div>
          {impact.endpoints.filter((endpoint) => endpoint.status !== "compatible").slice(0, 8).map((endpoint) => <article className="policy-impact-endpoint" key={endpoint.endpoint_id}><div><strong>{endpoint.endpoint_id}</strong><span>{endpoint.status} / {endpoint.profile_ids.join(", ") || "no profile"}</span></div><p>{endpoint.reasons.join("; ")}</p></article>)}
          {impact.details_truncated && <p>Details are bounded for review; the cryptographic commitment covers all {impact.counts.total} endpoints.</p>}
          <dl className="policy-impact-digests"><div><dt>Platform baseline</dt><dd><code>{impact.platform_policy_sha256 ?? "Not bound — refresh required"}</code></dd></div><div><dt>Estate commitment</dt><dd><code>{impact.endpoint_commitments_sha256}</code></dd></div><div><dt>Dependency graph</dt><dd><code>{impact.dependency_graph.graph_commitment_sha256}</code></dd></div><div><dt>Impact commitment</dt><dd><code>{impact.impact_sha256}</code></dd></div></dl>
          <label className="policy-impact-ack"><input type="checkbox" checked={impactAcknowledged} onChange={(event) => setImpactAcknowledged(event.target.checked)} /><span>I reviewed this exact assessment. Any capability refresh, expiry, endpoint, profile, or evidence change will invalidate approval.</span></label>
        </>}
      </div></section>
      {error && <div className="integration-error"><AlertTriangle size={15} />{error}</div>}
      <details className="advanced-policy-json"><summary>Advanced JSON preview</summary><pre>{JSON.stringify(draft, null, 2)}</pre></details>
      <footer><button className="button button--quiet" onClick={() => setEditorOpen(false)}>Cancel</button><button className="button button--primary" disabled={busy || healthyNodes < requiredNodes || !impactAcknowledged} onClick={() => void submit()}><ShieldCheck size={15} />{busy ? "Submitting…" : "Submit committed impact"}</button></footer>
    </div></Modal>}
  </div>;
}

function AccessView({
  onAssign,
  onRotateScim,
  onRevokeScim,
  onRefreshStatus,
  user,
  connector,
  demo,
}: {
  onAssign: (
    subject: string,
    role: ProductRole,
    operation: "grant" | "revoke",
  ) => Promise<void>;
  onRotateScim: () => Promise<ScimConnectorCredential>;
  onRevokeScim: () => Promise<void>;
  onRefreshStatus: () => Promise<ScimConnectorStatus | undefined>;
  user: ConsoleData["user"];
  connector: ScimConnectorStatus | undefined;
  demo: boolean;
}) {
  const [subject, setSubject] = useState("");
  const [role, setRole] = useState<ProductRole>("pki_operator");
  const [operation, setOperation] = useState<"grant" | "revoke">("grant");
  const [message, setMessage] = useState("");
  const [credential, setCredential] = useState<ScimConnectorCredential>();
  const [connectorMessage, setConnectorMessage] = useState("");
  const [assignments, setAssignments] = useState<AccessAssignment[]>([]);
  const [assignmentCursor, setAssignmentCursor] = useState<string>();
  const [assignmentsBusy, setAssignmentsBusy] = useState(true);
  const [assignmentsError, setAssignmentsError] = useState("");

  async function loadAssignments(append = false, cursor?: string) {
    setAssignmentsBusy(true);
    setAssignmentsError("");
    try {
      const page = demo ? {
        items: [
          { subject: "group:PKI-Admins", subject_type: "group" as const, display_subject: "group:PKI-Admins", active: true, role: "tenant_admin" as const },
          { subject: "user:00u-demo-operator", subject_type: "user" as const, immutable_subject: "00u-demo-operator", display_subject: "operator@acme.example", active: true, role: "pki_operator" as const },
          { subject: "group:PKI-Auditors", subject_type: "group" as const, display_subject: "group:PKI-Auditors", active: true, role: "auditor" as const },
        ],
      } : await listAccessAssignments(cursor);
      setAssignments((current) => append ? [...current, ...page.items] : page.items);
      setAssignmentCursor("next_cursor" in page ? page.next_cursor : undefined);
    } catch (error) {
      setAssignmentsError(error instanceof Error ? error.message : "Assignments could not be loaded");
    } finally {
      setAssignmentsBusy(false);
    }
  }

  useEffect(() => { void loadAssignments(); }, [demo]);
  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage("");
    try {
      const changedSubject = subject.trim();
      await onAssign(changedSubject, role, operation);
      if (demo) {
        setAssignments((current) => operation === "grant"
          ? [{ subject: changedSubject, subject_type: changedSubject.toLowerCase().startsWith("group:") ? "group" : "user", display_subject: changedSubject, active: true, role }, ...current.filter((item) => item.display_subject !== changedSubject || item.role !== role)]
          : current.filter((item) => item.display_subject !== changedSubject || item.role !== role));
      } else {
        await loadAssignments();
      }
      setMessage(
        operation === "grant"
          ? "Access assignment saved."
          : "Access assignment removed.",
      );
      setSubject("");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Assignment failed");
    }
  }
  async function removeAssignment(assignment: AccessAssignment) {
    if (!assignment.active || !/^(?:group:.+|[^@\s]+@[^@\s]+\.[^@\s]+)$/.test(assignment.display_subject)) return;
    if (!window.confirm(`Remove ${assignment.role.replaceAll("_", " ")} from ${assignment.display_subject}?`)) return;
    setMessage("");
    try {
      await onAssign(assignment.display_subject, assignment.role, "revoke");
      if (demo) setAssignments((current) => current.filter((item) => item !== assignment));
      else await loadAssignments();
      setMessage("Access assignment removed.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Assignment removal failed");
    }
  }
  async function syncStatus() {
    if (demo) {
      setConnectorMessage("SCIM is push-based. Current connector and provisioned group state is shown here.");
      return;
    }
    setConnectorMessage("Refreshing connector and assignments…");
    try {
      const [current] = await Promise.all([onRefreshStatus(), loadAssignments()]);
      setConnectorMessage(
        current?.status === "active"
          ? `Connector active · ${current.last_used_at ? `last provisioning call ${timeAgo(current.last_used_at)}` : "no provisioning call received yet"}.`
          : current?.status === "revoked"
            ? "Connector token is revoked. Provisioning is stopped."
            : "No SCIM connector is configured. Roles are assigned directly.",
      );
    } catch (error) {
      setConnectorMessage(error instanceof Error ? error.message : "Status could not be refreshed");
    }
  }
  async function rotateConnector() {
    if (
      connector?.status === "active" &&
      !window.confirm(
        "Rotate the current SCIM token? The existing token will stop working immediately.",
      )
    )
      return;
    setConnectorMessage("");
    try {
      setCredential(await onRotateScim());
      setConnectorMessage("Copy this token now. It cannot be retrieved later.");
    } catch (error) {
      setConnectorMessage(
        error instanceof Error ? error.message : "SCIM token rotation failed",
      );
    }
  }
  async function revokeConnector() {
    if (
      !window.confirm(
        "Revoke the current SCIM token immediately? Provisioning will stop until a new token is created.",
      )
    )
      return;
    setConnectorMessage("");
    try {
      await onRevokeScim();
      setCredential(undefined);
      setConnectorMessage("SCIM connector token revoked.");
    } catch (error) {
      setConnectorMessage(
        error instanceof Error ? error.message : "SCIM token revocation failed",
      );
    }
  }
  const roles: Array<{ id: ProductRole; name: string; text: string }> = [
    {
      id: "tenant_admin",
      name: "Tenant Admin",
      text: "Users, CAs, policy, nodes, and incidents",
    },
    {
      id: "pki_operator",
      name: "PKI Operator",
      text: "Issue, revoke, rotate, and operate nodes",
    },
    {
      id: "break_glass_operator",
      name: "Break-glass Operator",
      text: "Pause issuance or independently approve a guarded resume",
    },
    {
      id: "auditor",
      name: "Auditor",
      text: "Inventory and cryptographic evidence",
    },
    {
      id: "application_owner",
      name: "Application Owner",
      text: "Scoped profiles and certificate sets",
    },
    { id: "read_only", name: "Read Only", text: "Trust posture and inventory" },
  ];
  return (
    <div className="grid grid--2-1">
      <Card
        title="Role assignments"
        action={
          <button
            className="button button--quiet"
            onClick={() => void syncStatus()}
          >
            <RefreshCw size={14} /> Sync status
          </button>
        }
      >
        <form className="access-form" onSubmit={(event) => void submit(event)}>
          <label>
            <span>User or group</span>
            <input
              value={subject}
              onChange={(event) => setSubject(event.target.value)}
              placeholder="alex@company.com or group:PKI-Admins"
              required
            />
          </label>
          <label>
            <span>Product role</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value as ProductRole)}
            >
              {roles.map((item) => (
                <option value={item.id} key={item.id}>
                  {item.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Change</span>
            <select
              value={operation}
              onChange={(event) =>
                setOperation(event.target.value as "grant" | "revoke")
              }
            >
              <option value="grant">Grant role</option>
              <option value="revoke">Remove role</option>
            </select>
          </label>
          <button className="button button--primary" type="submit">
            {operation === "grant" ? "Assign access" : "Remove access"}
          </button>
        </form>
        {message && <p className="form-message">{message}</p>}
        <div className="access-changes" aria-live="polite">
          <span>AUTHORITATIVE OPENFGA ASSIGNMENTS</span>
          {assignmentsBusy && assignments.length === 0 && <p className="form-message">Loading current assignments…</p>}
          {assignmentsError && <p className="form-message form-message--error">{assignmentsError}</p>}
          {!assignmentsBusy && !assignmentsError && assignments.length === 0 && <p className="form-message">No direct user or group role is assigned.</p>}
          {assignments.map((item) => (
            <div key={`${item.subject}:${item.role}`}>
              <code>{item.display_subject}</code>
              <b>{item.role.replaceAll("_", " ")}</b>
              <Status value={item.active ? "healthy" : "warning"} label={item.active ? "active" : "deprovisioned"} />
              <button
                className="text-button"
                disabled={!item.active || !/^(?:group:.+|[^@\s]+@[^@\s]+\.[^@\s]+)$/.test(item.display_subject)}
                onClick={() => void removeAssignment(item)}
              >Remove</button>
            </div>
          ))}
          {assignmentCursor && <button className="button button--quiet" disabled={assignmentsBusy} onClick={() => void loadAssignments(true, assignmentCursor)}>Load more</button>}
        </div>
        <div className="role-list">
          {roles.map((item) => (
            <article key={item.id}>
              <span>
                <Users size={16} />
              </span>
              <div>
                <strong>{item.name}</strong>
                <p>{item.text}</p>
              </div>
              <button
                aria-label={`Manage ${item.name}`}
                onClick={() => {
                  setRole(item.id);
                  setOperation("grant");
                  setMessage(
                    `${item.name} selected. Enter a provisioned user or group above.`,
                  );
                }}
              >
                <ArrowRight size={15} />
              </button>
            </article>
          ))}
        </div>
      </Card>
      <div className="stack">
        <Card title="Enterprise identity">
          <div className="identity-card">
            <div className="avatar">{user.email.slice(0, 2).toUpperCase()}</div>
            <div>
              <strong>{user.email}</strong>
              <span>{user.groups[0] ?? "Direct assignment"}</span>
            </div>
            <Status
              value={user.mfa ? "healthy" : "warning"}
              label={user.mfa ? "MFA" : "MFA missing"}
            />
          </div>
          <dl className="detail-list">
            {(demo
              ? [
                  { label: "Authentication", value: "Sectigo Edge SSO" },
                  { label: "Federation", value: "SAML / OIDC" },
                  { label: "Provisioning", value: "SCIM groups" },
                ]
              : identityConfiguration(user, connector)
            ).map((row) => (
              <div key={row.label}>
                <dt>{row.label}</dt>
                <dd>{row.value}</dd>
              </div>
            ))}
          </dl>
        </Card>
        <Card title="SCIM provisioning">
          <div className="scim-connector">
            <header>
              <div>
                <strong>
                  {connector?.status === "active"
                    ? "Connector active"
                    : connector?.status === "revoked"
                      ? "Token revoked"
                      : "Not configured"}
                </strong>
                <p>
                  {connector?.status === "active"
                    ? `Token ${connector.token_hint ?? "configured"} · generation ${connector.token_version ?? 1}`
                    : "Create a tenant-bound token for your identity provider."}
                </p>
              </div>
              <Status
                value={connector?.status === "active" ? "healthy" : "warning"}
                label={connector?.status === "active" ? "active" : "setup"}
              />
            </header>
            {connector?.base_url && <code>{connector.base_url}</code>}
            {credential && (
              <div className="scim-secret">
                <span>ONE-TIME BEARER TOKEN</span>
                <div>
                  <code>{credential.bearer_token}</code>
                  <button
                    aria-label="Copy SCIM token"
                    onClick={() =>
                      void navigator.clipboard.writeText(
                        credential.bearer_token,
                      )
                    }
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>
            )}
            <div className="scim-actions">
              <button
                className="button button--primary"
                onClick={() => void rotateConnector()}
              >
                {connector?.status === "active"
                  ? "Rotate token"
                  : "Create token"}
              </button>
              {connector?.status === "active" && (
                <button
                  className="button button--danger-quiet"
                  onClick={() => void revokeConnector()}
                >
                  Revoke
                </button>
              )}
            </div>
            {connectorMessage && (
              <p className="form-message">{connectorMessage}</p>
            )}
            <small>
              Protected by tenant-admin authorization and fresh MFA. Sectigo
              Edge stores only the token digest.
            </small>
          </div>
        </Card>
        <Card className="policy-rule">
          <ShieldEllipsis size={22} />
          <div>
            <strong>Fine-grained authorization</strong>
            <p>
              Every API action is checked against its tenant, CA, profile, node,
              certificate set, policy, or audit stream.
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}

function AuditList({
  events,
  compactMode = false,
}: {
  events: AuditEvent[];
  compactMode?: boolean;
}) {
  return (
    <div className={cx("audit-list", compactMode && "audit-list--compact")}>
      {events.map((event) => (
        <article key={event.id}>
          <span className="audit-icon">
            <Check size={13} />
          </span>
          <div>
            <strong>{event.type.replaceAll(".", " ")}</strong>
            <p>
              {event.actor}
              {event.transaction_id ? ` · ${event.transaction_id}` : ""}
            </p>
          </div>
          <time>{timeAgo(event.occurred_at)}</time>
          <code>{event.event_hash.slice(0, 9)}…</code>
        </article>
      ))}
    </div>
  );
}

function AuditView({
  events,
  witness,
  verification,
  demo,
  onExport,
}: {
  events: AuditEvent[];
  witness: Overview["audit_witness"];
  verification: AuditVerification;
  demo: boolean;
  onExport: (transactionId: string) => void;
}) {
  const integrity = demo ? undefined : auditIntegrityLabel(verification);
  const transactionId = events.find(
    (event) => event.transaction_id,
  )?.transaction_id;
  return (
    <Card
      title="Tenant evidence chain"
      action={
        <button
          disabled={!transactionId}
          className="button button--quiet"
          onClick={() => transactionId && onExport(transactionId)}
        >
          <Download size={14} /> Export portable proof
        </button>
      }
    >
      <div className="chain-banner">
        <Fingerprint size={24} />
        <div>
          <strong>{integrity ? integrity.title : "Integrity verified end to end"}</strong>
          <p>
            {integrity && <>{integrity.detail} </>}
            Each event commits to the previous hash. Exported proofs include
            both trust decisions, node signatures, the signer registry
            attestation, a signed checkpoint, and a customer-retained witness
            quorum that can detect a cloud-side fork.
          </p>
        </div>
        <Status
          value={witness.status === "healthy" ? "healthy" : witness.status === "pending" ? "warning" : "offline"}
          label={witness.status === "healthy" ? `${witness.received}/${witness.required} witnessed` : witness.status.replace("_", " ")}
        />
      </div>
      <dl className="audit-witness-summary">
        <div><dt>Checkpoint</dt><dd>{witness.checkpoint_sequence ? `#${witness.checkpoint_sequence}` : "Not anchored"}</dd></div>
        <div><dt>Distinct signatures</dt><dd>{witness.received} of {witness.required}</dd></div>
        <div><dt>Customer nodes</dt><dd>{witness.node_ids.length ? witness.node_ids.join(", ") : "Waiting for an eligible Mesh Node"}</dd></div>
      </dl>
      <AuditList events={events} />
    </Card>
  );
}

const trustEventProjection: Record<string, string> = {
  "capability.observed": "capability.observed",
  "service.registration.activated": "service.claimed",
  "policy.activated": "policy.version.available",
  "certificate.staged": "certificate.staged",
  "certificate.promoted": "certificate.promoted",
  "issuance.paused": "incident.issuance_paused",
  "issuance.resumed": "incident.issuance_paused",
};

function TrustEventsView({ events, demo }: { events: AuditEvent[]; demo: boolean }) {
  const projected = events.filter((event) => trustEventProjection[event.type]);
  return (
    <div className="trust-events-view">
      <Card>
        <div className="trust-event-hero">
          <span className="trust-event-hero__icon"><RadioTower size={24} /></span>
          <div>
            <span className="eyebrow">Cryptographically verified pub/sub</span>
            <h2>React quickly without moving authority into a message bus</h2>
            <p>Mesh Nodes and Sectigo Edge publish independently signed facts. SDKs verify the tenant, source, key pin, sequence, digest, and signature before reconciliation begins.</p>
          </div>
          {demo
            ? <Status value="healthy" label="low-authority contract" />
            : <span className="tag">{projected.length} distributable fact{projected.length === 1 ? "" : "s"} in window</span>}
        </div>
        <div className="trust-event-boundary">
          <ShieldCheck size={20} />
          <p><strong>Security boundary:</strong> an event can trigger refresh or reconciliation. It can never approve issuance, activate policy, release a private key, authorize a claim, or revoke a certificate.</p>
        </div>
        {!demo && <p className="trust-event-copy">How each subscriber verifies an event before acting on it:</p>}
        <div className="trust-event-flow" aria-label="Signed trust event verification flow">
          {[
            ["1", "Source fact", "Hash-chained tenant or node audit"],
            ["2", "Safe projection", "Topic and fields are allowlisted"],
            ["3", "Source signature", "Tenant and source identity are bound"],
            ["4", "SDK verification", "Pinned key, digest, cursor, signature"],
            ["5", "Reconcile", "Local first; cloud only on transport loss"],
          ].map(([step, name, detail]) => <article key={step}><span>{step}</span><strong>{name}</strong><p>{detail}</p></article>)}
        </div>
      </Card>
      <div className="trust-event-grid">
        <Card title="Events eligible for projection">
          <div className="trust-event-feed">
            {projected.length ? projected.map((event) => (
              <article key={event.id}>
                <span className="trust-event-dot"><Check size={13} /></span>
                <div><strong>{trustEventProjection[event.type]}</strong><p>{event.type} · sequence {event.sequence.toLocaleString()}</p></div>
                <time>{timeAgo(event.occurred_at)}</time>
              </article>
            )) : <p className="trust-event-empty">No distributable facts are present in the current audit window.</p>}
          </div>
        </Card>
        <Card title="SDK and CLI subscriptions">
          <p className="trust-event-copy">The cursor is stored per signed source. Delivery is at least once, so consumers remain correct through restarts and local-to-cloud route changes.</p>
          <pre className="trust-event-code"><code>{`for await (const update of client.subscribeTrustEvents({
  topics: ["certificate.promoted", "policy.version.available"],
  cursorStore,
  signal,
})) {
  await reconcile(update.batch.events);
}`}</code></pre>
          <p className="trust-event-copy">The downloadable <code>sectigo-edge events</code> command applies the same node-key pin, signature, tenant, sequence, cursor, and freshness checks, then commits its local cursor only after verified output is written.</p>
          <div className="trust-event-pills"><span>mTLS workload identity</span><span>Explicit SPIFFE allowlist</span><span>Expired cursors fail closed</span></div>
        </Card>
      </div>
    </div>
  );
}

function CertificatesView({
  data,
  onRevoke,
  onRequest,
}: {
  data: ConsoleData;
  onRevoke: (set: CertificateSet) => void;
  onRequest: () => void;
}) {
  const [query, setQuery] = useState("");
  const [lifecycle, setLifecycle] = useState("all");
  const [algorithm, setAlgorithm] = useState("all");
  const normalizedQuery = query.trim().toLowerCase();
  // The demo keeps its sample algorithm; a real tenant sees what each issuing transaction recorded.
  const algorithmOf = (set: CertificateSet) =>
    data.demo ? "ECDSA P-384" : certificateAlgorithm(set, data.transactions);
  const algorithms = [...new Set(data.certificateSets.map(algorithmOf).filter((item): item is string => Boolean(item)))].sort();
  const visibleSets = data.certificateSets.filter((set) => {
    const matchesQuery =
      !normalizedQuery ||
      set.endpoint_id.toLowerCase().includes(normalizedQuery) ||
      set.profile_id.toLowerCase().includes(normalizedQuery) ||
      (set.active?.serial_number ?? "").toLowerCase().includes(normalizedQuery);
    return (
      matchesQuery &&
      (lifecycle === "all" || set.active?.state === lifecycle) &&
      (algorithm === "all" || algorithmOf(set) === algorithm)
    );
  });
  return (
    <Card
      className="table-card"
      title={`${compact(data.overview.counts.certificates ?? 0)} managed identities`}
      action={
        <button className="button button--primary" onClick={onRequest}>
          <FileKey2 size={15} /> Request certificate
        </button>
      }
    >
      <div className="filterbar">
        <div className="filter-search">
          <FileKey2 size={15} />
          <input
            aria-label="Search certificates"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search names, serials, profiles…"
          />
          <span>Search names, serials, profiles…</span>
        </div>
        <select
          aria-label="Algorithm"
          value={algorithm}
          onChange={(event) => setAlgorithm(event.target.value)}
        >
          <option value="all">All algorithms</option>
          {algorithms.map((item) => (
            <option value={item} key={item}>{item}</option>
          ))}
        </select>
        <select
          aria-label="Lifecycle state"
          value={lifecycle}
          onChange={(event) => setLifecycle(event.target.value)}
        >
          <option value="all">All lifecycle states</option>
          <option value="serving">Serving</option>
          <option value="staged">Staged</option>
          <option value="draining">Draining</option>
          <option value="revoked">Revoked</option>
        </select>
      </div>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Identity</th>
              <th>Profile</th>
              <th>Algorithm</th>
              <th>Deployment</th>
              <th>Expiry</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {visibleSets.map((set) => (
              <tr key={set.id}>
                <td>
                  <div className="primary-cell">
                    <span className="table-icon">
                      <FileKey2 size={16} />
                    </span>
                    <div>
                      <strong>
                        {set.endpoint_id.replace("spiffe://", "")}
                      </strong>
                      <small className="mono">
                        {set.active?.serial_number}
                      </small>
                    </div>
                  </div>
                </td>
                <td>{set.profile_id}</td>
                <td>
                  {algorithmOf(set) ? <span className="tag">{algorithmOf(set)}</span> : "—"}
                </td>
                <td>
                  <span className="tag tag--aqua">
                    {set.rotation_policy.mode.replace("_", " ")}
                  </span>
                </td>
                <td>
                  {set.active
                    ? new Date(set.active.not_after).toLocaleDateString()
                    : "—"}
                </td>
                <td>
                  <Status
                    value={
                      set.active?.health === "healthy" ? "healthy" : "warning"
                    }
                    label={set.active?.state ?? "pending"}
                  />
                </td>
                <td>
                  <button
                    disabled={!set.active || set.active.state === "revoked"}
                    className="button button--danger-quiet"
                    onClick={() => onRevoke(set)}
                  >
                    <ShieldX size={14} /> Revoke
                  </button>
                </td>
              </tr>
            ))}
            {visibleSets.length === 0 && (
              <tr>
                <td colSpan={7}>No managed identities match these filters.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function IntegrationsView({
  integrations,
  onConfigure,
  onTest,
}: {
  integrations: IntegrationRecord[];
  onConfigure: (record: IntegrationRecord) => void;
  onTest: (record: IntegrationRecord) => void;
}) {
  const details: Record<IntegrationKind, { text: string; icon: LucideIcon }> = {
    acme: { text: "Local workload certificate lifecycle", icon: Zap },
    spiffe: { text: "mTLS workload identity binding", icon: Fingerprint },
    nginx: {
      text: "Atomic TLS deployment, exact-cert health, and rollback",
      icon: Server,
    },
    apache: {
      text: "Graceful TLS rotation, exact-cert health, and rollback",
      icon: Server,
    },
    java_keystore: {
      text: "Atomic PKCS#12 delivery, application reload, and rollback",
      icon: Coffee,
    },
    hashicorp_vault: {
      text: "KV v2 delivery with CAS, health check, and rollback",
      icon: Vault,
    },
    kubernetes_csr: { text: "Native cluster issuance adapter", icon: Boxes },
    microsoft_adcs: {
      text: "Compatibility and migration adapter",
      icon: Laptop,
    },
    est: { text: "Device enrollment adapter", icon: Network },
    sectigo_scm: {
      text: "Managed CA and protected signing",
      icon: ShieldCheck,
    },
  };
  const status = (record: IntegrationRecord) =>
    record.status === "healthy"
      ? { label: "Healthy", tone: "healthy" as const }
      : record.status === "pending_node"
        ? { label: "Awaiting node", tone: "warning" as const }
        : record.status === "degraded"
          ? { label: "Degraded", tone: "warning" as const }
          : { label: "Disabled", tone: "offline" as const };
  return (
    <div className="integration-grid">
      {integrations.map((record) => {
        const detail = details[record.kind]!;
        const Icon = detail.icon;
        const state = status(record);
        return (
          <Card key={record.kind}>
            <header className="integration-head">
              <span className="integration-icon">
                <Icon size={21} />
              </span>
              <Status value={state.tone} label={state.label} />
            </header>
            <h3>{record.display_name}</h3>
            <p>{detail.text}</p>
            {record.last_error_code && (
              <p className="integration-error">
                {record.last_error_code.replaceAll("_", " ")}
              </p>
            )}
            <div className="integration-meta">
              <span>{integrationTargets(record)}</span>
              <span>Revision {record.revision}</span>
            </div>
            <div className="integration-actions">
              <button
                className="text-button"
                onClick={() => onConfigure(record)}
              >
                Configure <ArrowRight size={14} />
              </button>
              {record.enabled && (
                <button className="text-button" onClick={() => onTest(record)}>
                  <Activity size={14} /> Test
                </button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function IncidentsView({
  paused,
  quorum,
  requests,
  transactions,
  currentUserId,
  demo,
  audit,
  onOpenControl,
}: {
  audit: AuditVerification;
  paused: boolean;
  quorum: ConsoleData["overview"]["approval_quorum"];
  requests: BreakGlassResumeRequest[];
  transactions: TransactionRecord[];
  currentUserId: string;
  demo: boolean;
  onOpenControl: () => void;
}) {
  const pending = requests.find((request) => request.status === "pending");
  const awaitingAnotherOperator = pending?.requested_by === currentUserId && !demo;
  const assessments = transactions.flatMap((record) => record.issuance_risk ? [{ record, assessment: record.issuance_risk }] : []);
  const findings = assessments.flatMap(({ record, assessment }) => assessment.signals.map((signal) => ({ record, assessment, signal })));
  const blocked = assessments.filter(({ assessment }) => assessment.decision === "block").length;
  return (
    <div className="grid grid--2-1">
      <Card title="Security controls">
        <article className="incident-control">
          <span className={paused ? "danger-icon" : "safe-icon"}>
            {paused ? <PauseCircle size={24} /> : <ShieldCheck size={24} />}
          </span>
          <div>
            <strong>
              {paused
                ? "New issuance is paused"
                : "Issuance controls are normal"}
            </strong>
            <p>
              Existing certificates continue operating. New issuance and
              renewals requiring approval fail closed.
            </p>
          </div>
          <button
            className={cx(
              "button",
              paused ? "button--primary" : "button--danger",
            )}
            onClick={onOpenControl}
            disabled={Boolean(awaitingAnotherOperator)}
          >
            {!paused
              ? "Activate kill switch"
              : !pending
                ? "Request guarded resume"
                : awaitingAnotherOperator
                  ? "Awaiting another operator"
                  : demo && pending.requested_by === currentUserId
                    ? "Simulate second approval"
                    : "Independently approve resume"}
          </button>
        </article>
        {pending && (
          <article className="break-glass-request">
            <header>
              <span>RESUME REQUEST · GENERATION {pending.pause_generation}</span>
              {demo
                ? <Status value="warning" label="1 of 2 approvals" />
                : <Status {...resumeRequestState(pending)} />}
            </header>
            <strong>{pending.reason}</strong>
            <p>
              Requested by <code>{pending.requested_by}</code>. Expires at{" "}
              {new Date(pending.expires_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.
            </p>
            <small>A different immutable SSO identity with fresh MFA and the Break-glass Operator role must approve.</small>
          </article>
        )}
        <div className="incident-list">
          {assessments.length === 0 ? <span><Clock3 size={15} /> No issuance-risk assessment has been recorded yet</span> : findings.length === 0 ? <span><Check size={15} /> {assessments.length} signed issuance assessment{assessments.length === 1 ? "" : "s"} · no guardrail signal</span> : <span><AlertTriangle size={15} /> {findings.length} signed guardrail signal{findings.length === 1 ? "" : "s"} · {blocked} request{blocked === 1 ? "" : "s"} blocked</span>}
          {demo ? (
            <span>
              <Check size={15} /> Audit chain integrity verified
            </span>
          ) : (
            <span>
              {audit.state === "verified" ? <Check size={15} /> : audit.state === "pending" || audit.state === "empty" ? <Clock3 size={15} /> : <AlertTriangle size={15} />}{" "}
              {auditIntegrityLabel(audit).title}
            </span>
          )}
          <span>
            {quorum.available ? (
              <Check size={15} />
            ) : (
              <AlertTriangle size={15} />
            )}{" "}
            {quorum.healthy_nodes} healthy customer trust replicas
          </span>
        </div>
        {findings.length > 0 && <div className="risk-finding-list">{findings.slice(0, 6).map(({ record, assessment, signal }) => <article key={`${record.transaction.transaction_id}-${signal.type}`}>
          <span className={assessment.decision === "block" ? "danger-icon" : "safe-icon"}>{assessment.decision === "block" ? <ShieldX size={17} /> : <Activity size={17} />}</span>
          <div><strong>{signal.type.replaceAll("_", " ")}</strong><p>{signal.explanation}</p><code>{record.transaction.requester}</code></div>
          <Status value={assessment.decision === "block" ? "offline" : "warning"} label={assessment.decision} />
        </article>)}</div>}
      </Card>
      <Card title="Break-glass policy">
        <dl className="detail-list">
          <div>
            <dt>Resume authority</dt>
            <dd>Two distinct humans</dd>
          </div>
          <div>
            <dt>Approval quorum</dt>
            <dd>
              {quorum.required} of {quorum.eligible_nodes} local
            </dd>
          </div>
          <div>
            <dt>Quorum capacity</dt>
            <dd>{quorum.available ? "Available" : "Fail closed"}</dd>
          </div>
          <div>
            <dt>Approval window</dt>
            <dd>10 minutes · generation bound</dd>
          </div>
        </dl>
      </Card>
    </div>
  );
}

// Re-hashes the loaded audit window in the browser for real tenants; the demo's
// sample events carry illustrative hashes and keep their sample copy instead.
function useAuditVerification(data: ConsoleData | undefined): AuditVerification {
  const [result, setResult] = useState<AuditVerification>({ state: "pending" });
  const events = data?.audit;
  const head = data?.overview.audit_head;
  const demo = data?.demo ?? true;
  useEffect(() => {
    if (demo || !events) return;
    let active = true;
    setResult({ state: "pending" });
    void verifyAuditWindow(events, head, sha256).then((value) => { if (active) setResult(value); });
    return () => { active = false; };
  }, [events, head?.sequence, head?.hash, demo]);
  return result;
}

function Empty({
  title: emptyTitle,
  text,
  icon: Icon,
}: {
  title: string;
  text: string;
  icon: LucideIcon;
}) {
  return (
    <div className="empty">
      <span>
        <Icon size={24} />
      </span>
      <strong>{emptyTitle}</strong>
      <p>{text}</p>
    </div>
  );
}

function Modal({
  title: modalTitle,
  children,
  onClose,
  size = "standard",
}: {
  title: string;
  children: ReactNode;
  onClose: () => void;
  size?: "standard" | "wide";
}) {
  const titleId = useId();
  const dialog = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : undefined;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialog.current?.focus();
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", closeOnEscape);
    return () => {
      window.removeEventListener("keydown", closeOnEscape);
      document.body.style.overflow = priorOverflow;
      previous?.focus();
    };
  }, [onClose]);
  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        ref={dialog}
        className={cx("modal", size === "wide" && "modal--wide")}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
      >
        <header>
          <div>
            <span>SECTIGO EDGE</span>
            <h2 id={titleId}>{modalTitle}</h2>
          </div>
          <button onClick={onClose} aria-label="Close">
            <X size={20} />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}

export function App() {
  const [view, setView] = useState<View>(initialView);
  const [data, setData] = useState<ConsoleData>();
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pauseModal, setPauseModal] = useState(false);
  const [caModal, setCaModal] = useState(false);
  const [caManageTarget, setCaManageTarget] =
    useState<CertificateAuthorityConnector>();
  const [crlRefreshTarget, setCrlRefreshTarget] = useState<CertificateAuthorityConnector>();
  const [crlConfirmation, setCrlConfirmation] = useState("");
  const [ocspRefreshTarget, setOcspRefreshTarget] = useState<OCSPPublicationStatus>();
  const [ocspConfirmation, setOcspConfirmation] = useState("");
  const [ocspLoadingMore, setOcspLoadingMore] = useState(false);
  const [enrollModal, setEnrollModal] = useState(false);
  const [enrollmentName, setEnrollmentName] = useState("production-edge-01");
  const [enrollmentPlatform, setEnrollmentPlatform] = useState<NodeEnrollmentPackage["platform"]>("linux");
  const [enrollmentPackage, setEnrollmentPackage] = useState<NodeEnrollmentPackage>();
  const [enrollmentError, setEnrollmentError] = useState("");
  const [enrollmentLoading, setEnrollmentLoading] = useState(false);
  const [requestModal, setRequestModal] = useState(false);
  const [profileModal, setProfileModal] = useState(false);
  const [accountPasskeys, setAccountPasskeys] = useState<AccountPasskey[]>([]);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyMessage, setPasskeyMessage] = useState("");
  const [caName, setCaName] = useState("");
  const [caError, setCaError] = useState("");
  const [scmIdDraft, setScmIdDraft] = useState<ScmIdDraft>(EMPTY_SCM_DRAFT);
  const [scmIdErrors, setScmIdErrors] = useState<{ organization?: string; certificateType?: string }>({});
  const [scmManageDraft, setScmManageDraft] = useState<ScmIdDraft>(EMPTY_SCM_DRAFT);
  const [scmManageErrors, setScmManageErrors] = useState<{ organization?: string; certificateType?: string }>({});
  const [scmManageMessage, setScmManageMessage] = useState("");
  const [revokeTarget, setRevokeTarget] = useState<CertificateSet>();
  const [registrationTarget, setRegistrationTarget] =
    useState<ServiceRegistration>();
  const [integrationTarget, setIntegrationTarget] =
    useState<IntegrationRecord>();
  const [integrationDraft, setIntegrationDraft] =
    useState<IntegrationConfigurationInput>({
      enabled: false,
      node_ids: [],
      profile_id: "default",
    });
  const [integrationError, setIntegrationError] = useState("");
  const [registrationApproval, setRegistrationApproval] =
    useState<RegistrationApprovalInput>({
      human_code: "",
      owner: "",
      profile_id: "agent-mtls",
      channels: [],
      x509_svid_ttl: 3600,
      jwt_svid_ttl: 300,
    });
  const [revocationReason, setRevocationReason] = useState("key_compromise");
  const [revocationComment, setRevocationComment] = useState("");
  const [safeFailover, setSafeFailover] = useState(true);
  const [reason, setReason] = useState("");
  const [breakGlassConfirmation, setBreakGlassConfirmation] = useState("");
  const [toast, setToast] = useState("");

  const auditVerification = useAuditVerification(data);
  const profile = profileSummary(data?.user, Boolean(data?.demo));

  async function refresh() {
    setLoading(true);
    setError("");
    try {
      setData(await loadConsoleData());
    } catch (loadError) {
      if (
        loadError instanceof ConsoleApiError &&
        (loadError.status === 401 || loadError.code === "authentication_required")
      ) {
        const returnTo = `${location.pathname}${location.search}`;
        location.replace(`/login?return_to=${encodeURIComponent(returnTo)}`);
        return;
      }
      setError(
        loadError instanceof Error
          ? loadError.message
          : "Console data could not be loaded",
      );
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    void refresh();
  }, []);
  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(""), 3500);
    return () => clearTimeout(timer);
  }, [toast]);
  useEffect(() => {
    if (!data || view !== "integrations" || integrationTarget) return;
    const url = new URL(window.location.href);
    const requestedKind = url.searchParams.get("configure");
    if (!requestedKind) return;
    const record = data.integrations.find((item) => item.kind === requestedKind);
    if (!record) return;
    configureIntegration(record);
    url.searchParams.delete("configure");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [data, view, integrationTarget]);

  async function passwordAccountCall<T>(path: string, body: unknown): Promise<T> {
    if (!data) throw new Error("Account context is unavailable.");
    const suffix = `?tenant=${encodeURIComponent(data.user.tenant_id)}`;
    const response = await fetch(`${path}${suffix}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
    const result = await response.json().catch(() => ({})) as T & { error?: { message?: string } };
    if (!response.ok) throw new Error(result.error?.message ?? "Account security request failed.");
    return result;
  }

  async function loadAccountPasskeys() {
    if (!data || data.demo || data.user.authentication_method !== "password") return;
    setPasskeyBusy(true); setPasskeyMessage("");
    try {
      const result = await passwordAccountCall<{ passkeys: AccountPasskey[] }>("/api/v1/auth/password/passkeys/list", {});
      setAccountPasskeys(result.passkeys);
    } catch (loadError) {
      setPasskeyMessage(loadError instanceof Error ? loadError.message : "Passkeys could not be loaded.");
    } finally { setPasskeyBusy(false); }
  }

  useEffect(() => {
    if (profileModal && data?.user.authentication_method === "password" && !data.demo) void loadAccountPasskeys();
  }, [profileModal, data?.user.authentication_method, data?.user.tenant_id, data?.demo]);

  async function addAccountPasskey() {
    if (!passkeysSupported()) { setPasskeyMessage("This browser does not support passkeys."); return; }
    setPasskeyBusy(true); setPasskeyMessage("");
    try {
      const options = await passwordAccountCall<PasskeyEnrollmentOptions>("/api/v1/auth/password/passkeys/enrollment/options", {});
      const credential = await createPasskeyCredential(options);
      await passwordAccountCall("/api/v1/auth/password/passkeys/enrollment/verify", { challenge_token: options.challenge_token, credential });
      setPasskeyMessage("Passkey enrolled. It is available on your next sign-in.");
      const listed = await passwordAccountCall<{ passkeys: AccountPasskey[] }>("/api/v1/auth/password/passkeys/list", {});
      setAccountPasskeys(listed.passkeys);
    } catch (addError) {
      setPasskeyMessage(addError instanceof DOMException && addError.name === "NotAllowedError"
        ? "Passkey enrollment was cancelled or timed out."
        : addError instanceof Error ? addError.message : "Passkey enrollment failed.");
    } finally { setPasskeyBusy(false); }
  }

  async function removeAccountPasskey(passkey: AccountPasskey) {
    if (!window.confirm(`Revoke passkey …${passkey.credential_id_hint}? Every account session will be signed out.`)) return;
    setPasskeyBusy(true); setPasskeyMessage("");
    try {
      await passwordAccountCall("/api/v1/auth/password/passkeys/revoke", {
        credential_id: passkey.credential_id,
        confirmation: "REVOKE PASSKEY",
      });
      location.assign("/login?passkey_revoked=1");
    } catch (removeError) {
      setPasskeyMessage(removeError instanceof Error ? removeError.message : "Passkey revocation failed.");
      setPasskeyBusy(false);
    }
  }
  const page = title[view];
  const sectionedNav = useMemo(
    () => (data?.demo ? [...nav, guidedNavItem] : nav),
    [data?.demo],
  );

  async function submitBreakGlassControl() {
    if (!data) return;
    const pending = data.breakGlass?.requests.find((request) => request.status === "pending");
    const action = !data.overview.issuance_paused ? "pause" : pending ? "approve" : "request";
    const expected = action === "pause" ? "PAUSE ISSUANCE" : action === "request" ? "REQUEST ISSUANCE RESUME" : "APPROVE ISSUANCE RESUME";
    if (breakGlassConfirmation !== expected || action !== "approve" && reason.trim().length < 16) return;
    try {
      if (action === "pause") {
        const result = data.demo
          ? { paused: true as const, pause_generation: (data.breakGlass?.pause_generation ?? 0) + 1 }
          : await setKillSwitch(reason);
        setData({
          ...data,
          overview: { ...data.overview, issuance_paused: true, trust_status: "issuance_paused" },
          breakGlass: { paused: true, pause_generation: result.pause_generation, requests: [] },
        });
        setToast("New issuance paused; existing certificates remain active");
      } else if (action === "request") {
        const created = data.demo
          ? {
              id: `bgr_demo-${Date.now()}`,
              action: "resume_issuance" as const,
              status: "pending" as const,
              pause_generation: data.breakGlass?.pause_generation ?? 1,
              reason: reason.trim(),
              requested_by: data.user.id,
              requested_at: new Date().toISOString(),
              expires_at: new Date(Date.now() + 10 * 60_000).toISOString(),
            }
          : await requestIssuanceResume(reason);
        setData({
          ...data,
          breakGlass: {
            paused: true,
            pause_generation: created.pause_generation,
            requests: [created, ...(data.breakGlass?.requests ?? [])],
          },
        });
        setToast("Guarded resume requested; a different operator must approve");
      } else if (pending) {
        const approved = data.demo
          ? { ...pending, status: "approved" as const, approved_by: "user:independent-demo-admin", approved_at: new Date().toISOString() }
          : await approveIssuanceResume(pending.id);
        setData({
          ...data,
          overview: { ...data.overview, issuance_paused: false, trust_status: "healthy" },
          breakGlass: {
            paused: false,
            pause_generation: approved.pause_generation,
            requests: (data.breakGlass?.requests ?? []).map((item) => item.id === approved.id ? approved : item),
          },
        });
        setToast(data.demo ? "Second administrator approval simulated; issuance resumed" : "Independent approval recorded; issuance resumed");
      }
      setPauseModal(false);
      setReason("");
      setBreakGlassConfirmation("");
    } catch (actionError) {
      setToast(
        actionError instanceof Error ? actionError.message : "Action failed",
      );
    }
  }
  async function promote(id: string, healthURL: string) {
    const current = data;
    if (!current) return;
    const demo = current.demo;
    try {
      if (demo) {
        const now = new Date().toISOString();
        setData({
          ...current,
          certificateSets: current.certificateSets.map((set) => {
            if (set.id !== id || !set.standby) return set;
            const previousActive = set.active;
            return {
              ...set,
              active: {
                ...set.standby,
                slot: "active",
                state: "serving",
                promoted_at: now,
              },
              ...(previousActive
                ? {
                    standby: {
                      ...previousActive,
                      slot: "standby" as const,
                      state: "draining" as const,
                    },
                  }
                : {}),
            };
          }),
        });
      } else {
        await promoteCertificateSet(id, healthURL);
        await refresh();
      }
      setToast(demo ? "Standby promoted. Previous certificate is draining." : "Promotion command queued. The assigned Mesh Node will verify the exact certificate and report completion.");
    } catch (actionError) {
      setToast(
        actionError instanceof Error ? actionError.message : "Promotion failed",
      );
    }
  }
  async function role(
    subject: string,
    productRole: ProductRole,
    operation: "grant" | "revoke",
  ) {
    if (!data?.demo) await assignRole(subject, productRole, operation);
    setToast(
      operation === "grant"
        ? "Access assignment saved in OpenFGA"
        : "Access assignment removed from OpenFGA",
    );
  }
  async function rotateScim(): Promise<ScimConnectorCredential> {
    if (!data) throw new Error("Console data is unavailable");
    const result = data.demo
      ? {
          configured: true,
          status: "active" as const,
          base_url:
            data.scimConnector?.base_url ??
            "https://api.sharppki.com/scim/acme-corp/v2",
          token_hint: "...D3MO",
          token_version: (data.scimConnector?.token_version ?? 0) + 1,
          updated_at: new Date().toISOString(),
          bearer_token: "spki_scim_demo_token_displayed_once_123456789",
          token_displayed_once: true as const,
        }
      : await rotateScimConnector();
    const {
      bearer_token: _token,
      token_displayed_once: _once,
      ...status
    } = result;
    setData({ ...data, scimConnector: status });
    setToast("SCIM token rotated. Copy the one-time credential now.");
    return result;
  }
  async function revokeScim(): Promise<void> {
    if (!data) throw new Error("Console data is unavailable");
    const status = data.demo
      ? (() => {
          const { token_hint: _hint, ...rest } = data.scimConnector ?? {
            configured: true,
            base_url: "https://api.sharppki.com/scim/acme-corp/v2",
          };
          return {
            ...rest,
            status: "revoked" as const,
            updated_at: new Date().toISOString(),
          };
        })()
      : await revokeScimConnector();
    setData({ ...data, scimConnector: status });
    setToast(
      "SCIM connector revoked. Existing sessions now fail provisioning checks.",
    );
  }
  function resetCaForm() {
    setCaName("");
    setScmIdDraft(EMPTY_SCM_DRAFT);
    setScmIdErrors({});
  }

  function openCaManage(ca: CertificateAuthorityConnector) {
    setScmManageDraft({
      organization: ca.scm_organization_id ? String(ca.scm_organization_id) : "",
      certificateType: ca.scm_certificate_type_id ? String(ca.scm_certificate_type_id) : "",
    });
    setScmManageErrors({});
    setScmManageMessage("");
    setCaManageTarget(ca);
  }

  async function saveScmIds() {
    if (!data || !caManageTarget) return;
    const parsed = parseScmIdDraft(scmManageDraft);
    setScmManageErrors(parsed.errors);
    if (!parsed.ids) return;
    try {
      const { incomplete_fields: _computed, ...stored } = caManageTarget;
      const updated = data.demo
        ? { ...stored, ...parsed.ids, updated_at: new Date().toISOString() }
        : await updateCaScmIds(caManageTarget, parsed.ids);
      setData({ ...data, cas: data.cas.map((ca) => (ca.id === updated.id ? updated : ca)) });
      setCaManageTarget(updated);
      setScmManageMessage("SCM enrollment IDs saved. The signer picks them up at the next registry publication.");
    } catch (actionError) {
      setScmManageMessage(actionError instanceof Error ? actionError.message : "SCM enrollment IDs could not be saved");
    }
  }

  async function connectCa(provider: "sectigo_scm" | "byo_ca") {
    if (caName.trim().length < 3) {
      setCaError("Enter a descriptive connector name.");
      return;
    }
    let scmIds: { scm_organization_id: number; scm_certificate_type_id: number } | undefined;
    if (provider === "sectigo_scm") {
      const parsed = parseScmIdDraft(scmIdDraft);
      setScmIdErrors(parsed.errors);
      if (!parsed.ids) {
        setCaError("Enter the SCM organization and certificate type IDs before connecting Sectigo SCM.");
        return;
      }
      scmIds = parsed.ids;
    } else {
      setScmIdErrors({});
    }
    setCaError("");
    try {
      if (data?.demo) {
        const now = new Date().toISOString();
        const id = `ca-demo-${provider}-${Date.now()}`;
        setData({
          ...data,
          cas: [
            {
              id,
              tenant_id: data.overview.tenant_id,
              name: caName.trim(),
              provider,
              status: "active",
              certificate_chain_pem: "",
              signer_key_reference: `demo:${provider}:${id}`,
              ...(scmIds ?? {}),
              created_at: now,
              updated_at: now,
            },
            ...data.cas,
          ],
        });
        setCaModal(false);
        resetCaForm();
        setToast(
          `${provider === "sectigo_scm" ? "SCM connector" : "BYO CA connector"} added to the demo signing plane.`,
        );
        return;
      }
      const session = await createCaImportSession(provider, caName.trim(), scmIds);
      const next = session.authorize_url ?? session.upload_url;
      if (next) {
        window.location.assign(next);
        return;
      }
      setCaModal(false);
      resetCaForm();
      setToast("Secure CA connection session created.");
    } catch (actionError) {
      setCaError(
        actionError instanceof Error
          ? actionError.message
          : "Connection session failed",
      );
    }
  }

  async function submitCrlRefresh() {
    if (!data || !crlRefreshTarget || crlConfirmation !== "REFRESH CRL") return;
    try {
      const publication = data.demo
        ? {
            ...(data.crlPublications.find((item) => item.connector_id === crlRefreshTarget.id) ?? {
              connector_id: crlRefreshTarget.id, previous_crl_sha256: "genesis", required_revocations: 0,
              issuer_spki_sha256: "demo", provider_reference: "demo", signer_receipt: "demo", r2_key: "demo",
            }),
            generation: (data.crlPublications.find((item) => item.connector_id === crlRefreshTarget.id)?.generation ?? 0) + 1,
            crl_sha256: `demo-${crypto.randomUUID()}`, crl_number: String((data.crlPublications.find((item) => item.connector_id === crlRefreshTarget.id)?.generation ?? 0) + 1),
            this_update: new Date().toISOString(), next_update: new Date(Date.now() + 24 * 60 * 60_000).toISOString(),
            published_at: new Date().toISOString(), status: "healthy" as const,
          }
        : await refreshCrl(crlRefreshTarget.id);
      setData({
        ...data,
        crlPublications: [publication, ...data.crlPublications.filter((item) => item.connector_id !== publication.connector_id)],
      });
      setCrlRefreshTarget(undefined);
      setCrlConfirmation("");
      setToast(`CRL generation ${publication.generation} published and independently verified.`);
    } catch (actionError) {
      setToast(actionError instanceof Error ? actionError.message : "CRL refresh failed");
    }
  }

  async function submitOcspRefresh() {
    if (!data || !ocspRefreshTarget || ocspConfirmation !== "REFRESH OCSP") return;
    try {
      const current = data.ocspPublications.filter((item) => item.transaction_id === ocspRefreshTarget.transaction_id);
      const publications: OCSPPublicationStatus[] = data.demo
        ? (["sha1", "sha256"] as const).map((hashAlgorithm) => ({
            ...(current.find((item) => item.hash_algorithm === hashAlgorithm) ?? ocspRefreshTarget),
            hash_algorithm: hashAlgorithm,
            generation: Math.max(0, ...current.map((item) => item.generation)) + 1,
            previous_response_sha256: current.find((item) => item.hash_algorithm === hashAlgorithm)?.response_sha256 ?? "genesis",
            response_sha256: `demo-${hashAlgorithm}-${crypto.randomUUID()}`,
            request_key: `demo-${hashAlgorithm}-${ocspRefreshTarget.serial_number}`,
            produced_at: new Date().toISOString(),
            this_update: new Date().toISOString(),
            next_update: new Date(Date.now() + 24 * 60 * 60_000).toISOString(),
            published_at: new Date().toISOString(),
            health: "healthy" as const,
          }))
        : (await refreshOcsp(ocspRefreshTarget.transaction_id)).map((item) => ({ ...item, health: "healthy" as const }));
      const queued = publications.length === 0;
      const replaced = new Set(publications.map((item) => `${item.transaction_id}:${item.hash_algorithm}`));
      setData({
        ...data,
        ocspPublications: [
          ...publications,
          ...data.ocspPublications.filter((item) => !replaced.has(`${item.transaction_id}:${item.hash_algorithm}`)),
        ],
      });
      setOcspRefreshTarget(undefined);
      setOcspConfirmation("");
      setToast(queued
        ? `SHA-1 and SHA-256 Online Responder work queued for serial ${ocspRefreshTarget.serial_number}. The assigned Windows node will publish after independent verification.`
        : `SHA-1 and SHA-256 OCSP responses published for serial ${ocspRefreshTarget.serial_number}.`);
    } catch (actionError) {
      setToast(actionError instanceof Error ? actionError.message : "OCSP refresh failed");
    }
  }

  async function loadMoreOcsp() {
    if (!data?.ocspNextCursor || ocspLoadingMore) return;
    setOcspLoadingMore(true);
    try {
      const page = await loadOCSPPage(data.ocspNextCursor);
      const known = new Set(data.ocspPublications.map((item) => `${item.transaction_id}:${item.hash_algorithm}`));
      const { ocspNextCursor: _previousCursor, ...dataWithoutCursor } = data;
      setData({
        ...dataWithoutCursor,
        ocspPublications: [...data.ocspPublications, ...page.items.filter((item) => !known.has(`${item.transaction_id}:${item.hash_algorithm}`))],
        ...(page.next_cursor ? { ocspNextCursor: page.next_cursor } : {}),
      });
    } catch (actionError) {
      setToast(actionError instanceof Error ? actionError.message : "Could not load more OCSP responses");
    } finally {
      setOcspLoadingMore(false);
    }
  }
  function simulateNodeEnrollment() {
    if (!data?.demo) return;
    const id = `node-demo-${data.nodes.length + 1}`;
    const now = new Date().toISOString();
    setData({
      ...data,
      nodes: [
        ...data.nodes,
        {
          id,
          name: enrollmentName,
          platform: enrollmentPlatform === "kubernetes" ? "linux" : enrollmentPlatform,
          version: "0.1.9",
          status: "healthy",
          capabilities: ["acme", "dual-slot", "cloud-fallback"],
          public_key_sha256: "demo…enrolled",
          last_seen_at: now,
        },
      ],
      overview: {
        ...data.overview,
        counts: { ...data.overview.counts, nodes: data.nodes.length + 1 },
        approval_quorum: {
          ...data.overview.approval_quorum,
          eligible_nodes: data.overview.approval_quorum.eligible_nodes + 1,
          healthy_nodes: data.overview.approval_quorum.healthy_nodes + 1,
          available: true,
        },
      },
    });
    setEnrollModal(false);
    setEnrollmentPackage(undefined);
    setToast(
      "Demo Mesh Node enrolled, attested, and added as a healthy replica.",
    );
  }
  async function generateEnrollmentPackage() {
    if (!data) return;
    setEnrollmentError("");
    setEnrollmentLoading(true);
    try {
      if (data.demo) {
        const expiresAt = new Date(Date.now() + 30 * 60_000).toISOString();
        setEnrollmentPackage({
          id: `enroll-demo-${crypto.randomUUID()}`,
          enrollment_token: `spki_enroll_demo_${crypto.randomUUID().replaceAll("-", "")}`,
          token_displayed_once: true,
          tenant_id: data.overview.tenant_id,
          node_name: enrollmentName,
          platform: enrollmentPlatform,
          expires_at: expiresAt,
          config: {
            tenant_id: data.overview.tenant_id,
            node_name: enrollmentName,
            edge_url: "https://sharppki.com",
            audit_checkpoint_key_id: "audit-production-2026-01",
            audit_checkpoint_public_key_base64: "customer-pinned-ed25519-public-key",
            policy_trust_bundle_file: enrollmentPlatform === "windows" ? "C:\\ProgramData\\Sectigo\\Edge\\policy-trust.json" : "/etc/edgepki/policy-trust.json",
            registration_trust_bundle_file: enrollmentPlatform === "windows" ? "C:\\ProgramData\\Sectigo\\Edge\\registration-trust.json" : "/etc/edgepki/registration-trust.json",
          },
          policy_trust: { protocol: "sectigo-edge.policy-trust.v1", tenant_id: data.overview.tenant_id, keys: [{ key_id: "policy-production-2026-01", algorithm: "Ed25519", public_key_spki_base64: "customer-pinned-spki" }] },
          registration_trust: { protocol: "sectigo-edge.registration-trust.v1", tenant_id: data.overview.tenant_id, keys: [{ key_id: "registration-production-2026-01", algorithm: "Ed25519", public_key_spki_base64: "customer-pinned-spki" }] },
          bootstrap_package: {
            protocol: "sectigo-edge.node-bootstrap-envelope.v1",
            key_id: "demo-bootstrap-not-installable",
            algorithm: "Ed25519",
            payload_base64: "DEMO_PACKAGE_NOT_INSTALLABLE",
            payload_sha256: "A".repeat(43),
            signature: "A".repeat(86),
          },
        });
      } else {
        setEnrollmentPackage(await createNodeEnrollmentPackage(enrollmentName, enrollmentPlatform));
      }
    } catch (actionError) {
      setEnrollmentError(actionError instanceof Error ? actionError.message : "Enrollment package could not be created");
    } finally {
      setEnrollmentLoading(false);
    }
  }
  function simulateCertificateRequest() {
    if (!data?.demo) return;
    const template = data.certificateSets[0];
    if (!template) return;
    const now = new Date();
    const id = `certset_demo_${Date.now()}`;
    const { standby: _templateStandby, ...templateWithoutStandby } = template;
    const certificateSet: CertificateSet = {
      ...templateWithoutStandby,
      id,
      endpoint_id: "spiffe://acme/prod/catalog-demo",
      profile_id: "prod-service",
      generation: 1,
      active: {
        slot: "active",
        certificate_id: `cert_demo_${Date.now()}`,
        serial_number: Math.floor(Math.random() * 0xffffffff)
          .toString(16)
          .padStart(8, "0"),
        not_before: now.toISOString(),
        not_after: new Date(now.getTime() + 24 * 60 * 60_000).toISOString(),
        state: "serving",
        health: "healthy",
        deployed_at: now.toISOString(),
      },
    };
    setData({
      ...data,
      certificateSets: [certificateSet, ...data.certificateSets],
      overview: {
        ...data.overview,
        counts: {
          ...data.overview.counts,
          certificates: (data.overview.counts.certificates ?? 0) + 1,
          issued: (data.overview.counts.issued ?? 0) + 1,
        },
      },
    });
    setRequestModal(false);
    setToast(
      "Demo request approved by both trust domains and deployed healthy.",
    );
  }
  function configureIntegration(record: IntegrationRecord) {
    const defaultCaConnector =
      record.ca_connector_id ??
      (record.kind === "microsoft_adcs"
        ? data?.cas.find(
            (ca) => ca.provider === "byo_ca" && ca.status === "active",
          )?.id
        : undefined);
    setIntegrationTarget(record);
    setIntegrationError("");
    setIntegrationDraft({
      enabled: record.enabled,
      node_ids: [...record.node_ids],
      profile_id: record.profile_id,
      ...(record.adapter_reference
        ? { adapter_reference: record.adapter_reference }
        : record.kind === "kubernetes_csr"
          ? { adapter_reference: "local/kubernetes-incluster" }
          : record.kind === "microsoft_adcs"
            ? { adapter_reference: "local/adcs-production" }
            : record.kind === "est"
              ? { adapter_reference: "local/est-builtin" }
              : record.kind === "nginx"
                ? { adapter_reference: "local/nginx-production" }
              : record.kind === "apache"
                ? { adapter_reference: "local/apache-production" }
              : record.kind === "java_keystore"
                ? { adapter_reference: "local/java-production" }
              : record.kind === "hashicorp_vault"
                ? { adapter_reference: "local/vault-production" }
              : {}),
      ...(defaultCaConnector
        ? { ca_connector_id: defaultCaConnector }
        : {}),
    });
  }
  async function saveIntegrationConfiguration() {
    if (!data || !integrationTarget) return;
    setIntegrationError("");
    try {
      const current = integrationTarget;
      const updated = data.demo
        ? {
            ...current,
            ...integrationDraft,
            status: integrationDraft.enabled
              ? current.kind === "sectigo_scm"
                ? ("healthy" as const)
                : ("pending_node" as const)
              : ("disabled" as const),
            revision: current.revision + 1,
            applied_revision:
              integrationDraft.enabled && current.kind !== "sectigo_scm"
                ? current.applied_revision
                : current.revision + 1,
            requested_action: "reconcile" as const,
            node_statuses: {},
            updated_at: new Date().toISOString(),
            updated_by: data.user.id,
          }
        : await saveIntegration(current.kind, integrationDraft);
      setData({
        ...data,
        integrations: data.integrations.map((record) =>
          record.kind === current.kind ? updated : record,
        ),
      });
      setIntegrationTarget(undefined);
      setToast(
        updated.enabled
          ? "Configuration saved. The assigned Mesh Node will reconcile it over its outbound channel."
          : "Integration disabled and desired state updated.",
      );
      if (data.demo && updated.enabled && updated.kind !== "sectigo_scm") {
        window.setTimeout(() => {
          setData((currentData) =>
            currentData
              ? {
                  ...currentData,
                  integrations: currentData.integrations.map((record) =>
                    record.kind === updated.kind &&
                    record.revision === updated.revision &&
                    record.enabled
                      ? {
                          ...record,
                          status: "healthy",
                          applied_revision: updated.revision,
                          node_statuses: Object.fromEntries(
                            record.node_ids.map((nodeId) => [
                              nodeId,
                              {
                                revision: updated.revision,
                                health: "healthy" as const,
                                checked_at: new Date().toISOString(),
                              },
                            ]),
                          ),
                          last_checked_at: new Date().toISOString(),
                        }
                      : record,
                  ),
                }
              : currentData,
          );
          setToast(
            `${updated.display_name} reconciled successfully on every assigned demo node.`,
          );
        }, 900);
      }
    } catch (actionError) {
      setIntegrationError(
        actionError instanceof Error
          ? actionError.message
          : "Integration configuration failed",
      );
    }
  }
  async function runIntegrationTest(record: IntegrationRecord) {
    if (!data) return;
    try {
      if (data.demo) {
        const revision = record.revision + 1;
        setData({
          ...data,
          integrations: data.integrations.map((item) =>
            item.kind === record.kind
              ? {
                  ...item,
                  status: "pending_node",
                  requested_action: "test",
                  revision,
                  updated_at: new Date().toISOString(),
                }
              : item,
          ),
        });
        setToast("Test request queued to the assigned Mesh Node…");
        window.setTimeout(() => {
          setData((current) =>
            current
              ? {
                  ...current,
                  integrations: current.integrations.map((item) =>
                    item.kind === record.kind
                      ? {
                          ...item,
                          status: "healthy",
                          applied_revision: revision,
                          node_statuses: Object.fromEntries(
                            item.node_ids.map((nodeId) => [
                              nodeId,
                              {
                                revision,
                                health: "healthy" as const,
                                checked_at: new Date().toISOString(),
                              },
                            ]),
                          ),
                          last_checked_at: new Date().toISOString(),
                        }
                      : item,
                  ),
                }
              : current,
          );
          setToast(
            record.kind === "microsoft_adcs"
              ? "ADCS test passed: Windows service identity reached the CA, resolved the pinned template, and matched the uploaded public chain. No certificate was issued."
              : record.kind === "nginx"
                ? "NGINX test passed: executable pin, expanded managed include, active generation, and configuration validation are healthy. No reload was performed."
              : record.kind === "apache"
                ? "Apache test passed: executable pin, managed include expansion, active generation, and configuration validation are healthy. No graceful reload was performed."
              : record.kind === "java_keystore"
                ? "Java keystore test passed: keytool and reload pins, password-file isolation, configured alias, active PKCS#12 digest, and immutable generation are healthy. No reload was performed."
              : record.kind === "hashicorp_vault"
                ? "Vault test passed: local token file, KV v2 mount, CAS-guarded pointer, and active generation digest are healthy. No secret was written."
              : "Integration test passed. Node reachability and adapter handshake verified.",
          );
        }, 900);
      } else {
        const updated = await testIntegration(record.kind);
        setData({
          ...data,
          integrations: data.integrations.map((item) =>
            item.kind === record.kind ? updated : item,
          ),
        });
        setToast(integrationTestMessage(updated));
      }
    } catch (actionError) {
      setToast(
        actionError instanceof Error
          ? actionError.message
          : "Integration test failed",
      );
    }
  }
  async function exportEvidence(transactionId: string) {
    try {
      if (data?.demo) {
        const matchingEvents = data.audit.filter(
          (event) => event.transaction_id === transactionId,
        );
        const bundle = {
          schema: "com.sectigo.edge.portable-evidence.v1",
          demo: true,
          warning:
            "SIMULATED EVIDENCE — not a production signature or compliance artifact",
          exported_at: new Date().toISOString(),
          tenant_id: data.overview.tenant_id,
          transaction_id: transactionId,
          policy: {
            id: data.policy.id,
            version: data.policy.version,
            status: data.policy.status,
          },
          approval_quorum: data.overview.approval_quorum,
          audit_head: data.overview.audit_head,
          audit_events: matchingEvents,
          verification: {
            result: "simulated_valid",
            checks: [
              "tenant and transaction binding",
              "customer approval quorum",
              "active policy digest",
              "certificate and CSR binding",
              "append-only audit hash linkage",
            ],
          },
        };
        const blob = new Blob([JSON.stringify(bundle, null, 2)], {
          type: "application/json",
        });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `sectigo-edge-demo-evidence-${transactionId}.json`;
        link.click();
        window.setTimeout(() => URL.revokeObjectURL(url), 0);
        setToast("Simulated portable evidence bundle downloaded.");
        return;
      }
      await exportTransactionEvidence(transactionId);
      setToast("Portable verification bundle downloaded.");
    } catch (actionError) {
      setToast(
        actionError instanceof Error
          ? actionError.message
          : "Evidence export failed",
      );
    }
  }
  async function revokeActive() {
    if (!data || !revokeTarget?.active) return;
    const certificateId = revokeTarget.active.certificate_id;
    let revocationPending = false;
    try {
      if (data.demo) {
        setData({
          ...data,
          certificateSets: data.certificateSets.map((set) => {
            if (set.id !== revokeTarget.id || !set.active) return set;
            if (safeFailover && set.standby?.health === "healthy")
              return {
                ...set,
                active: { ...set.standby, slot: "active", state: "serving" },
                standby: {
                  ...set.active,
                  slot: "standby",
                  state: "revoked",
                  health: "unhealthy",
                },
              };
            return {
              ...set,
              active: { ...set.active, state: "revoked", health: "unhealthy" },
            };
          }),
        });
      } else {
        const result = await revokeCertificate(
          certificateId,
          revocationReason,
          revocationComment.trim(),
          safeFailover,
        );
        revocationPending = result.status === "revocation_pending";
        await refresh();
      }
      setRevokeTarget(undefined);
      setRevocationComment("");
      setToast(
        revocationPending
          ? "Revocation is durably queued to the assigned Windows node. ADCS completion will update inventory and failover automatically."
          : safeFailover && revokeTarget.standby?.health === "healthy"
          ? "Standby promoted and compromised certificate revoked without downtime."
          : "Certificate revoked. Existing trust should be removed immediately.",
      );
    } catch (actionError) {
      setToast(
        actionError instanceof Error
          ? actionError.message
          : "Revocation failed",
      );
    }
  }
  async function approveRegistration() {
    if (!data || !registrationTarget) return;
    try {
      if (data.demo) {
        const now = new Date().toISOString();
        setData({
          ...data,
          registrations: data.registrations.map((registration) =>
            registration.claim_id === registrationTarget.claim_id
              ? {
                  ...registration,
                  status: "approved",
                  approval: {
                    spiffe_id: registration.announcement.requested_spiffe_id,
                    owner: registrationApproval.owner,
                    profile_id: registrationApproval.profile_id,
                    channels: registrationApproval.channels,
                    selectors:
                      registration.announcement.measurements[0]?.kind ===
                      "kubernetes-service-account"
                        ? [
                            {
                              type: "k8s",
                              value: `ns:${registration.announcement.measurements[0].value.split("/")[0]}`,
                            },
                            {
                              type: "k8s",
                              value: `sa:${registration.announcement.measurements[0].value.split("/")[1]}`,
                            },
                          ]
                        : [],
                    approved_by: data.user.id,
                    approved_at: now,
                  },
                  updated_at: now,
                }
              : registration,
          ),
        });
      } else {
        await approveServiceRegistration(
          registrationTarget.node_id,
          registrationTarget.claim_id,
          registrationApproval,
        );
        await refresh();
      }
      setRegistrationTarget(undefined);
      setToast(
        "Signed registration grant approved. The node will verify and reconcile it into SPIRE.",
      );
    } catch (actionError) {
      setToast(
        actionError instanceof Error
          ? actionError.message
          : "Service claim failed",
      );
    }
  }

  return (
    <div className={cx("shell", data?.demo && "shell--demo")}>
      <aside className={cx("sidebar", mobileOpen && "sidebar--open")}>
        <div className="brand">
          <img src="/sectigo-logo.svg" alt="Sectigo" />
          <span>EDGE</span>
        </div>
        <div className="tenant">
          <span className="tenant__mark">{workspaceIdentity(Boolean(data?.demo), data?.overview.tenant_id, data?.user.tenant_name).mark}</span>
          <div>
            <small>WORKSPACE</small>
            <strong>{workspaceIdentity(Boolean(data?.demo), data?.overview.tenant_id, data?.user.tenant_name).name}</strong>
          </div>
          <ChevronDown size={15} />
        </div>
        <nav>
          {sectionedNav.map((item, index) => (
            <div key={item.id}>
              {item.section && (
                <span className="nav-section">{item.section}</span>
              )}
              <button
                className={cx(view === item.id && "active")}
                onClick={() => {
                  setView(item.id);
                  setMobileOpen(false);
                }}
              >
                <item.icon size={17} />
                <span>{item.label}</span>
                {item.id === "incidents" && data && (data.demo || hasActiveIncident(data.overview, data.breakGlass)) && <i aria-label="Active incident" />}
              </button>
              {index === 5 && <div className="nav-rule" />}
            </div>
          ))}
        </nav>
        <div className="sidebar__bottom">
          <div className="edge-state">
            {edgeStatus(data?.nodes ?? []).live ? <span className="pulse" /> : <span className="pulse pulse--idle" />}
            <div>
              <strong>{edgeStatus(data?.nodes ?? []).label}</strong>
              <small>{edgeStatus(data?.nodes ?? []).detail}</small>
            </div>
          </div>
          <button className="profile" onClick={() => setProfileModal(true)}>
            <span>{profile.initials}</span>
            <div>
              <strong>{profile.name}</strong>
              {profile.role && <small>{profile.role}</small>}
            </div>
            <Settings2 size={15} />
          </button>
        </div>
      </aside>
      {mobileOpen && (
        <button
          className="mobile-scrim"
          aria-label="Close navigation"
          onClick={() => setMobileOpen(false)}
        />
      )}
      <main>
        <header className="topbar">
          <button
            className="mobile-menu"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={21} />
          </button>
          <div>
            <span>Sectigo Edge</span>
            <i>/</i>
            <strong>{page.heading}</strong>
          </div>
          <div className="topbar__right">
            {data?.demo && <span className="demo-pill">DEMO DATA</span>}
            {data && (data.demo ? (
              <Status
                value={data.overview.issuance_paused ? "paused" : "healthy"}
                label={
                  data.overview.issuance_paused
                    ? "issuance paused"
                    : "all systems healthy"
                }
              />
            ) : (
              <Status {...topbarStatus(data.overview, data.nodes)} />
            ))}
            <button
              className="icon-button"
              onClick={() => void refresh()}
              aria-label="Refresh"
            >
              <RefreshCw size={16} />
            </button>
          </div>
        </header>
        <div className="content">
          <div className="page-head">
            <div>
              <span>{page.eyebrow}</span>
              <h1>{page.heading}</h1>
              <p>{page.description}</p>
            </div>
            {view === "health" && (
              <button
                className={cx(
                  "button",
                  data?.overview.issuance_paused
                    ? "button--primary"
                    : "button--danger-quiet",
                )}
                onClick={() => setPauseModal(true)}
              >
                <PauseCircle size={16} />
                {data?.overview.issuance_paused
                  ? "Resume issuance"
                  : "Emergency controls"}
              </button>
            )}
            {view === "nodes" && (
              <button
                className="button button--primary"
                onClick={() => setView("downloads")}
              >
                <Download size={16} /> Install a Mesh Node
              </button>
            )}
          </div>
          {loading && !data && (
            <div className="loading">
              <RefreshCw className="spin" size={24} />
              <span>Verifying both trust domains…</span>
            </div>
          )}
          {error && (
            <div className="error-state">
              <AlertTriangle size={24} />
              <strong>Console unavailable</strong>
              <p>{error}</p>
              <button
                className="button button--primary"
                onClick={() => void refresh()}
              >
                Try again
              </button>
            </div>
          )}
          {data && (
            <>
              {view === "guided" && <GuidedDemo />}
              {view === "health" && (
                <HealthView data={data} audit={auditVerification} onNavigate={setView} />
              )}
              {view === "certificates" && (
                <CertificatesView
                  data={data}
                  onRevoke={setRevokeTarget}
                  onRequest={() => setRequestModal(true)}
                />
              )}
              {view === "nodes" && (
                <NodesView
                  nodes={data.nodes}
                  quorum={data.overview.approval_quorum}
                  onEnroll={() => setEnrollModal(true)}
                />
              )}
              {view === "downloads" && (
                <DownloadsView onEnroll={() => setEnrollModal(true)} />
              )}
              {view === "registrations" && (
                <RegistrationsView
                  registrations={data.registrations}
                  onApprove={(registration) => {
                    setRegistrationTarget(registration);
                    setRegistrationApproval({
                      human_code: "",
                      owner: registration.announcement.owner_hint ?? "",
                      profile_id: "agent-mtls",
                      channels: [...registration.announcement.protocols],
                      x509_svid_ttl: 3600,
                      jwt_svid_ttl: 300,
                    });
                  }}
                />
              )}
              {view === "crypto" && <CryptoView data={data} />}
              {view === "dependencies" && <DependencyGraphView data={data} />}
              {view === "migrations" && (
                <MigrationView
                  data={data}
                  onNavigatePolicies={() => setView("policies")}
                  onPlanChange={(plan) => setData((current) => current ? {
                    ...current,
                    migrations: current.migrations.some((item) => item.definition.id === plan.definition.id)
                      ? current.migrations.map((item) => item.definition.id === plan.definition.id ? plan : item)
                      : [plan, ...current.migrations],
                  } : current)}
                />
              )}
              {view === "rotation" && (
                <RotationView
                  sets={data.certificateSets}
                  transactions={data.transactions}
                  onPromote={(id, healthURL) => void promote(id, healthURL)}
                />
              )}
              {view === "cas" && (
                <CasView
                  cas={data.cas}
                  crls={data.crlPublications}
                  onConnect={() => setCaModal(true)}
                  onManage={openCaManage}
                  onRefreshCrl={(ca) => {
                    setCrlConfirmation("");
                    setCrlRefreshTarget(ca);
                  }}
                />
              )}
              {view === "revocation" && (
                <RevocationView
                  cas={data.cas}
                  crls={data.crlPublications}
                  ocsp={data.ocspPublications}
                  hasMore={Boolean(data.ocspNextCursor)}
                  loadingMore={ocspLoadingMore}
                  onRefreshCrl={(ca) => {
                    setCrlConfirmation("");
                    setCrlRefreshTarget(ca);
                  }}
                  onRefreshOcsp={(publication) => {
                    setOcspConfirmation("");
                    setOcspRefreshTarget(publication);
                  }}
                  onLoadMore={() => void loadMoreOcsp()}
                />
              )}
              {view === "policies" && <PolicyManagementView policy={data.policy} platformPolicy={data.platformPolicy} platformPolicySHA256={data.platformPolicySHA256} nodes={data.nodes} capabilities={data.capabilities} dependencies={data.dependencies} certificateSets={data.certificateSets} transactions={data.transactions} user={data.user} demo={data.demo} onActivated={(activePolicy) => setData({ ...data, policy: activePolicy, overview: { ...data.overview, policy_version: activePolicy.version } })} />}
              {view === "access" && (
                <AccessView
                  user={data.user}
                  connector={data.scimConnector}
                  demo={data.demo}
                  onAssign={role}
                  onRotateScim={rotateScim}
                  onRevokeScim={revokeScim}
                  onRefreshStatus={async () => {
                    const fresh = await loadConsoleData();
                    setData(fresh);
                    return fresh.scimConnector;
                  }}
                />
              )}
              {view === "audit" && (
                <AuditView
                  events={data.audit}
                  witness={data.overview.audit_witness}
                  verification={auditVerification}
                  demo={data.demo}
                  onExport={(id) => void exportEvidence(id)}
                />
              )}
              {view === "events" && <TrustEventsView events={data.audit} demo={data.demo} />}
              {view === "integrations" && (
                <IntegrationsView
                  integrations={data.integrations}
                  onConfigure={configureIntegration}
                  onTest={(record) => void runIntegrationTest(record)}
                />
              )}
              {view === "incidents" && (
                <IncidentsView
                  paused={data.overview.issuance_paused}
                  quorum={data.overview.approval_quorum}
                  requests={data.breakGlass?.requests ?? []}
                  transactions={data.transactions}
                  currentUserId={data.user.id}
                  demo={data.demo}
                  audit={auditVerification}
                  onOpenControl={() => {
                    setReason("");
                    setBreakGlassConfirmation("");
                    setPauseModal(true);
                  }}
                />
              )}
            </>
          )}
        </div>
      </main>
      {pauseModal && (
        <Modal
          title={
            !data?.overview.issuance_paused
              ? "Activate issuance kill switch"
              : data.breakGlass?.requests.some((request) => request.status === "pending")
                ? "Independently approve issuance resume"
                : "Request guarded issuance resume"
          }
          onClose={() => setPauseModal(false)}
        >
          <div className="modal-body">
            <div className="warning-callout">
              <AlertTriangle size={21} />
              <p>
                {data?.overview.issuance_paused
                  ? data.breakGlass?.requests.some((request) => request.status === "pending")
                    ? "You are the second human control. Self-approval, stale MFA, expired requests, and earlier pause generations are rejected."
                    : "Issuance remains paused until a different fresh-MFA operator independently approves this ten-minute request."
                  : "New issuance and renewals will fail closed. Existing certificates continue operating."}
              </p>
            </div>
            {!data?.breakGlass?.requests.some((request) => request.status === "pending") && <label className="field">
              <span>Reason for audited change</span>
              <textarea
                value={reason}
                onChange={(event) => setReason(event.target.value)}
                placeholder="Describe the incident or approval…"
                autoFocus
              />
              <small>16–1,000 characters. This reason becomes immutable audit evidence.</small>
            </label>}
            {data?.breakGlass?.requests.find((request) => request.status === "pending") && (
              <div className="break-glass-approval-summary">
                <span>REQUESTED BY</span>
                <code>{data.breakGlass.requests.find((request) => request.status === "pending")?.requested_by}</code>
                <p>{data.breakGlass.requests.find((request) => request.status === "pending")?.reason}</p>
              </div>
            )}
            <label className="field">
              <span>Type exact confirmation</span>
              <input
                value={breakGlassConfirmation}
                onChange={(event) => setBreakGlassConfirmation(event.target.value)}
                placeholder={
                  !data?.overview.issuance_paused
                    ? "PAUSE ISSUANCE"
                    : data.breakGlass?.requests.some((request) => request.status === "pending")
                      ? "APPROVE ISSUANCE RESUME"
                      : "REQUEST ISSUANCE RESUME"
                }
                autoComplete="off"
              />
            </label>
            <div className="secure-note">
              <LockKeyhole size={17} />
              <span>Fresh enterprise MFA and Break-glass Operator authorization are checked again when submitted.</span>
            </div>
            <footer>
              <button
                className="button button--quiet"
                onClick={() => setPauseModal(false)}
              >
                Cancel
              </button>
              <button
                disabled={
                  breakGlassConfirmation !== (
                    !data?.overview.issuance_paused
                      ? "PAUSE ISSUANCE"
                      : data.breakGlass?.requests.some((request) => request.status === "pending")
                        ? "APPROVE ISSUANCE RESUME"
                        : "REQUEST ISSUANCE RESUME"
                  ) || (!data?.breakGlass?.requests.some((request) => request.status === "pending") && reason.trim().length < 16)
                }
                className={cx(
                  "button",
                  !data?.overview.issuance_paused ? "button--danger" : "button--primary",
                )}
                onClick={() => void submitBreakGlassControl()}
              >
                {!data?.overview.issuance_paused
                  ? "Pause new issuance"
                  : data.breakGlass?.requests.some((request) => request.status === "pending")
                    ? data.demo ? "Simulate second approval" : "Approve and resume issuance"
                    : "Request independent approval"}
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {enrollModal && data && (
        <Modal title="Create a Mesh Node enrollment" size="wide" onClose={() => { setEnrollModal(false); setEnrollmentPackage(undefined); setEnrollmentError(""); }}>
          <div className="modal-body">
            <div className="secure-note">
              <LockKeyhole size={17} />
              <span>
                This creates one tenant-scoped token, pinned trust files, and a
                node configuration. The token is delivered once inside the
                signed package, expires in 30 minutes, and is consumed by the
                node's first successful start.
              </span>
            </div>
            {!enrollmentPackage ? <>
              <section className="modal-section enrollment-form">
                <header><strong>Enrollment scope</strong><span>The exact name and runtime are cryptographically bound to this one-time token.</span></header>
                <label className="modal-field">
                  <span>Node name</span>
                  <input value={enrollmentName} onChange={(event) => setEnrollmentName(event.target.value)} placeholder="production-edge-01" />
                </label>
                <label className="modal-field">
                  <span>Runtime</span>
                  <select value={enrollmentPlatform} onChange={(event) => setEnrollmentPlatform(event.target.value as NodeEnrollmentPackage["platform"])}>
                    <option value="linux">Linux service</option>
                    <option value="windows">Windows service</option>
                    <option value="kubernetes">Kubernetes / Helm</option>
                  </select>
                </label>
              </section>
              {enrollmentError && <div className="integration-error"><AlertTriangle size={15} />{enrollmentError}</div>}
            </> : <>
              <section className="modal-section enrollment-secret">
                <header><strong>Signed one-time bootstrap</strong><span>The secret remains inside the signed package and never enters a command argument or configuration file.</span></header>
                <div className="enrollment-package-summary">
                  <span><Fingerprint size={16} /> {enrollmentPackage.bootstrap_package.key_id}</span>
                  <span><Clock3 size={16} /> Expires {new Date(enrollmentPackage.expires_at).toLocaleString()}</span>
                  <span><ShieldCheck size={16} /> Exact {enrollmentPackage.platform} runtime binding</span>
                </div>
              </section>
              <section className="modal-section">
                <header><strong>Download one bootstrap package</strong><span>The verified installer authenticates this envelope against bootstrap trust embedded in the signed release before parsing it.</span></header>
                <div className="enrollment-downloads">
                  <button className="button button--primary" onClick={() => downloadJson(bootstrapFilename(enrollmentPackage), enrollmentPackage.bootstrap_package)}><Download size={15} /> Download signed package</button>
                  <button className="button button--quiet" onClick={() => downloadJson("policy-trust.json", enrollmentPackage.policy_trust)}><Download size={15} /> policy trust</button>
                  <button className="button button--quiet" onClick={() => downloadJson("registration-trust.json", enrollmentPackage.registration_trust)}><Download size={15} /> registration trust</button>
                </div>
              </section>
              <div className="warning-callout">
                <AlertTriangle size={20} />
                <p>Create a private <code>node-material</code> directory containing <code>local-api.crt</code>, <code>local-api.key</code>, <code>workload-ca.pem</code>, <code>edge-client.crt</code>, and <code>edge-client.key</code>. These customer-held identities never pass through the cloud console.</p>
              </div>
              {enrollmentPackage.platform === "linux" && <InstallCommand label="Install after downloading the release" command={`sudo sh ./install-node-linux.sh --bootstrap-package ./${bootstrapFilename(enrollmentPackage)} --material-directory ./node-material . hassard0/sharppki`} />}
              {enrollmentPackage.platform === "windows" && <InstallCommand label="Install as Administrator" command={`.\\install-node-windows.ps1 -Binary .\\edgepki-node-windows-amd64.exe -BootstrapPackage .\\${bootstrapFilename(enrollmentPackage)} -MaterialDirectory .\\node-material -ReleaseDirectory .`} />}
              {data.demo && <p className="modal-help">Demo packages are deliberately non-installable. In a real authenticated workspace this envelope is signed by the production bootstrap key and accepted only by the matching verified installer.</p>}
              {!data.demo && <p className="modal-help">The installer stages and validates all trust and TLS material before changing the machine, starts the service, waits for enrollment, and confirms that the consumed token was durably deleted.</p>}
            </>}
            <footer>
              <button
                className="button button--quiet"
                onClick={() => { setEnrollModal(false); setEnrollmentPackage(undefined); setEnrollmentError(""); }}
              >
                Close
              </button>
              {!enrollmentPackage ? (
                <button
                  className="button button--primary"
                  disabled={enrollmentLoading || enrollmentName.trim().length < 2}
                  onClick={() => void generateEnrollmentPackage()}
                >
                  <KeyRound size={15} /> {enrollmentLoading ? "Creating..." : data.demo ? "Generate demo package" : "Create one-time package"}
                </button>
              ) : data.demo ? <button className="button button--primary" onClick={simulateNodeEnrollment}><Zap size={15} /> Simulate first start</button> : <button className="button button--primary" onClick={() => { setEnrollModal(false); setView("downloads"); }}><Download size={15} /> Open component downloads</button>}
            </footer>
          </div>
        </Modal>
      )}
      {requestModal && data && (() => {
        const cliCommand = data.demo
          ? "sectigo-edge cert issue --profile prod-service --spiffe-id spiffe://acme/prod/catalog"
          : cliIssueExample(data.policy);
        return (
        <Modal
          title="Request a managed identity"
          onClose={() => setRequestModal(false)}
        >
          <div className="modal-body">
            <div className="provider-choices">
              <button
                onClick={() => {
                  setRequestModal(false);
                  setView("integrations");
                }}
              >
                <span className="provider-logo provider-logo--byo_ca">
                  <Network size={21} />
                </span>
                <div>
                  <strong>Local Mesh Node</strong>
                  <p>Use ACME, the CLI, SDK, or SPIFFE Workload API.</p>
                </div>
                <ArrowRight size={17} />
              </button>
              <button
                onClick={() => {
                  setRequestModal(false);
                  setView("registrations");
                }}
              >
                <span className="provider-logo provider-logo--sectigo_scm">
                  <BadgeCheck size={21} />
                </span>
                <div>
                  <strong>Claim a discovered service</strong>
                  <p>Authorize a measured workload, agent, or MCP server.</p>
                </div>
                <ArrowRight size={17} />
              </button>
            </div>
            <div className="command-block">
              <span>LOCAL CLI EXAMPLE</span>
              <code>{cliCommand}</code>
              <button
                aria-label="Copy certificate request command"
                onClick={() => void navigator.clipboard.writeText(cliCommand)}
              >
                <Copy size={14} />
              </button>
            </div>
            <footer>
              <button
                className="button button--quiet"
                onClick={() => setRequestModal(false)}
              >
                Close
              </button>
              {data.demo && (
                <button
                  className="button button--primary"
                  onClick={simulateCertificateRequest}
                >
                  <FileKey2 size={15} /> Run demo request
                </button>
              )}
            </footer>
          </div>
        </Modal>
        );
      })()}
      {profileModal && data && (
        <Modal title="Account & session" onClose={() => setProfileModal(false)}>
          <div className="modal-body">
            <div className="identity-card">
              <div className="avatar">{data.demo ? data.user.email.slice(0, 2) : profile.initials}</div>
              <div>
                <strong>{data.user.email}</strong>
                <span>
                  {data.user.groups.join(", ") || "Direct assignment"}
                </span>
              </div>
              <Status
                value={data.user.mfa ? "healthy" : "warning"}
                label={data.user.mfa ? "MFA verified" : "MFA required"}
              />
            </div>
            <dl className="detail-list">
              <div>
                <dt>Workspace</dt>
                <dd>{data.user.tenant_id}</dd>
              </div>
              <div>
                <dt>Authentication</dt>
                <dd>{data.user.authentication_method === "password" ? "Sectigo Edge account" : "Enterprise SSO"}</dd>
              </div>
              <div>
                <dt>MFA method</dt>
                <dd>{data.user.mfa_method === "passkey" ? "Passkey · phishing-resistant" : data.user.mfa_method === "totp" ? "Authenticator code" : "Federated MFA"}</dd>
              </div>
              <div>
                <dt>Provisioning</dt>
                <dd>{data.scimConnector?.status ?? "not configured"}</dd>
              </div>
            </dl>
            {data.user.authentication_method === "password" && !data.demo && <section className="account-passkeys">
              <header>
                <div><strong>Passkeys</strong><span>Phishing-resistant factors for this account</span></div>
                <button className="button button--quiet" disabled={passkeyBusy || accountPasskeys.length >= 10 || !passkeysSupported()} onClick={() => void addAccountPasskey()}><Fingerprint size={14} /> Add passkey</button>
              </header>
              {accountPasskeys.length === 0 && !passkeyBusy && <p>No passkeys enrolled. Authenticator-code MFA remains active.</p>}
              {accountPasskeys.map((passkey) => <article key={passkey.credential_id}>
                <Fingerprint size={17} />
                <div><strong>Passkey …{passkey.credential_id_hint}</strong><span>Added {new Date(passkey.created_at).toLocaleDateString()} · {passkey.last_used_at ? `last used ${timeAgo(passkey.last_used_at)}` : "not used yet"}{passkey.backup_eligible ? " · sync-capable" : ""}</span></div>
                <button className="text-button" disabled={passkeyBusy} onClick={() => void removeAccountPasskey(passkey)}>Revoke</button>
              </article>)}
              {passkeyBusy && <p>Updating account security…</p>}
              {passkeyMessage && <p className="form-message">{passkeyMessage}</p>}
              <small>Revoking a passkey invalidates every active account session. When phishing-resistant MFA is mandatory, the final passkey cannot be removed.</small>
            </section>}
            <footer>
              <button
                className="button button--primary"
                onClick={() => {
                  setProfileModal(false);
                  setView("access");
                }}
              >
                Manage access
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {caManageTarget && data && (
        <Modal
          title={caManageTarget.name}
          onClose={() => setCaManageTarget(undefined)}
        >
          <div className="modal-body">
            <dl className="detail-list">
              <div>
                <dt>Provider</dt>
                <dd>{caManageTarget.provider.replaceAll("_", " ")}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{caManageTarget.status}</dd>
              </div>
              <div>
                <dt>Key reference</dt>
                <dd className="mono">
                  {caManageTarget.signer_key_reference ?? "pending"}
                </dd>
              </div>
              {caManageTarget.provider === "sectigo_scm" && (
                <div>
                  <dt>SCM enrollment</dt>
                  <dd>{scmIdsLabel(caManageTarget)?.label}</dd>
                </div>
              )}
            </dl>
            {caManageTarget.provider === "sectigo_scm" && (
              <>
                {scmIdsLabel(caManageTarget)?.complete === false && (
                  <div className="warning-callout">
                    <ShieldCheck size={19} />
                    <p>
                      This connector is incomplete. Its signing profiles are not
                      published to the protected signer until both SCM IDs are set.
                    </p>
                  </div>
                )}
                <ScmIdFields
                  draft={scmManageDraft}
                  errors={scmManageErrors}
                  onChange={(draft) => {
                    setScmManageDraft(draft);
                    setScmManageErrors({});
                    setScmManageMessage("");
                  }}
                />
                {scmManageMessage && <p className="form-message">{scmManageMessage}</p>}
              </>
            )}
            <div className="secure-note">
              <LockKeyhole size={17} />
              <span>
                Sectigo Edge stores only the public chain and protected signer
                reference. Rotation starts a new one-time connection ceremony.
              </span>
            </div>
            <footer>
              <button
                className="button button--quiet"
                onClick={() => {
                  setCaManageTarget(undefined);
                  setCaModal(true);
                }}
              >
                Start replacement
              </button>
              {caManageTarget.provider === "sectigo_scm" && (
                <button className="button button--quiet" onClick={() => void saveScmIds()}>
                  Save SCM IDs
                </button>
              )}
              {caManageTarget.provider === "sectigo_scm" && (
                <button
                  className="button button--primary"
                  onClick={() => {
                    const integration = data.integrations.find(
                      (item) => item.kind === "sectigo_scm",
                    );
                    setCaManageTarget(undefined);
                    setView("integrations");
                    if (integration) configureIntegration(integration);
                  }}
                >
                  Configure SCM integration
                </button>
              )}
            </footer>
          </div>
        </Modal>
      )}
      {crlRefreshTarget && data && (
        <Modal title="Publish a new revocation list" onClose={() => setCrlRefreshTarget(undefined)}>
          <div className="modal-body">
            <div className="warning-callout">
              <ShieldCheck size={19} />
              <p>
                The protected signer must return a fresh CA-signed CRL containing every known revocation.
                Sectigo Edge verifies the issuer, signature, entries, reasons, freshness, and monotonic generation before publication.
              </p>
            </div>
            <dl className="detail-list crl-details">
              <div><dt>Signing authority</dt><dd>{crlRefreshTarget.name}</dd></div>
              <div><dt>Current generation</dt><dd>{data.crlPublications.find((item) => item.connector_id === crlRefreshTarget.id)?.generation ?? 0}</dd></div>
              <div><dt>Required revocations</dt><dd>{data.crlPublications.find((item) => item.connector_id === crlRefreshTarget.id)?.required_revocations ?? 0}</dd></div>
              <div>
                <dt>Public endpoint</dt>
                <dd className="mono">/pki/{crlRefreshTarget.tenant_id}/crl/{crlRefreshTarget.id}.crl</dd>
              </div>
            </dl>
            <label className="modal-field">
              <span>Type REFRESH CRL to confirm</span>
              <input value={crlConfirmation} onChange={(event) => setCrlConfirmation(event.target.value)} autoFocus />
              <small>Fresh MFA and revocation permission are enforced again by the control plane.</small>
            </label>
            <footer>
              <button className="button button--quiet" onClick={() => setCrlRefreshTarget(undefined)}>Cancel</button>
              <button className="button button--primary" disabled={crlConfirmation !== "REFRESH CRL"} onClick={() => void submitCrlRefresh()}>
                <RefreshCw size={15} /> Verify and publish
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {ocspRefreshTarget && data && (
        <Modal title="Refresh certificate status responses" onClose={() => setOcspRefreshTarget(undefined)}>
          <div className="modal-body">
            <div className="warning-callout warning-callout--neutral">
              <ShieldCheck size={19} />
              <p>
                {ocspRefreshTarget.health === "external"
                  ? "Sectigo Edge queues SHA-1 and SHA-256 CertID requests to the certificate's exact Windows signing node. The node calls its pinned Microsoft Online Responder; the node and edge independently verify status, signature, responder authority, and freshness before publication."
                  : "Sectigo Edge requests both SHA-1 and SHA-256 CertID responses from the configured responder, verifies their signature and status independently, then atomically advances the published generation."}
              </p>
            </div>
            <dl className="detail-list crl-details">
              <div><dt>Signing authority</dt><dd>{data.cas.find((ca) => ca.id === ocspRefreshTarget.connector_id)?.name ?? ocspRefreshTarget.connector_id}</dd></div>
              <div><dt>Certificate serial</dt><dd className="mono">{ocspRefreshTarget.serial_number}</dd></div>
              <div><dt>Current status</dt><dd>{ocspRefreshTarget.status}</dd></div>
              <div><dt>Public endpoint</dt><dd className="mono">/pki/{data.user.tenant_id}/ocsp/{ocspRefreshTarget.connector_id}</dd></div>
            </dl>
            <label className="modal-field">
              <span>Type REFRESH OCSP to confirm</span>
              <input value={ocspConfirmation} onChange={(event) => setOcspConfirmation(event.target.value)} autoFocus />
              <small>Fresh MFA and revocation permission are enforced again by the control plane.</small>
            </label>
            <footer>
              <button className="button button--quiet" onClick={() => setOcspRefreshTarget(undefined)}>Cancel</button>
              <button className="button button--primary" disabled={ocspConfirmation !== "REFRESH OCSP"} onClick={() => void submitOcspRefresh()}>
                <RefreshCw size={15} /> {ocspRefreshTarget.health === "external" ? "Verify and queue" : "Verify and publish both"}
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {caModal && (
        <Modal
          title="Connect a signing authority"
          onClose={() => setCaModal(false)}
        >
          <div className="modal-body">
            <label className="modal-field">
              <span>Connector name</span>
              <input
                value={caName}
                onChange={(event) => setCaName(event.target.value)}
                placeholder="Production issuing CA"
                autoFocus
              />
            </label>
            <ScmIdFields
              draft={scmIdDraft}
              errors={scmIdErrors}
              onChange={(draft) => {
                setScmIdDraft(draft);
                setScmIdErrors({});
              }}
            />
            <div className="provider-choices">
              <button onClick={() => void connectCa("sectigo_scm")}>
                <span className="provider-logo provider-logo--sectigo_scm">
                  <ShieldCheck size={21} />
                </span>
                <div>
                  <strong>Sectigo SCM</strong>
                  <p>Connect an SCM account and select a managed private CA.</p>
                </div>
                <ArrowRight size={17} />
              </button>
              <button onClick={() => void connectCa("byo_ca")}>
                <span className="provider-logo provider-logo--byo_ca">
                  <KeyRound size={21} />
                </span>
                <div>
                  <strong>Bring your own CA</strong>
                  <p>Open a one-time secure signer import ceremony.</p>
                </div>
                <ArrowRight size={17} />
              </button>
            </div>
            {caError && <p className="form-message">{caError}</p>}
            <div className="secure-note">
              <LockKeyhole size={17} />
              <span>
                CA private keys are never stored in the Sectigo Edge application
                plane.
              </span>
            </div>
          </div>
        </Modal>
      )}
      {integrationTarget && data && (
        <Modal
          title={`Configure ${integrationTarget.display_name}`}
          onClose={() => setIntegrationTarget(undefined)}
          size="wide"
        >
          <div className="modal-body integration-modal">
            <section className="integration-modal-summary">
              <div>
                <span>INTEGRATION DESIRED STATE</span>
                <p>
                  Configure the cloud-side intent here. An authenticated local
                  node validates and applies it, then returns health evidence.
                </p>
              </div>
              <Status
                value={integrationTarget.enabled ? "healthy" : "offline"}
                label={integrationTarget.enabled ? "Configured" : "Not configured"}
              />
            </section>
            <section className="modal-section">
              <header><strong>Activation and targets</strong><span>Choose where this integration is allowed to run.</span></header>
            <label className="safety-option">
              <input
                type="checkbox"
                checked={integrationDraft.enabled}
                onChange={(event) =>
                  setIntegrationDraft({
                    ...integrationDraft,
                    enabled: event.target.checked,
                  })
                }
              />
              <span>
                <strong>Enable this integration</strong>
                <small>
                  Desired state is reconciled by an authenticated Mesh Node and
                  fails closed until acknowledged.
                </small>
              </span>
            </label>
            {integrationTarget.kind !== "sectigo_scm" && (
              <div className="modal-field">
                <span>Target Mesh Nodes</span>
                <div className="node-choices">
                  {data.nodes.map((node) => {
                    const compatible =
                      !["nginx", "apache"].includes(integrationTarget.kind) ||
                      node.platform === "linux" ||
                      node.platform === "container";
                    return (
                    <label key={node.id} className={compatible ? undefined : "node-choice--disabled"}>
                      <input
                        type="checkbox"
                        disabled={!compatible}
                        checked={integrationDraft.node_ids.includes(node.id)}
                        onChange={(event) =>
                          setIntegrationDraft({
                            ...integrationDraft,
                            node_ids: event.target.checked
                              ? [...integrationDraft.node_ids, node.id]
                              : integrationDraft.node_ids.filter(
                                  (id) => id !== node.id,
                                ),
                          })
                        }
                      />
                      <span>
                        <strong>{node.name}</strong>
                        <small>
                          {node.platform} · {compatible ? node.status : `${integrationTarget.display_name} requires Linux`}
                        </small>
                      </span>
                    </label>
                    );
                  })}
                </div>
              </div>
            )}
            </section>
            <section className="modal-section">
              <header><strong>Profile and adapter</strong><span>Bind an exact identity profile and a non-secret local adapter reference.</span></header>
            <label className="modal-field">
              <span>Certificate / identity profile</span>
              <input
                value={integrationDraft.profile_id}
                onChange={(event) =>
                  setIntegrationDraft({
                    ...integrationDraft,
                    profile_id: event.target.value,
                  })
                }
                placeholder="prod-service"
              />
            </label>
            {integrationTarget.kind === "sectigo_scm" && (
              <label className="modal-field">
                <span>Active SCM connector</span>
                <select
                  value={integrationDraft.ca_connector_id ?? ""}
                  onChange={(event) =>
                    setIntegrationDraft({
                      ...integrationDraft,
                      ca_connector_id: event.target.value,
                    })
                  }
                >
                  <option value="">Select connector…</option>
                  {data.cas
                    .filter(
                      (ca) =>
                        ca.provider === "sectigo_scm" && ca.status === "active",
                    )
                    .map((ca) => (
                      <option key={ca.id} value={ca.id}>
                        {ca.name}
                      </option>
                    ))}
                </select>
              </label>
            )}
            {integrationTarget.kind !== "sectigo_scm" && (
              <label className="modal-field">
                <span>Local adapter reference</span>
                <input
                  value={integrationDraft.adapter_reference ?? ""}
                  readOnly={
                    integrationTarget.kind === "acme" ||
                    integrationTarget.kind === "spiffe"
                  }
                  onChange={(event) =>
                    setIntegrationDraft({
                      ...integrationDraft,
                      adapter_reference: event.target.value,
                    })
                  }
                  placeholder={
                    integrationTarget.kind === "acme"
                      ? "builtin/acme"
                      : integrationTarget.kind === "spiffe"
                        ? "builtin/spire"
                        : "local/adapter-profile"
                  }
                />
              </label>
            )}
            {integrationTarget.kind === "microsoft_adcs" && (
              <label className="modal-field">
                <span>ADCS public CA chain</span>
                <select
                  value={integrationDraft.ca_connector_id ?? ""}
                  onChange={(event) =>
                    setIntegrationDraft({
                      ...integrationDraft,
                      ca_connector_id: event.target.value,
                    })
                  }
                >
                  <option value="">Select uploaded BYO CA chain…</option>
                  {data.cas
                    .filter(
                      (ca) => ca.provider === "byo_ca" && ca.status === "active",
                    )
                    .map((ca) => (
                      <option key={ca.id} value={ca.id}>
                        {ca.name}
                      </option>
                    ))}
                </select>
                <small>
                  The cloud uses this public chain to verify every certificate
                  returned by the Windows node. ADCS credentials remain local.
                </small>
              </label>
            )}
            </section>
            <section className="modal-section modal-section--security">
            <div className="secure-note">
              <LockKeyhole size={17} />
              <span>
                {integrationTarget.kind === "java_keystore"
                  ? "The node builds and validates modern PKCS#12 generations, pins JDK keytool and the application reload executable, keeps the password in a private local file, verifies the exact served certificate, and restores the previous keystore automatically on failure."
                  : integrationTarget.kind === "hashicorp_vault"
                  ? "The node reads the Vault token from a private local file that is never sent to Sectigo Edge, writes each certificate as an immutable KV v2 generation, moves the active pointer with check-and-set, verifies the published digest, and restores the previous pointer automatically on failure."
                  : integrationTarget.kind === "nginx" || integrationTarget.kind === "apache"
                  ? `The node pins the exact ${integrationTarget.display_name} binary, stages immutable certificate generations, validates the managed include, reloads without a shell, verifies the exact served certificate, and restores the previous generation automatically on failure.`
                  : "Only this non-secret reference is stored in the cloud. Passwords, tokens, and private keys stay in the node's encrypted local credential store."}
              </span>
            </div>
            {integrationTarget.kind === "nginx" && (
              <div className="integration-guide-link">
                <Server size={18} />
                <div>
                  <strong>Configure the local server first</strong>
                  <span>
                    Add the managed include, pin the NGINX executable digest,
                    and import the currently serving key pair before enabling
                    desired state.
                  </span>
                </div>
                <a href="/docs#nginx" target="_blank" rel="noreferrer">
                  Open guide <ArrowRight size={14} />
                </a>
              </div>
            )}
            {integrationTarget.kind === "apache" && (
              <div className="integration-guide-link">
                <Server size={18} />
                <div>
                  <strong>Configure the local server first</strong>
                  <span>
                    Include the managed TLS snippet inside the target virtual
                    host, pin the httpd executable digest, and import the
                    currently serving key pair.
                  </span>
                </div>
                <a href="/docs#apache" target="_blank" rel="noreferrer">
                  Open guide <ArrowRight size={14} />
                </a>
              </div>
            )}
            {integrationTarget.kind === "java_keystore" && (
              <div className="integration-guide-link">
                <Coffee size={18} />
                <div>
                  <strong>Prepare the local Java runtime</strong>
                  <span>
                    Pin JDK keytool and the reload executable, create a restricted
                    high-entropy password file, import the serving key pair, and
                    configure the application to read the managed PKCS#12 path.
                  </span>
                </div>
                <a href="/docs#java-keystore" target="_blank" rel="noreferrer">
                  Open guide <ArrowRight size={14} />
                </a>
              </div>
            )}
            {integrationTarget.kind === "hashicorp_vault" && (
              <div className="integration-guide-link">
                <Vault size={18} />
                <div>
                  <strong>Prepare the local Vault access</strong>
                  <span>
                    Store a narrowly scoped Vault token in a restricted file on
                    the Mesh Node, enable a KV v2 mount, and grant the token
                    write access only to the configured base path, where the
                    node keeps certificate generations and a CAS pointer.
                  </span>
                </div>
                <a href="/docs#hashicorp-vault" target="_blank" rel="noreferrer">
                  Open guide <ArrowRight size={14} />
                </a>
              </div>
            )}
            {integrationTarget.kind === "acme" && (
              <div className="integration-guide-link">
                <Fingerprint size={18} />
                <div>
                  <strong>Create a node-bound external account</strong>
                  <span>
                    Generate a distinct short-lived EAB credential and ACME
                    account for every named primary or failover node. Never
                    send account traffic through random load balancing.
                  </span>
                </div>
                <a href="/docs#acme" target="_blank" rel="noreferrer">
                  Open ACME guide <ArrowRight size={14} />
                </a>
              </div>
            )}
            {integrationTarget.kind === "microsoft_adcs" && (
              <div className="integration-guide-link">
                <Laptop size={18} />
                <div>
                  <strong>CA-signed revocation distribution</strong>
                  <span>
                    The assigned Windows node publishes and retrieves each CRL
                    through native ADCS administration APIs. Sectigo Edge
                    rejects wrong-issuer, stale, incomplete, or non-monotonic
                    artifacts before immutable distribution.
                  </span>
                </div>
                <a href="/docs#integrations" target="_blank" rel="noreferrer">
                  Open ADCS guide <ArrowRight size={14} />
                </a>
              </div>
            )}
            </section>
            {integrationError && (
              <p className="form-message form-message--error">
                {integrationError}
              </p>
            )}
            <footer>
              <button
                className="button button--quiet"
                onClick={() => setIntegrationTarget(undefined)}
              >
                Cancel
              </button>
              <button
                disabled={
                  integrationDraft.profile_id.trim().length < 2 ||
                  (integrationDraft.enabled &&
                    integrationTarget.kind !== "sectigo_scm" &&
                    integrationDraft.node_ids.length === 0) ||
                  (integrationDraft.enabled &&
                    integrationTarget.kind === "sectigo_scm" &&
                    !integrationDraft.ca_connector_id) ||
                  (integrationDraft.enabled &&
                    integrationTarget.kind === "microsoft_adcs" &&
                    !integrationDraft.ca_connector_id) ||
                  (integrationDraft.enabled &&
                    ["kubernetes_csr", "microsoft_adcs", "est", "nginx", "apache", "java_keystore", "hashicorp_vault"].includes(
                      integrationTarget.kind,
                    ) &&
                    !integrationDraft.adapter_reference?.trim())
                }
                className="button button--primary"
                onClick={() => void saveIntegrationConfiguration()}
              >
                <ShieldCheck size={15} /> Save desired state
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {revokeTarget?.active && (
        <Modal
          title="Revoke certificate immediately"
          onClose={() => setRevokeTarget(undefined)}
        >
          <div className="modal-body">
            <div className="warning-callout">
              <ShieldX size={21} />
              <p>
                Revocation is a one-party safety action and cannot be undone.
                New issuance still requires both trust domains.
              </p>
            </div>
            <label className="modal-field">
              <span>RFC 5280 reason</span>
              <select
                value={revocationReason}
                onChange={(event) => setRevocationReason(event.target.value)}
              >
                <option value="key_compromise">Key compromise</option>
                <option value="superseded">Superseded</option>
                <option value="cessation_of_operation">
                  Cessation of operation
                </option>
                <option value="affiliation_changed">Affiliation changed</option>
                <option value="privilege_withdrawn">Privilege withdrawn</option>
                <option value="certificate_hold">Certificate hold</option>
                <option value="unspecified">Unspecified</option>
              </select>
            </label>
            {revokeTarget.standby?.health === "healthy" && (
              <label className="safety-option">
                <input
                  type="checkbox"
                  checked={safeFailover}
                  onChange={(event) => setSafeFailover(event.target.checked)}
                />
                <span>
                  <strong>Promote healthy standby first</strong>
                  <small>
                    Preserves service while immediately removing the compromised
                    certificate.
                  </small>
                </span>
              </label>
            )}
            <label className="field">
              <span>Audited incident context</span>
              <textarea
                value={revocationComment}
                onChange={(event) => setRevocationComment(event.target.value)}
                placeholder="Ticket, evidence, or incident context…"
                autoFocus
              />
            </label>
            <footer>
              <button
                className="button button--quiet"
                onClick={() => setRevokeTarget(undefined)}
              >
                Cancel
              </button>
              <button
                className="button button--danger"
                onClick={() => void revokeActive()}
              >
                <ShieldX size={15} /> Revoke now
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {registrationTarget && (
        <Modal
          title="Verify and claim service"
          onClose={() => setRegistrationTarget(undefined)}
        >
          <div className="modal-body">
            <div className="secure-note">
              <LockKeyhole size={17} />
              <span>
                Enter the eight-character code shown by the local service.
                Sectigo Edge stores only its digest and cannot reveal it.
              </span>
            </div>
            <label className="modal-field">
              <span>Human verification code</span>
              <input
                className="mono"
                value={registrationApproval.human_code}
                maxLength={8}
                onChange={(event) =>
                  setRegistrationApproval({
                    ...registrationApproval,
                    human_code: event.target.value.toUpperCase(),
                  })
                }
                placeholder="ABCD2345"
                autoFocus
              />
            </label>
            <label className="modal-field">
              <span>Owner</span>
              <input
                value={registrationApproval.owner}
                onChange={(event) =>
                  setRegistrationApproval({
                    ...registrationApproval,
                    owner: event.target.value,
                  })
                }
                placeholder="team@company.com"
              />
            </label>
            <label className="modal-field">
              <span>Identity profile</span>
              <input
                value={registrationApproval.profile_id}
                onChange={(event) =>
                  setRegistrationApproval({
                    ...registrationApproval,
                    profile_id: event.target.value,
                  })
                }
              />
            </label>
            <div className="channel-choices">
              <span>Permitted channels</span>
              {registrationTarget.announcement.protocols.map((channel) => (
                <label key={channel}>
                  <input
                    type="checkbox"
                    checked={registrationApproval.channels.includes(channel)}
                    onChange={(event) =>
                      setRegistrationApproval({
                        ...registrationApproval,
                        channels: event.target.checked
                          ? [...registrationApproval.channels, channel]
                          : registrationApproval.channels.filter(
                              (value) => value !== channel,
                            ),
                      })
                    }
                  />
                  {channel}
                </label>
              ))}
            </div>
            <div className="registration-warning">
              <AlertTriangle size={17} />
              <span>
                SPIRE selectors are derived from node-observed measurements.
                They cannot be edited here or through MCP.
              </span>
            </div>
            <footer>
              <button
                className="button button--quiet"
                onClick={() => setRegistrationTarget(undefined)}
              >
                Cancel
              </button>
              <button
                disabled={
                  !/^[A-HJ-NP-Z2-9]{8}$/.test(
                    registrationApproval.human_code,
                  ) ||
                  registrationApproval.owner.trim().length < 3 ||
                  registrationApproval.channels.length === 0
                }
                className="button button--primary"
                onClick={() => void approveRegistration()}
              >
                <BadgeCheck size={15} /> Claim service
              </button>
            </footer>
          </div>
        </Modal>
      )}
      {toast && (
        <div className="toast">
          <Check size={16} />
          {toast}
        </div>
      )}
    </div>
  );
}
